// Path: assets/ps-vehicle-color-selector.js
//
// Unified Vehicle + Color Search module.
// Single source for all vehicle/color selection flows:
//   - Unknown-color product pages (PDP mode: navigate on color click)
//   - Homepage color search (Search mode: picked summary, product rows, text replace)
//
// API endpoints (shared with sections/color-search.liquid):
//   GET /api/shopify/makes
//   GET /api/shopify/years/{make}
//   GET /api/shopify/models/{make}/{year}
//   GET /api/shopify/colors/{make}?year=Y&model=M&q=Q&page=P&per_page=N
//   GET /api/shopify/colors/{make}?year=Y&model=M&q=Q&page=1&per_page=N&include_two_tone_groups=1
//   GET /api/shopify/cars/{makeSlug}/{colorSlug}
//
// Strict ES5 only. Dependencies: ps-core.js (PS.u.apiBase, PS.cc).

(function(window, document) {
  'use strict';

  // ==========================================================
  // 1. SHARED UTILITIES
  // ==========================================================

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function esc(s) {
    return String(s || '').replace(/[&<>"']/g, function(c) {
      return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c];
    });
  }

  function escRegExp(s) {
    return String(s || '').replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  function qs(obj) {
    var a = [], k, v;
    for (k in obj) {
      if (!obj.hasOwnProperty(k)) continue;
      v = obj[k] == null ? '' : String(obj[k]).replace(/\s+/g, '+');
      a.push(encodeURIComponent(k) + '=' + v);
    }
    return a.join('&');
  }

  function getSensitiveSearchState(value) {
    var searchApi = window.PS && window.PS.Search;
    var reason = '';
    var data = null;
    if (!searchApi || typeof searchApi.classifySensitive !== 'function' ||
        typeof searchApi.sensitiveData !== 'function' || typeof searchApi.helpUrl !== 'function') {
      if (!String(value || '').replace(/^\s+|\s+$/g, '')) return null;
      return {
        reason: 'search_safety_unavailable',
        text: 'Search is unavailable right now. Start with your vehicle paint code.',
        url: '/touch-up-paint-codes/paint-code.html'
      };
    }
    reason = searchApi.classifySensitive(value);
    if (!reason) return null;
    data = searchApi.sensitiveData(value, reason);
    return {
      reason: reason,
      text: data && typeof searchApi.guidanceText === 'function' ? searchApi.guidanceText(data) : '',
      url: searchApi.helpUrl(reason)
    };
  }

  function isSensitiveSearchValue(value) {
    return !!getSensitiveSearchState(value);
  }

  function navigateToSensitiveSearchHelp(value) {
    var sensitiveState = getSensitiveSearchState(value);
    if (!sensitiveState) return false;
    window.location.href = sensitiveState.url;
    return true;
  }

  function debounce(fn, wait) {
    var timeout = null;
    return function() {
      var args = arguments;
      var ctx = this;
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(function() {
        timeout = null;
        fn.apply(ctx, args);
      }, wait || 0);
    };
  }

  function apiBase() {
    try {
      if (window.PS && PS.u && typeof PS.u.apiBase === 'function') return PS.u.apiBase();
      if (window.paintscratchTheme && window.paintscratchTheme.psUrl)
        return String(window.paintscratchTheme.psUrl).replace(/\/+$/, '') + '/api/';
    } catch(e) {}
    return '';
  }

  function toSlug(s) {
    s = String(s || '').toLowerCase();
    s = s.replace(/&/g, ' and ');
    s = s.replace(/[^a-z0-9]+/g, '-');
    s = s.replace(/-+/g, '-');
    return s.replace(/^-|-$/g, '');
  }

  function buildColorSlug(colorName, colorCode) {
    var name = toSlug(colorName);
    var code = String(colorCode || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return code ? (name ? name + '-' + code : code) : name;
  }

  function normHex(h) {
    return String(h || '').replace(/^#?/, '').toLowerCase();
  }

  function normalizeColorRgb(h) {
    var normalized = normHex(h);
    return /^[0-9a-f]{6}$/.test(normalized) ? ('#' + normalized.toUpperCase()) : '';
  }

  function resolveElement(ref, ctx) {
    if (!ref) return null;
    if (typeof ref === 'string') return $(ref, ctx);
    return ref;
  }

  function trimValue(value) {
    return String(value || '').replace(/^\s+|\s+$/g, '');
  }

  function normalizeColorCodeDisplay(value) {
    var code = trimValue(value);
    return code ? code.toUpperCase() : 'Code unavailable';
  }

  function normalizeColorNameDisplay(value) {
    var name = trimValue(value);
    return name ? name : 'Color name unavailable';
  }

  function colorCodeHtml(value) {
    var html = esc(normalizeColorCodeDisplay(value));
    html = html.replace(/([\/.,-])/g, '$1<wbr>');
    html = html.replace(/(\))/g, '$1<wbr>');
    return html;
  }

  function colorIdentityHtml(code, name) {
    var codeText = normalizeColorCodeDisplay(code);
    var nameText = normalizeColorNameDisplay(name);
    if (codeText === 'Code unavailable') {
      return esc(nameText);
    }
    if (nameText === 'Color name unavailable') {
      return colorCodeHtml(codeText);
    }
    return colorCodeHtml(codeText) + ' - ' + esc(nameText);
  }

  function colorCardAccessibleLabel(code, name) {
    var codeText = normalizeColorCodeDisplay(code);
    var nameText = normalizeColorNameDisplay(name);
    if (codeText === 'Code unavailable') {
      return 'Select color code unavailable, ' + nameText + '.';
    }
    return 'Select color code ' + codeText + ', ' + nameText + '.';
  }

  function normalizeScopedSearchScope(scope) {
    scope = scope || {};
    return {
      make: trimValue(scope.make),
      year: trimValue(scope.year),
      model: trimValue(scope.model)
    };
  }

  function readStoredVehicleContextScope(keyPrefix) {
    var storage = null;
    var key = '';
    var raw = '';
    var parsed = null;
    var normalized = null;
    var i = 0;

    try {
      storage = window.sessionStorage;
    } catch(_e) {
      storage = null;
    }
    if (!storage || !storage.length) return null;

    for (i = 0; i < storage.length; i++) {
      key = '';
      raw = '';
      parsed = null;
      try {
        key = storage.key(i);
      } catch(_e2) {
        key = '';
      }
      if (!key || key.indexOf(keyPrefix) !== 0) continue;
      try {
        raw = storage.getItem(key);
      } catch(_e3) {
        raw = '';
      }
      if (!raw) continue;
      try {
        parsed = JSON.parse(raw);
      } catch(_e4) {
        parsed = null;
      }
      normalized = normalizeScopedSearchScope(parsed || {});
      if (normalized && normalized.make) return normalized;
    }
    return null;
  }

  function buildVehicleContextLabel(scope, fallbackMake) {
    var cc = readCookie() || {};
    scope = normalizeScopedSearchScope(scope);
    var make = trimValue(cc.make || scope.make || fallbackMake || '');
    var year = trimValue(cc.year || scope.year || '');
    var model = trimValue(cc.model || scope.model || '');
    var parts = [];

    function mergeVehicleContextScope(source) {
      var sourceMake = '';
      source = normalizeScopedSearchScope(source);
      if (!source || !source.make) return;
      sourceMake = trimValue(source.make);
      if (make && sourceMake && toSlug(make) !== toSlug(sourceMake)) return;
      if (!make) make = sourceMake;
      if (!year && source.year) year = source.year;
      if (!model && source.model) model = source.model;
    }

    mergeVehicleContextScope(readStoredVehicleContextScope('ps-submitted-search:'));
    mergeVehicleContextScope(readStoredVehicleContextScope('ps-edit-restore:'));

    if (make && year) parts.push(year);
    if (make) parts.push(make);
    if (make && model) parts.push(model);
    return parts.length ? parts.join(' ') : 'COLOR';
  }

  function buildScopedTypeaheadQueryValue(term, scope) {
    var parts = [];
    scope = normalizeScopedSearchScope(scope);
    term = trimValue(term);
    if (scope.make) parts.push(scope.make);
    if (scope.year) parts.push(scope.year);
    if (scope.model) parts.push(scope.model);
    if (term) parts.push(term);
    return parts.join(' ').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
  }

  function buildScopedWorkerResultsUrl(term, scope, page, perPageValue) {
    var params = [];
    scope = normalizeScopedSearchScope(scope);
    var sensitiveState = getSensitiveSearchState(term);
    if (sensitiveState) return sensitiveState.url;
    function pushParam(key, value) {
      if (!value && value !== 0) return;
      value = trimValue(value);
      if (!value) return;
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }
    pushParam('q', trimValue(term));
    pushParam('page', page || 1);
    if (perPageValue) pushParam('per_page', perPageValue);
    pushParam('predictive', 'color_scoped');
    pushParam('make', scope.make);
    pushParam('year', scope.year);
    pushParam('model', scope.model);
    return '/search/results?' + params.join('&');
  }

  function buildScopedCommittedResultsUrl(term, scope, page, perPageValue) {
    var params = [];
    scope = normalizeScopedSearchScope(scope);
    var sensitiveState = getSensitiveSearchState(term);
    if (sensitiveState) return sensitiveState.url;
    function pushParam(key, value) {
      if (!value && value !== 0) return;
      value = trimValue(value);
      if (!value) return;
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }
    pushParam('q', buildScopedTypeaheadQueryValue(term, scope));
    pushParam('page', page || 1);
    if (perPageValue) pushParam('per_page', perPageValue);
    pushParam('filter_by', 'type:=color_hub');
    pushParam('sort_by', 'recent_order_count:desc');
    pushParam('make', scope.make);
    pushParam('year', scope.year);
    pushParam('model', scope.model);
    return '/search/results?' + params.join('&');
  }

  function buildScopedResultsPageUrl(term, scope) {
    var params = [];
    scope = normalizeScopedSearchScope(scope);
    var sensitiveState = getSensitiveSearchState(term);
    if (sensitiveState) return sensitiveState.url;
    function pushParam(key, value) {
      if (!value && value !== 0) return;
      value = trimValue(value);
      if (!value) return;
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }
    pushParam('q', term);
    pushParam('make', scope.make);
    pushParam('year', scope.year);
    pushParam('model', scope.model);
    return '/search' + (params.length ? '?' + params.join('&') : '');
  }

  function normalizeScopedColorHubUrl(url, sameHost) {
    var str = trimValue(url);
    var abs = null;
    var match = null;
    if (!str) return '';
    if (str.indexOf('/colors/') === 0) {
      return /\/$/.test(str) ? str : str + '/';
    }
    if (/^https?:\/\//i.test(str)) {
      abs = document.createElement('a');
      abs.href = str;
      if (String(abs.pathname || '').indexOf('/colors/') === 0) {
        if (sameHost !== false) return /\/$/.test(abs.pathname) ? abs.pathname : abs.pathname + '/';
        return abs.href;
      }
    }
    match = str.match(/(\/colors\/[^\/]+\/[^\/]+)\/?/i);
    if (!match || !match[1]) return '';
    return match[1] + '/';
  }

  function buildScopedTypeaheadSuggestionData(result, scope, rank, sameHost) {
    var title = result && result.title ? String(result.title) : '';
    var label = title.replace(/\s+Touch Up Paint\s*$/i, '');
    var metaParts = [];
    var href = normalizeScopedColorHubUrl(result && result.url ? result.url : '', sameHost);
    scope = normalizeScopedSearchScope(scope);
    if (!href) return null;
    if (!label) label = title || href;
    if (scope.year) metaParts.push(scope.year);
    if (scope.model) metaParts.push(scope.model);
    if (scope.make) metaParts.unshift(scope.make);
    return {
      key: String((result && result.id) || href || rank),
      label: label,
      url: href,
      meta: metaParts.join(' ' + String.fromCharCode(8226) + ' '),
      hex: normalizeColorRgb(result && (result.color_rgb || result.hex) ? (result.color_rgb || result.hex) : ''),
      rank: rank
    };
  }

  var _scopedTypeaheadCounter = 0;

  function attachScopedTypeahead(cfg) {
    var input = null;
    var form = null;
    var host = null;
    var classPrefix = '';
    var minChars = 2;
    var requestPerPage = 10;
    var suggestionLimit = 10;
    var debounceMs = 250;
    var panelId = '';
    var shell = null;
    var list = null;
    var message = null;
    var footer = null;
    var docClickHandler = null;
    var pendingXhr = null;
    var requestId = 0;
    var activeIndex = -1;
    var suggestions = [];
    var cache = {};

    function getScope() {
      if (typeof cfg.getScope === 'function') return normalizeScopedSearchScope(cfg.getScope() || {});
      return normalizeScopedSearchScope(cfg.scope || {});
    }

    function abortPendingRequest() {
      if (pendingXhr && pendingXhr.readyState !== 4) {
        try { pendingXhr.abort(); } catch(_e) {}
      }
      pendingXhr = null;
    }

    function closePanel() {
      var items = null;
      var i = 0;
      if (!shell) return;
      shell.removeAttribute('data-open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      items = list ? list.querySelectorAll('[data-ps-scoped-typeahead-index]') : [];
      for (i = 0; i < items.length; i++) {
        items[i].setAttribute('aria-selected', 'false');
        items[i].removeAttribute('data-active');
      }
    }

    function clearSuggestions() {
      abortPendingRequest();
      requestId++;
      suggestions = [];
      activeIndex = -1;
      if (list) list.innerHTML = '';
      if (message) message.innerHTML = '';
      if (footer) footer.innerHTML = '';
      closePanel();
    }

    function openPanel() {
      if (!shell) return;
      shell.setAttribute('data-open', 'true');
      input.setAttribute('aria-expanded', 'true');
    }

    function ensureUi() {
      if (shell) return;
      shell = document.createElement('div');
      shell.id = panelId;
      shell.className = classPrefix + '-panel';
      shell.setAttribute('role', 'listbox');
      shell.setAttribute('aria-label', cfg.listboxLabel || 'Suggested colors');
      list = document.createElement('div');
      list.className = classPrefix + '-list';
      message = document.createElement('div');
      message.className = classPrefix + '-message';
      footer = document.createElement('div');
      footer.className = classPrefix + '-footer';
      shell.appendChild(list);
      shell.appendChild(message);
      shell.appendChild(footer);
      shell.addEventListener('mousedown', function(evt) {
        evt.preventDefault();
      });
      shell.addEventListener('click', function(evt) {
        var target = evt.target || evt.srcElement;
        while (target && target !== shell && (!target.getAttribute || !target.getAttribute('data-ps-scoped-typeahead-index'))) {
          target = target.parentNode;
        }
        if (!target || target === shell || !target.getAttribute) return;
        applySuggestion(parseInt(target.getAttribute('data-ps-scoped-typeahead-index'), 10));
      });
      host.appendChild(shell);
      input.setAttribute('aria-controls', panelId);
      input.setAttribute('aria-haspopup', 'listbox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
    }

    function renderSeeAllLink(url, totalFound) {
      if (!footer) return;
      if (!url || totalFound <= suggestionLimit) {
        footer.innerHTML = '';
        return;
      }
      footer.innerHTML =
        '<a class="' + classPrefix + '-see-all" href="' + esc(url) + '">' +
        esc(cfg.seeAllLabel || 'See all colors') +
        '</a>';
    }

    function renderSuggestions(term, nextSuggestions, resultsPageUrl, totalFound) {
      var html = [];
      var i = 0;
      ensureUi();
      suggestions = nextSuggestions || [];
      activeIndex = -1;
      if (!list || !message) return;
      if (!suggestions.length) {
        list.innerHTML = '';
        renderSeeAllLink('', 0);
        message.innerHTML =
          '<div class="' + classPrefix + '-empty-row">' +
          esc(String(cfg.emptyMessage || 'No close matches found for "[term]".').replace('[term]', term)) +
          '</div>';
        openPanel();
        return;
      }
      message.innerHTML = '';
      for (i = 0; i < suggestions.length; i++) {
        html.push(
          '<button type="button" id="' + panelId + '-option-' + i + '" class="' + classPrefix + '-item" role="option" aria-selected="false" data-ps-scoped-typeahead-index="' + i + '">' +
          '<span class="' + classPrefix + '-swatch" style="background:' + esc(suggestions[i].hex || 'transparent') + '"></span>' +
          '<span class="' + classPrefix + '-copy">' +
          '<span class="' + classPrefix + '-title">' + esc(suggestions[i].label) + '</span>' +
          '<span class="' + classPrefix + '-meta">' + esc(suggestions[i].meta || 'Touch-up paint color') + '</span>' +
          '</span>' +
          '</button>'
        );
      }
      list.innerHTML = html.join('');
      renderSeeAllLink(resultsPageUrl, totalFound);
      openPanel();
    }

    function setActiveIndex(index) {
      var items = null;
      var i = 0;
      if (!list) return;
      items = list.querySelectorAll('[data-ps-scoped-typeahead-index]');
      if (!items.length) return;
      if (index < 0) index = items.length - 1;
      if (index >= items.length) index = 0;
      activeIndex = index;
      for (i = 0; i < items.length; i++) {
        if (i === index) {
          items[i].setAttribute('aria-selected', 'true');
          items[i].setAttribute('data-active', 'true');
        } else {
          items[i].setAttribute('aria-selected', 'false');
          items[i].removeAttribute('data-active');
        }
      }
      input.setAttribute('aria-activedescendant', panelId + '-option-' + index);
    }

    function applySuggestion(index) {
      var suggestion = suggestions[index];
      if (!suggestion) return;
      closePanel();
      if (typeof cfg.onSelect === 'function') {
        cfg.onSelect(suggestion, index);
        return;
      }
      if (suggestion.url) window.location.href = suggestion.url;
    }

    function requestSuggestions(term) {
      var scope = getScope();
      var cacheKey = '';
      var url = '';
      if (!scope.make || !term || term.length < minChars) {
        clearSuggestions();
        return;
      }
      ensureUi();
      cacheKey = [
        String(scope.make || '').toLowerCase(),
        String(scope.year || '').toLowerCase(),
        String(scope.model || '').toLowerCase(),
        String(term || '').toLowerCase()
      ].join('|');
      if (cache.hasOwnProperty(cacheKey)) {
        renderSuggestions(term, cache[cacheKey].suggestions, cache[cacheKey].resultsPageUrl, cache[cacheKey].totalFound);
        return;
      }
      abortPendingRequest();
      url = buildScopedWorkerResultsUrl(term, scope, 1, requestPerPage);
      requestId++;
      pendingXhr = new XMLHttpRequest();
      var xhr = pendingXhr;
      var currentRequestId = requestId;
      xhr.open('GET', url, true);
      xhr.onreadystatechange = function() {
        var data = null;
        var raw = [];
        var seen = {};
        var nextSuggestions = [];
        var totalFound = 0;
        var resultsPageUrl = '';
        var i = 0;
        if (xhr.readyState !== 4) return;
        if (currentRequestId !== requestId) return;
        if (!(xhr.status >= 200 && xhr.status < 300)) {
          if (pendingXhr === xhr) pendingXhr = null;
          clearSuggestions();
          return;
        }
        try { data = JSON.parse(xhr.responseText || '{}'); } catch(_e) { data = null; }
        if (pendingXhr === xhr) pendingXhr = null;
        raw = data && data.suggestions ? data.suggestions : (data && data.results ? data.results : []);
        totalFound = parseInt(data && data.totalFound, 10);
        if (isNaN(totalFound) || totalFound < 0) totalFound = raw.length;
        resultsPageUrl = buildScopedResultsPageUrl(term, scope);
        for (i = 0; i < raw.length; i++) {
          var suggestion = null;
          var href = normalizeScopedColorHubUrl(raw[i] && raw[i].url ? raw[i].url : '', cfg.sameHostColorHub !== false);
          if (!href) continue;
          if (seen[href]) continue;
          seen[href] = true;
          suggestion = buildScopedTypeaheadSuggestionData(raw[i], scope, i, cfg.sameHostColorHub !== false);
          if (!suggestion) continue;
          nextSuggestions.push(suggestion);
          if (nextSuggestions.length >= suggestionLimit) break;
        }
        cache[cacheKey] = {
          suggestions: nextSuggestions,
          resultsPageUrl: resultsPageUrl,
          totalFound: totalFound
        };
        renderSuggestions(term, nextSuggestions, resultsPageUrl, totalFound);
      };
      xhr.send(null);
    }

    cfg = cfg || {};
    input = resolveElement(cfg.input);
    if (!input) return null;
    form = resolveElement(cfg.form) || input.form;
    host = resolveElement(cfg.host) || input.parentNode;
    if (!host) return null;
    classPrefix = trimValue(cfg.classPrefix || 'ps-scoped-typeahead');
    if (!classPrefix) classPrefix = 'ps-scoped-typeahead';
    minChars = parseInt(cfg.minChars, 10);
    if (isNaN(minChars) || minChars < 1) minChars = 2;
    requestPerPage = parseInt(cfg.requestPerPage, 10);
    if (isNaN(requestPerPage) || requestPerPage < 1) requestPerPage = 10;
    suggestionLimit = parseInt(cfg.suggestionLimit, 10);
    if (isNaN(suggestionLimit) || suggestionLimit < 1) suggestionLimit = 10;
    debounceMs = parseInt(cfg.debounceMs, 10);
    if (isNaN(debounceMs) || debounceMs < 0) debounceMs = 250;
    panelId = classPrefix + '-panel-' + (++_scopedTypeaheadCounter);

    if (input._psScopedTypeahead && typeof input._psScopedTypeahead.destroy === 'function') {
      try { input._psScopedTypeahead.destroy(); } catch(_e) {}
    }

    docClickHandler = function(evt) {
      var target = evt.target || evt.srcElement;
      if (!shell || !shell.getAttribute('data-open')) return;
      if (target === input) return;
      if (shell.contains(target)) return;
      closePanel();
    };

    input.addEventListener('input', debounce(function() {
      var term = trimValue(input.value);
      if (!term.length) {
        clearSuggestions();
        return;
      }
      requestSuggestions(term);
    }, debounceMs));

    input.addEventListener('focus', function() {
      var term = trimValue(input.value);
      if (term.length >= minChars) requestSuggestions(term);
    });

    input.addEventListener('blur', function() {
      setTimeout(function() {
        var active = document.activeElement;
        if (active === input) return;
        if (shell && active && shell.contains(active)) return;
        closePanel();
      }, 120);
    });

    input.addEventListener('keydown', function(evt) {
      var key = evt.which || evt.keyCode;
      if (!shell || !shell.getAttribute('data-open')) return;
      if (key === 40) {
        evt.preventDefault();
        setActiveIndex(activeIndex + 1);
      } else if (key === 38) {
        evt.preventDefault();
        setActiveIndex(activeIndex - 1);
      } else if (key === 13) {
        if (activeIndex > -1) {
          evt.preventDefault();
          applySuggestion(activeIndex);
        }
      } else if (key === 27) {
        evt.preventDefault();
        closePanel();
      }
    });

    if (form) {
      form.addEventListener('submit', function() {
        clearSuggestions();
      });
    }

    document.addEventListener('click', docClickHandler);

    input._psScopedTypeahead = {
      close: closePanel,
      clear: clearSuggestions,
      destroy: function() {
        abortPendingRequest();
        closePanel();
        if (shell && shell.parentNode) shell.parentNode.removeChild(shell);
        shell = null;
        list = null;
        message = null;
        footer = null;
        suggestions = [];
        activeIndex = -1;
        document.removeEventListener('click', docClickHandler);
        input._psScopedTypeahead = null;
        input.removeAttribute('aria-controls');
        input.removeAttribute('aria-haspopup');
        input.removeAttribute('aria-autocomplete');
        input.removeAttribute('aria-expanded');
        input.removeAttribute('aria-activedescendant');
      }
    };

    return input._psScopedTypeahead;
  }

  // ==========================================================
  // 2. IN-MEMORY CACHE + HTTP
  // ==========================================================

  var _cache = {};

  function cachedGet(url, cb) {
    if (_cache[url]) {
      setTimeout(function() { cb(_cache[url].data, _cache[url].status); }, 0);
      return null;
    }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onreadystatechange = function() {
      if (xhr.readyState !== 4) return;
      var data = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try { data = JSON.parse(xhr.responseText); } catch(e) {}
      }
      if (data) { _cache[url] = { data: data, status: xhr.status }; }
      cb(data, xhr.status);
    };
    xhr.send(null);
    return xhr;
  }

  // ==========================================================
  // 3. COOKIE I/O
  // ==========================================================

  function readCookie() {
    try {
      var cname = window.PS && PS.cc && PS.cc.NAME ? PS.cc.NAME : 'ps_cc';
      var m = (document.cookie || '').match(new RegExp('(?:^|; )' + cname + '=([^;]*)'));
      if (m) {
        try { return JSON.parse(decodeURIComponent(m[1] || '')); } catch(_e) { return {}; }
      }
    } catch(_e2) {}
    return {};
  }

  function writeCookie(p) {
    try {
      var safePayload = p || {};
      var key = '';
      if (safePayload.q && isSensitiveSearchValue(safePayload.q)) {
        safePayload = {};
        for (key in p) {
          if (p.hasOwnProperty(key)) safePayload[key] = p[key];
        }
        safePayload.q = '';
      }
      if (window.PS && PS.cc && typeof PS.cc.write === 'function') { PS.cc.write(safePayload); }
    } catch(_e) {}
  }

  function writePendingVehicleColorContext(payload) {
    var data = payload || {};
    if (!data.make_slug || !data.color_slug || !data.year) return;
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem('ps_pending_vehicle_color_context', JSON.stringify({
          make_slug: String(data.make_slug).toLowerCase(),
          color_slug: String(data.color_slug).toLowerCase(),
          year: String(data.year || ''),
          model: String(data.model || ''),
          ts: Date.now()
        }));
      }
    } catch(_e) {}
  }

  function writeUrlMakeContextCookie(payload) {
    var value = '';
    var parts = [];
    var hostParts = [];
    if (!payload) return;
    try {
      if (window.PS && PS.cc && typeof PS.cc.write === 'function') {
        PS.cc.write(payload);
        return;
      }
    } catch(_e) {}
    try {
      payload.ts = Date.now();
      value = encodeURIComponent(JSON.stringify(payload));
      parts = ['ps_cc=' + value, 'Path=/', 'Max-Age=' + (60 * 60 * 24 * 30), 'SameSite=Lax'];
      if (window.location.protocol === 'https:') parts.push('Secure');
      hostParts = String(window.location.hostname || '').split('.');
      if (hostParts.length > 1) parts.push('Domain=.' + hostParts.slice(-2).join('.'));
      document.cookie = 'ps_cc=; Path=/; Max-Age=0; SameSite=Lax' + (window.location.protocol === 'https:' ? '; Secure' : '');
      document.cookie = parts.join('; ');
    } catch(_err) {}
  }

  function readQueryParam(name) {
    var raw = '';
    var parts = [];
    var i = 0;
    var pair = [];
    try {
      raw = String((window.location && window.location.search) || '').replace(/^\?/, '');
    } catch(_e) {
      raw = '';
    }
    if (!raw) return '';
    parts = raw.split('&');
    for (i = 0; i < parts.length; i++) {
      pair = parts[i].split('=');
      if (safeDecodeQueryPart(pair[0]) === name) {
        return safeDecodeQueryPart(pair.slice(1).join('='));
      }
    }
    return '';
  }

  function queryParamCount(name) {
    var raw = String((window.location && window.location.search) || '').replace(/^\?/, '');
    var parts = raw ? raw.split('&') : [];
    var count = 0;
    var i, pair;
    for (i = 0; i < parts.length; i++) {
      pair = parts[i].split('=');
      if (safeDecodeQueryPart(pair[0]) === name) count += 1;
    }
    return count;
  }

  function safeDecodeQueryPart(value) {
    try {
      return decodeURIComponent(String(value || '').replace(/\+/g, ' '));
    } catch(_e) {
      return '';
    }
  }

  function sameMakeContext(current, makeSlug) {
    var currentSlug = toSlug(current && (current.make_slug || current.make));
    return !!(currentSlug && makeSlug && currentSlug === makeSlug);
  }

  function isContextProductPath() {
    var path = '';
    try {
      path = String((window.location && window.location.pathname) || '');
    } catch(_e) {
      path = '';
    }
    return /^\/products\/touch-up-paint-(pen|bottle-2oz|spray-can|pro-gallon|kit-pen|kit-bottle|kit-spray)\/?$/i.test(path);
  }

  function applyUrlMakeContext() {
    var active = readQueryParam('ps_make_context');
    var makeName = '';
    var makeSlug = '';
    var current = {};
    if (window.__psMakeContextApplied) return;
    window.__psMakeContextApplied = true;
    if (active !== '1' || !isContextProductPath()) return;
    makeName = String(readQueryParam('ps_make') || '').replace(/^\s+|\s+$/g, '');
    makeSlug = toSlug(readQueryParam('ps_make_slug') || makeName);
    if (!makeName || !makeSlug || makeName.length > 80 || makeSlug.length > 80) return;
    current = readCookie() || {};
    if (sameMakeContext(current, makeSlug)) return;
    writeUrlMakeContextCookie({
      make: makeName,
      make_slug: makeSlug,
      model: '',
      color: '',
      code: '',
      hex: '',
      year: '',
      factory_code: '',
      color_slug: '',
      vehicle_scope: false
    });
  }

  function applyUrlVehicleHandoff() {
    var path = String((window.location && window.location.pathname) || '');
    var markerCount = queryParamCount('ps_search_vehicle');
    var searchApi = window.PS && PS.Search;
    var scope = {};
    var names = ['make', 'year', 'model'];
    var values = {};
    var i, name, raw, count, classify, sensitive;
    function writeScope(vehicle) {
      vehicle = vehicle || {};
      writeUrlMakeContextCookie({
        make: vehicle.make || '', make_slug: vehicle.make ? toSlug(vehicle.make) : '',
        year: vehicle.year || '', model: vehicle.model || '',
        color: '', code: '', hex: '', factory_code: '', color_slug: '', image: '',
        valid: false, shopify_page: '', products: [], q: '', vehicle_scope: false, ps_from_search: false
      });
    }
    if (path !== '/' || !markerCount || window.__psSearchVehicleHandoffApplied) return;
    window.__psSearchVehicleHandoffApplied = true;
    if (markerCount !== 1 || readQueryParam('ps_search_vehicle') !== '1' ||
        !searchApi || typeof searchApi.vehicleContext !== 'function' || typeof searchApi.classifySensitive !== 'function') {
      writeScope({});
      return;
    }
    try { scope = searchApi.vehicleContext() || {}; } catch (_e) { scope = {}; }
    classify = searchApi.classifySensitive;
    for (i = 0; i < names.length; i++) {
      name = names[i];
      count = queryParamCount(name);
      raw = readQueryParam(name);
      values[name] = scope[name] == null ? '' : String(scope[name]).replace(/^\s+|\s+$/g, '');
      if (count > 1 || (count && !raw) || (count && raw !== values[name])) {
        writeScope({});
        return;
      }
      try { sensitive = values[name] ? classify(values[name]) : ''; } catch (_e2) { sensitive = 'invalid'; }
      if (values[name] && (!/^[a-z0-9 .()'\-\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff]{1,60}$/i.test(values[name]) || sensitive ||
          (name === 'year' && !/^\d{4}$/.test(values[name])))) {
        writeScope({});
        return;
      }
    }
    if (!values.make) { writeScope({}); return; }
    writeScope(values);
  }

  // ==========================================================
  // 4. PRODUCT METADATA HELPERS
  // ==========================================================

  // Extract per-product-type metadata from a color result's products field.
  function extractMeta(products) {
    var out = {
      pen:    { url: '', img: '', name: '', price: '' },
      bottle: { url: '', img: '', name: '', price: '' },
      spray:  { url: '', img: '', name: '', price: '' },
      pro:    { url: '', img: '', name: '', price: '' }
    };
    function setFor(base, entry) {
      if (!base || !out[base]) return;
      if (entry.url) out[base].url = entry.url;
      var img = entry.imageUrl || entry.image_thumb || (entry.images && entry.images[0]) || '';
      if (img) out[base].img = img;
      if (entry.name || entry.title) out[base].name = entry.name || entry.title;
      if (entry.price) out[base].price = entry.price;
    }
    if (!products) return out;
    if (Array.isArray(products)) {
      for (var i = 0; i < products.length; i++) {
        var p = products[i] || {};
        var t = String(p.type_slug || '').toLowerCase().split('-')[0];
        setFor(t, p);
      }
    } else if (typeof products === 'object') {
      for (var key in products) {
        if (!products.hasOwnProperty(key)) continue;
        var base = String(key).toLowerCase().split('-')[0];
        setFor(base, products[key] || {});
      }
    }
    return out;
  }

  // Build URL/img/name maps from the MakeColor detail API response.
  function buildMapsFromMakeColor(data) {
    var maps = {
      url:  { pen: '', bottle: '', spray: '', pro: '' },
      img:  { pen: '', bottle: '', spray: '', pro: '' },
      name: { pen: '', bottle: '', spray: '', pro: '' }
    };
    if (!data || !data.products) return maps;
    function inferBase(entry, keyHint) {
      var raw = (entry && (entry.slug || entry.type_slug || entry.product_slug || entry.handle || '')) || keyHint || '';
      raw = String(raw || '').toLowerCase();
      if (raw.indexOf('paint-pen') > -1) raw = 'pen';
      if (raw.indexOf('paint-bottle') > -1) raw = 'bottle';
      if (raw.indexOf('paint-spray') > -1) raw = 'spray';
      var b = raw.split('-')[0];
      if (b !== 'pen' && b !== 'bottle' && b !== 'spray' && b !== 'pro') return '';
      return b;
    }
    function set(b, src) {
      if (!b || !maps.url.hasOwnProperty(b)) return;
      if (src.url) maps.url[b] = src.url;
      var img = src.image_thumb || src.imageUrl || (src.images && src.images[0]) || '';
      if (img) maps.img[b] = img;
      var nm = src.name || src.title;
      if (nm) maps.name[b] = nm;
    }
    var prod = data.products;
    if (prod && typeof prod === 'object' && !Array.isArray(prod)) {
      for (var k in prod) {
        if (!prod.hasOwnProperty(k)) continue;
        set(inferBase(prod[k] || {}, k), prod[k] || {});
      }
    } else if (Array.isArray(prod)) {
      for (var i = 0; i < prod.length; i++) {
        set(inferBase(prod[i] || {}, ''), prod[i] || {});
      }
    }
    return maps;
  }

  // ==========================================================
  // 5. INSTANCE CREATOR
  // ==========================================================

  function createInstance(cfg) {
    cfg = cfg || {};
    var uid = cfg.uid || '';
    var pfx = cfg.prefix || 'ps-vcs';
    var selectShellTheme = cfg.selectShellTheme || null;

    // ---- Element discovery ----
    // Looks up by ID (prefix-role-uid), then by class, then null.
    function el(role) {
      var id = pfx + '-' + role + '-' + uid;
      return document.getElementById(id) || $('.' + id) || null;
    }

    var root      = cfg.root ? (typeof cfg.root === 'string' ? $(cfg.root) : cfg.root) : document.getElementById(pfx + '-' + uid);
    var makeSel   = el('make');
    var yearSel   = el('year');
    var modelSel  = el('model');
    var searchQ   = el('q');
    if (searchQ) searchQ.setAttribute('data-clarity-mask', 'true');
    var submitBtn = el('btn');
    var grid      = el('grid') || el('results');
    var loader    = el('loader');
    var errBox    = el('error');
    var emptyBox  = el('empty');
    var picked    = el('picked');
    var searchUI  = el('search-ui');
    var pager     = el('pager');
    var prevBtn   = el('prev');
    var nextBtn   = el('next');
    var pagesWrap = el('pages');
    var statusSR  = el('status');
    var pagerWrap = el('pager-wrap');

    if (!makeSel) return null;

    // ---- Config ----
    var productBase              = cfg.productBase || '';
    var perPage                  = cfg.perPage || 15;
    var navigateOnSelect         = cfg.navigateOnSelect !== false;
    var showPickedSummary        = !!cfg.showPicked;
    var enableSearch             = !!(cfg.enableSearch && searchQ);
    var hydrateFromCookie        = !!cfg.hydrate;
    var doRewriteProducts        = !!cfg.rewriteProducts;
    var doReplaceText            = !!cfg.replaceVehicleCopy;
    var replaceSelectionTokens   = cfg.replaceSelectionTokens !== false;
    var ensureKitHubCtasEnabled  = cfg.ensureKitHubCtas !== false;
    var searchInputGated         = cfg.searchInputGated !== false;
    var keywordSearchMinChars    = 2;
    var directSearchDebounceMs   = 300;
    var directGridSearchMode     = pfx === 'pscs';
    var searchOnSelectedHydrate  = cfg.searchOnSelectedHydrate === true || !!productBase;
    var sameHostColorHub         = cfg.sameHostColorHub !== false;
    var isHomeTemplate           = !!cfg.isHomeTemplate;
    var cardStyle                = cfg.cardStyle || 'compact'; // 'compact' or 'row'
    var showHelpCard             = cfg.showHelpCard !== false;
    var onColorClick             = typeof cfg.onColorClick === 'function' ? cfg.onColorClick : null;
    var onColorValidated         = typeof cfg.onColorValidated === 'function' ? cfg.onColorValidated : null;

    // Product row wrapper IDs (for homepage search mode)
    var prodRowRegId       = (cfg.productRows && cfg.productRows.regularId) || 'ps_search_products_regular';
    var prodRowTriId       = (cfg.productRows && cfg.productRows.tricoatId) || 'ps_search_products_tricoat';
    var prodRowUnknownId   = (cfg.productRows && cfg.productRows.unknownId) || '';
    var prodRowUnknownIds  = [];
    if (cfg.productRows && Object.prototype.toString.call(cfg.productRows.unknownIds) === '[object Array]') {
      prodRowUnknownIds = cfg.productRows.unknownIds.slice(0);
    }
    if (prodRowUnknownId) prodRowUnknownIds.unshift(prodRowUnknownId);
    var selectedHeaderId   = cfg.selectedHeaderId || 'color_selected_header';

    // Text replacement config
    var textReplaceList = (cfg.textReplace && cfg.textReplace.replaceList) || ['custom color', 'selected color'];
    var textAppendList  = (cfg.textReplace && cfg.textReplace.appendList) || ['factory-matched custom'];

    // ---- State ----
    var state = { make: '', year: '', model: '', q: '', page: 1, per: perPage, pages: 1, backend: 'hub', lastItems: [], lastTwoToneGroups: [] };
    var editRestoreToken = 0;
    var submittedSearchRestoreBlocked = false;
    var submittedSearchRestoreTimer = null;
    var pendingXhr = null;
    var inFlight = false;
    var overlayPane = null;
    var _autoHydrated = false;
    var hydrationXhr = null;
    var hydrationRequestSeq = 0;
    var selectedFormHydrationActive = false;
    var showBtnSpinner = false;
    var fetchRequestSeq = 0;
    var directSearchTimer = null;
    var historySnapshotKey = pfx + ':' + uid;
    var editRestoreStateKey = 'ps-edit-restore:' + historySnapshotKey;
    var submittedSearchStateKey = 'ps-submitted-search:' + historySnapshotKey;
    var submittedSearchGlobalStateKey = 'ps-submitted-search:' + pfx + ':global';
    var submittedSearchMaxAgeMs = 6 * 60 * 60 * 1000;
    var submittedSearchCapturePending = false;
    var submittedSearchCaptureScope = null;
    var makesLoadRequestSeq = 0;
    var yearsLoadRequestSeq = 0;
    var modelsLoadRequestSeq = 0;
    var typeaheadState = {
      host: null,
      shell: null,
      list: null,
      message: null,
      footer: null,
      suggestions: [],
      seeAllUrl: '',
      totalFound: 0,
      activeIndex: -1,
      requestId: 0,
      pendingXhr: null,
      cache: {}
    };
    var selectStores = {
      make: {
        select: makeSel,
        options: [],
        placeholderText: 'Select Make',
        groupMode: 'alpha',
        emptyText: 'No matching makes',
        searchPlaceholder: 'Search makes',
        ui: null
      },
      year: {
        select: yearSel,
        options: [],
        placeholderText: 'Select Year',
        groupMode: 'decade',
        emptyText: 'No matching years',
        searchPlaceholder: 'Search years',
        ui: null
      },
      model: {
        select: modelSel,
        options: [],
        placeholderText: 'Select Model',
        groupMode: 'alpha',
        emptyText: 'No matching models',
        searchPlaceholder: 'Search models',
        ui: null
      }
    };

    // ==========================================================
    // DISPLAY HELPERS
    // ==========================================================

    function setLoading(on, overlay) {
      inFlight = !!on;
      if (on) {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-disabled', 'true');
        }
        if (overlay && grid) {
          grid.classList.remove('is-loading');
          grid.classList.add('is-overlay');
          if (!overlayPane) {
            overlayPane = document.createElement('div');
            overlayPane.className = pfx + '-overlay-pane-' + uid;
            overlayPane.innerHTML =
              '<div class="' + pfx + '-ol-wrap-' + uid + '">' +
              '<div class="' + pfx + '-ol-text-' + uid + '">Loading colors...</div>' +
              '<span class="' + pfx + '-spinner-' + uid + '" aria-hidden="true" style="display:inline-block"></span>' +
              '</div>';
            grid.appendChild(overlayPane);
          }
          if (statusSR) statusSR.textContent = 'Loading next page...';
        } else {
          if (grid) {
            grid.classList.remove('is-overlay');
            if (overlayPane && overlayPane.parentNode) overlayPane.parentNode.removeChild(overlayPane);
            overlayPane = null;
            grid.classList.add('is-loading');
          }
          if (loader) loader.style.display = 'flex';
          if (submitBtn && showBtnSpinner) submitBtn.classList.add('is-busy');
          if (statusSR) statusSR.textContent = 'Loading results...';
        }
        if (pager) pager.setAttribute('aria-busy', 'true');
      } else {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-disabled');
        }
        if (grid) grid.classList.remove('is-loading', 'is-overlay');
        if (overlayPane && overlayPane.parentNode) overlayPane.parentNode.removeChild(overlayPane);
        overlayPane = null;
        if (loader) loader.style.display = 'none';
        if (pager) pager.setAttribute('aria-busy', 'false');
        if (submitBtn) submitBtn.classList.remove('is-busy');
        if (statusSR) statusSR.textContent = 'Results updated.';
      }
    }

    function showError(msg) {
      if (errBox) {
        if (msg) {
          errBox.innerHTML = esc(msg) + ' <a href="#" data-ps-retry="1" style="color:#991b1b;text-decoration:underline;font-weight:600">Try again</a>';
        }
        errBox.style.display = msg ? 'block' : 'none';
      } else if (msg && grid) {
        grid.innerHTML = '<div style="text-align:center;padding:16px;color:#999">' + esc(msg) + '</div>';
      }
    }

    function showEmpty(on) {
      if (emptyBox) {
        emptyBox.style.display = on ? 'block' : 'none';
      } else if (on && grid) {
        grid.innerHTML = '<div style="text-align:center;padding:16px;color:#999">No colors found.</div>';
      }
    }

    function clearGrid() {
      state.lastItems = [];
      state.lastTwoToneGroups = [];
      if (grid) grid.innerHTML = '';
      showEmpty(false);
      if (pager) pager.style.display = 'none';
      setPagerWrapDisplay(false);
    }

    function abortPending() {
      if (pendingXhr && pendingXhr.readyState !== 4) {
        try { pendingXhr.abort(); } catch(e) {}
      }
      pendingXhr = null;
    }

    function cancelActiveSearch() {
      if (!inFlight) return;
      fetchRequestSeq += 1;
      abortPending();
      finalizeSubmittedSearchCapture(false);
      setLoading(false);
    }

    // ==========================================================
    // DROPDOWN MANAGEMENT
    // ==========================================================

    function setSelectDisabled(sel, disabled) {
      if (!sel) return;
      sel.disabled = !!disabled;
      if (disabled) sel.setAttribute('aria-disabled', 'true');
      else sel.removeAttribute('aria-disabled');
    }

    function buildOptionItem(value, text) {
      return {
        value: String(value == null ? '' : value),
        text: String(text == null ? value : text)
      };
    }

    function getSelectStoreByElement(sel) {
      var key = '';
      for (key in selectStores) {
        if (!selectStores.hasOwnProperty(key)) continue;
        if (selectStores[key].select === sel) return selectStores[key];
      }
      return null;
    }

    function findDropdownHosts(node) {
      var current = node;
      var hosts = [];
      var computed = null;
      while (current && current !== document.body) {
        computed = window.getComputedStyle ? window.getComputedStyle(current, null) : current.currentStyle;
        if (
          (current.className && String(current.className).indexOf('shopify-section') > -1) ||
          (computed && computed.zIndex && computed.zIndex !== 'auto')
        ) {
          hosts.push({
            node: current,
            basePosition: current.style.position || '',
            baseZIndex: current.style.zIndex || ''
          });
        }
        current = current.parentNode;
      }
      return hosts;
    }

    function ensureDropdownRootLayer(node) {
      var computed = null;
      if (!node || !node.style) return;
      computed = window.getComputedStyle ? window.getComputedStyle(node, null) : node.currentStyle;
      if (computed && (!computed.position || computed.position === 'static')) {
        node.style.position = 'relative';
      }
      if (!node.style.zIndex) node.style.zIndex = '2';
    }

    function elevateDropdownHosts(cfg) {
      var hosts = null;
      var entry = null;
      var computed = null;
      var i;
      if (!cfg || !cfg.ui || !cfg.ui.hosts || !cfg.ui.hosts.length) return;
      hosts = cfg.ui.hosts;
      for (i = 0; i < hosts.length; i++) {
        entry = hosts[i];
        if (!entry || !entry.node) continue;
        computed = window.getComputedStyle ? window.getComputedStyle(entry.node, null) : entry.node.currentStyle;
        if (
          computed &&
          (!computed.position || computed.position === 'static') &&
          entry.node.className &&
          String(entry.node.className).indexOf('shopify-section') > -1
        ) {
          entry.node.style.position = 'relative';
        }
        entry.node.style.zIndex = String(70 + i);
      }
    }

    function resetDropdownHosts(cfg) {
      var hosts = null;
      var entry = null;
      var i;
      if (!cfg || !cfg.ui || !cfg.ui.hosts || !cfg.ui.hosts.length) return;
      hosts = cfg.ui.hosts;
      for (i = 0; i < hosts.length; i++) {
        entry = hosts[i];
        if (!entry || !entry.node) continue;
        entry.node.style.position = entry.basePosition || '';
        entry.node.style.zIndex = entry.baseZIndex || '';
      }
    }

    function resetSelectStore(name) {
      var cfg = selectStores[name];
      if (!cfg) return;
      cfg.options = [];
      if (cfg.ui && cfg.ui.search) cfg.ui.search.value = '';
    }

    function normalizeOptionItem(raw) {
      var value = '';
      var text = '';
      if (raw == null) return null;
      if (typeof raw === 'string' || typeof raw === 'number') {
        return buildOptionItem(raw, raw);
      }
      if (typeof raw !== 'object') return null;
      if (raw.value != null && String(raw.value) !== '') value = raw.value;
      else if (raw.name != null && String(raw.name) !== '') value = raw.name;
      else if (raw.label != null && String(raw.label) !== '') value = raw.label;
      else if (raw.model != null && String(raw.model) !== '') value = raw.model;
      else if (raw.text != null && String(raw.text) !== '') value = raw.text;

      if (raw.text != null && String(raw.text) !== '') text = raw.text;
      else if (raw.label != null && String(raw.label) !== '') text = raw.label;
      else if (raw.name != null && String(raw.name) !== '') text = raw.name;
      else if (raw.model != null && String(raw.model) !== '') text = raw.model;
      else text = value;

      if (!value && !text) return null;
      return buildOptionItem(value || text, text || value);
    }

    function pickThemeToken(value, fallback) {
      if (value == null) return fallback;
      value = String(value);
      return value ? value : fallback;
    }

    function buildSearchShellTheme(themeCfg, computed) {
      var enabled = themeCfg && themeCfg.enabled ? themeCfg.enabled : {};
      var disabled = themeCfg && themeCfg.disabled ? themeCfg.disabled : {};
      var open = themeCfg && themeCfg.open ? themeCfg.open : {};
      var stepActive = themeCfg && themeCfg.stepActive ? themeCfg.stepActive : {};
      var baseBorderColor = computed.borderTopColor || '#ccc';
      var baseBackground = computed.backgroundColor || '#fff';
      var baseColor = computed.color || '#121212';
      var baseBoxShadow = computed.boxShadow && computed.boxShadow !== 'none' ? computed.boxShadow : 'none';
      return {
        enabled: {
          background: pickThemeToken(enabled.background, baseBackground),
          color: pickThemeToken(enabled.color, baseColor),
          borderColor: pickThemeToken(enabled.borderColor, baseBorderColor),
          boxShadow: pickThemeToken(enabled.boxShadow, baseBoxShadow),
          opacity: pickThemeToken(enabled.opacity, '1'),
          fontWeight: pickThemeToken(enabled.fontWeight, computed.fontWeight || '400')
        },
        disabled: {
          background: pickThemeToken(disabled.background, baseBackground),
          color: pickThemeToken(disabled.color, baseColor),
          borderColor: pickThemeToken(disabled.borderColor, baseBorderColor),
          boxShadow: pickThemeToken(disabled.boxShadow, baseBoxShadow),
          opacity: pickThemeToken(disabled.opacity, '0.6'),
          fontWeight: pickThemeToken(disabled.fontWeight, computed.fontWeight || '400')
        },
        open: {
          borderColor: pickThemeToken(open.borderColor, '#164068'),
          boxShadow: pickThemeToken(open.boxShadow, '0 0 0 2px rgba(22,64,104,0.16)')
        },
        stepActive: {
          background: pickThemeToken(stepActive.background, ''),
          color: pickThemeToken(stepActive.color, ''),
          borderColor: pickThemeToken(stepActive.borderColor, '#e4431e'),
          boxShadow: pickThemeToken(stepActive.boxShadow, '0 0 0 2px rgba(228,67,30,0.28)'),
          fontWeight: pickThemeToken(stepActive.fontWeight, '')
        }
      };
    }

    function isSearchBoundaryChar(ch) {
      return !(/[a-z0-9]/i).test(String(ch || ''));
    }

    function buildSearchMatch(option, query, index) {
      var text = String((option && option.text) || '').toLowerCase();
      var value = String((option && option.value) || '').toLowerCase();
      var textPos = -1;
      var valuePos = -1;
      var rank = 99;
      var pos = 999;
      if (!option) return null;
      if (!query) {
        return {
          option: option,
          rank: 0,
          pos: 0,
          index: index
        };
      }
      textPos = text.indexOf(query);
      valuePos = value.indexOf(query);
      if (textPos === 0) {
        rank = 0;
        pos = textPos;
      } else if (valuePos === 0) {
        rank = 1;
        pos = valuePos;
      } else if (textPos > -1 && isSearchBoundaryChar(text.charAt(textPos - 1))) {
        rank = 2;
        pos = textPos;
      } else if (valuePos > -1 && isSearchBoundaryChar(value.charAt(valuePos - 1))) {
        rank = 3;
        pos = valuePos;
      } else if (textPos > -1) {
        rank = 4;
        pos = textPos;
      } else if (valuePos > -1) {
        rank = 5;
        pos = valuePos;
      }
      if (rank === 99) return null;
      return {
        option: option,
        rank: rank,
        pos: pos,
        index: index
      };
    }

    function resolveOptionValue(options, value, caseInsensitive) {
      var i;
      var optionValue = '';
      var target = String(value || '').trim();
      var lower = target.toLowerCase();
      if (!target) return '';
      for (i = 0; i < options.length; i++) {
        optionValue = String((options[i] && options[i].value) || '').trim();
        if (!optionValue) continue;
        if (optionValue === target) return options[i].value;
        if (caseInsensitive && optionValue.toLowerCase() === lower) return options[i].value;
      }
      return '';
    }

    function resolveHandoffMake(options, value) {
      var target = String(value || '').replace(/\u00eb/gi, 'e').toLowerCase();
      var i;
      var optionValue;
      if (!target) return '';
      for (i = 0; i < options.length; i++) {
        optionValue = String((options[i] && options[i].value) || '').replace(/\u00eb/gi, 'e').toLowerCase();
        if (optionValue && optionValue === target) return options[i].value;
      }
      return '';
    }

    function renderSelectOptions(cfg, selectedValue) {
      var html = [];
      var i;
      var option = null;
      var selected = resolveOptionValue(cfg.options || [], selectedValue, true);
      var hasSelected = !!selected;
      if (!cfg || !cfg.select) return;

      html.push('<option value="" disabled' + (hasSelected ? '' : ' selected') + '>' + esc(cfg.placeholderText) + '</option>');
      if (!cfg.options || !cfg.options.length) {
        cfg.select.innerHTML = html.join('');
        cfg.select.selectedIndex = 0;
        return;
      }
      for (i = 0; i < cfg.options.length; i++) {
        option = cfg.options[i];
        if (!option) continue;
        html.push('<option value="' + esc(option.value) + '"' + (selected === option.value ? ' selected' : '') + '>' + esc(option.text) + '</option>');
      }

      cfg.select.innerHTML = html.join('');
      if (!hasSelected) cfg.select.selectedIndex = 0;
    }

    function dispatchSelectChange(sel) {
      var ev = null;
      if (!sel) return;
      if (typeof Event === 'function') {
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      ev = document.createEvent('Event');
      ev.initEvent('change', true, false);
      sel.dispatchEvent(ev);
    }

    function searchDropdownLabel(cfg) {
      var selectedIndex = 0;
      if (!cfg || !cfg.select) return '';
      if (cfg.select.getAttribute('data-ps-fetching') === 'true' && cfg.select.options && cfg.select.options[0]) {
        return cfg.select.options[0].text || cfg.placeholderText;
      }
      selectedIndex = cfg.select.selectedIndex;
      if (selectedIndex > -1 && cfg.select.options && cfg.select.options[selectedIndex]) {
        return cfg.select.options[selectedIndex].text || cfg.placeholderText;
      }
      return cfg.placeholderText;
    }

    function closeSearchDropdown(cfg) {
      if (!cfg || !cfg.ui) return;
      cfg.ui.panel.style.display = 'none';
      cfg.ui.wrapper.style.zIndex = '1';
      cfg.ui.trigger.setAttribute('aria-expanded', 'false');
      cfg.ui.wrapper.setAttribute('data-ps-open', 'false');
      applySearchTriggerVisualState(cfg);
      resetDropdownHosts(cfg);
    }

    function closeAllSearchDropdowns(skipCfg) {
      var key = '';
      for (key in selectStores) {
        if (!selectStores.hasOwnProperty(key)) continue;
        if (skipCfg && selectStores[key] === skipCfg) continue;
        closeSearchDropdown(selectStores[key]);
      }
    }

    function renderSearchDropdownList(cfg) {
      var fragment = null;
      var empty = null;
      var option = null;
      var button = null;
      var query = '';
      var matches = [];
      var match = null;
      var i;
      if (!cfg || !cfg.ui || !cfg.ui.list) return;
      cfg.ui.list.innerHTML = '';
      if (cfg.select.disabled || cfg.select.getAttribute('data-ps-fetching') === 'true') {
        empty = document.createElement('div');
        empty.style.padding = '12px 14px';
        empty.style.color = '#666';
        empty.style.fontSize = cfg.ui.baseFontSize;
        empty.appendChild(document.createTextNode(searchDropdownLabel(cfg)));
        cfg.ui.list.appendChild(empty);
        return;
      }
      query = String((cfg.ui.search && cfg.ui.search.value) || '').toLowerCase();
      for (i = 0; i < cfg.options.length; i++) {
        option = cfg.options[i];
        if (!option) continue;
        match = buildSearchMatch(option, query, i);
        if (match) matches.push(match);
      }
      if (!matches.length) {
        empty = document.createElement('div');
        empty.style.padding = '12px 14px';
        empty.style.color = '#666';
        empty.style.fontSize = cfg.ui.baseFontSize;
        empty.appendChild(document.createTextNode(cfg.emptyText));
        cfg.ui.list.appendChild(empty);
        return;
      }
      if (query && matches.length > 1) {
        matches.sort(function(a, b) {
          if (a.rank !== b.rank) return a.rank - b.rank;
          if (a.pos !== b.pos) return a.pos - b.pos;
          return a.index - b.index;
        });
      }
      fragment = document.createDocumentFragment();
      for (i = 0; i < matches.length; i++) {
        option = matches[i].option;
        button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-value', option.value);
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', cfg.select.value === option.value ? 'true' : 'false');
        button.style.display = 'block';
        button.style.width = '100%';
        button.style.padding = '10px 14px';
        button.style.border = '0';
        button.style.background = cfg.select.value === option.value ? 'rgba(22,64,104,0.08)' : 'transparent';
        button.style.color = cfg.ui.baseColor;
        button.style.fontFamily = cfg.ui.baseFontFamily;
        button.style.fontSize = cfg.ui.baseFontSize;
        button.style.fontWeight = cfg.select.value === option.value ? '600' : '400';
        button.style.lineHeight = '1.3';
        button.style.textAlign = 'left';
        button.style.cursor = 'pointer';
        button.appendChild(document.createTextNode(option.text));
        fragment.appendChild(button);
      }
      cfg.ui.list.appendChild(fragment);
    }

    function openSearchDropdown(cfg) {
      if (!cfg || !cfg.ui || cfg.select.disabled) return;
      closeAllSearchDropdowns(cfg);
      cfg.ui.search.value = '';
      renderSearchDropdownList(cfg);
      elevateDropdownHosts(cfg);
      cfg.ui.wrapper.style.zIndex = '50';
      cfg.ui.panel.style.display = 'block';
      cfg.ui.trigger.setAttribute('aria-expanded', 'true');
      cfg.ui.wrapper.setAttribute('data-ps-open', 'true');
      applySearchTriggerVisualState(cfg);
      try { cfg.ui.search.focus(); } catch(_e) {}
    }

    function applySearchTriggerVisualState(cfg) {
      var theme = null;
      var triggerTheme = null;
      var isOpen = false;
      var isStepActive = false;
      if (!cfg || !cfg.ui) return;
      theme = cfg.ui.theme;
      if (!theme) return;
      isOpen = cfg.ui.panel && cfg.ui.panel.style.display === 'block';
      isStepActive = cfg.ui.trigger.getAttribute('data-ps-step-active') === 'true';
      triggerTheme = cfg.select.disabled ? theme.disabled : theme.enabled;

      cfg.ui.trigger.style.backgroundColor = triggerTheme.background;
      cfg.ui.trigger.style.color = triggerTheme.color;
      cfg.ui.trigger.style.borderColor = triggerTheme.borderColor;
      cfg.ui.trigger.style.boxShadow = triggerTheme.boxShadow;
      cfg.ui.trigger.style.opacity = triggerTheme.opacity;
      cfg.ui.trigger.style.fontWeight = triggerTheme.fontWeight;
      cfg.ui.trigger.style.cursor = cfg.select.disabled ? 'not-allowed' : 'pointer';
      if (cfg.ui.label) cfg.ui.label.style.color = triggerTheme.color;
      if (cfg.ui.caret) cfg.ui.caret.style.color = triggerTheme.color;

      if (isStepActive && !cfg.select.disabled) {
        if (theme.stepActive.background) cfg.ui.trigger.style.backgroundColor = theme.stepActive.background;
        if (theme.stepActive.color) {
          cfg.ui.trigger.style.color = theme.stepActive.color;
          if (cfg.ui.label) cfg.ui.label.style.color = theme.stepActive.color;
          if (cfg.ui.caret) cfg.ui.caret.style.color = theme.stepActive.color;
        }
        if (theme.stepActive.fontWeight) cfg.ui.trigger.style.fontWeight = theme.stepActive.fontWeight;
        cfg.ui.trigger.style.borderColor = theme.stepActive.borderColor;
        cfg.ui.trigger.style.boxShadow = theme.stepActive.boxShadow;
      }

      if (isOpen) {
        cfg.ui.trigger.style.borderColor = theme.open.borderColor;
        cfg.ui.trigger.style.boxShadow = theme.open.boxShadow;
      }
    }

    function syncSearchDropdown(cfg) {
      if (!cfg || !cfg.ui) return;
      cfg.ui.label.textContent = searchDropdownLabel(cfg);
      cfg.ui.trigger.disabled = !!cfg.select.disabled;
      cfg.ui.trigger.setAttribute('aria-disabled', cfg.select.disabled ? 'true' : 'false');
      if (cfg.select.disabled) closeSearchDropdown(cfg);
      else applySearchTriggerVisualState(cfg);
      renderSearchDropdownList(cfg);
    }

    function ensureSearchDropdown(name) {
      var cfg = selectStores[name];
      var computed = null;
      var wrapper = null;
      var trigger = null;
      var label = null;
      var caret = null;
      var panel = null;
      var search = null;
      var list = null;
      var theme = null;
      if (!cfg || !cfg.select || cfg.ui) return;
      computed = window.getComputedStyle ? window.getComputedStyle(cfg.select, null) : cfg.select.currentStyle;
      theme = buildSearchShellTheme(selectShellTheme, computed);
      wrapper = document.createElement('div');
      wrapper.style.position = 'relative';
      wrapper.style.width = '100%';
      wrapper.style.zIndex = '1';
      trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.style.position = 'relative';
      trigger.style.display = 'block';
      trigger.style.width = '100%';
      trigger.style.height = computed.height || '44px';
      trigger.style.paddingTop = '0';
      trigger.style.paddingBottom = '0';
      trigger.style.paddingLeft = computed.paddingLeft || '12px';
      trigger.style.paddingRight = '38px';
      trigger.style.border = computed.border || '1px solid #ccc';
      trigger.style.borderRadius = computed.borderRadius || '4px';
      trigger.style.backgroundColor = theme.enabled.background;
      trigger.style.color = theme.enabled.color;
      trigger.style.fontFamily = computed.fontFamily || 'inherit';
      trigger.style.fontSize = computed.fontSize || '14px';
      trigger.style.fontWeight = computed.fontWeight || '400';
      trigger.style.lineHeight = computed.lineHeight === 'normal' ? '1.2' : computed.lineHeight;
      trigger.style.textAlign = 'left';
      trigger.style.cursor = 'pointer';
      trigger.style.overflow = 'hidden';
      trigger.style.textOverflow = 'ellipsis';
      trigger.style.whiteSpace = 'nowrap';
      trigger.style.boxShadow = 'none';
      label = document.createElement('span');
      label.style.display = 'block';
      label.style.overflow = 'hidden';
      label.style.textOverflow = 'ellipsis';
      label.style.whiteSpace = 'nowrap';
      trigger.appendChild(label);
      caret = document.createElement('span');
      caret.innerHTML = '&#9662;';
      caret.setAttribute('aria-hidden', 'true');
      caret.style.position = 'absolute';
      caret.style.top = '50%';
      caret.style.right = '14px';
      caret.style.marginTop = '-7px';
      caret.style.fontSize = '12px';
      caret.style.color = theme.enabled.color;
      caret.style.pointerEvents = 'none';
      trigger.appendChild(caret);
      panel = document.createElement('div');
      panel.style.display = 'none';
      panel.style.position = 'absolute';
      panel.style.left = '0';
      panel.style.right = '0';
      panel.style.top = 'calc(100% + 6px)';
      panel.style.zIndex = '60';
      panel.style.backgroundColor = theme.enabled.background;
      panel.style.border = computed.border || '1px solid #ccc';
      panel.style.borderRadius = computed.borderRadius || '4px';
      panel.style.boxShadow = '0 12px 24px rgba(0,0,0,0.14)';
      panel.style.overflow = 'hidden';
      search = document.createElement('input');
      search.type = 'search';
      search.placeholder = cfg.searchPlaceholder;
      search.setAttribute('autocomplete', 'off');
      search.setAttribute('autocapitalize', 'off');
      search.setAttribute('spellcheck', 'false');
      search.style.display = 'block';
      search.style.width = 'calc(100% - 20px)';
      search.style.height = '38px';
      search.style.margin = '10px';
      search.style.padding = '0 12px';
      search.style.border = computed.border || '1px solid #ccc';
      search.style.borderRadius = computed.borderRadius || '4px';
      search.style.backgroundColor = theme.enabled.background;
      search.style.color = theme.enabled.color;
      search.style.fontFamily = computed.fontFamily || 'inherit';
      search.style.fontSize = computed.fontSize || '14px';
      search.style.lineHeight = computed.lineHeight === 'normal' ? '1.2' : computed.lineHeight;
      list = document.createElement('div');
      list.setAttribute('role', 'listbox');
      list.style.maxHeight = '260px';
      list.style.overflowY = 'auto';
      list.style.paddingBottom = '6px';
      panel.appendChild(search);
      panel.appendChild(list);
      wrapper.appendChild(trigger);
      wrapper.appendChild(panel);
      cfg.select.parentNode.insertBefore(wrapper, cfg.select);
      if (cfg.select.hasAttribute('required')) {
        cfg.select.setAttribute('data-ps-required', '1');
        cfg.select.removeAttribute('required');
      }
      cfg.select.tabIndex = -1;
      cfg.select.style.display = 'none';
      cfg.select.style.opacity = '0';
      cfg.select.style.pointerEvents = 'none';
      ensureDropdownRootLayer(root);
      cfg.ui = {
        wrapper: wrapper,
        trigger: trigger,
        label: label,
        caret: caret,
        panel: panel,
        search: search,
        list: list,
        theme: theme,
        hosts: findDropdownHosts(cfg.select),
        baseBorderColor: theme.enabled.borderColor,
        baseBoxShadow: theme.enabled.boxShadow,
        baseColor: theme.enabled.color,
        baseFontFamily: computed.fontFamily || 'inherit',
        baseFontSize: computed.fontSize || '14px'
      };
      trigger.addEventListener('click', function() {
        if (cfg.select.disabled) return;
        if (cfg.ui.panel.style.display === 'block') closeSearchDropdown(cfg);
        else openSearchDropdown(cfg);
      });
      trigger.addEventListener('keydown', function(evt) {
        var key = evt && evt.key;
        var code = evt && (evt.which || evt.keyCode) || 0;
        if (key === 'Enter' || code === 13 || key === ' ' || code === 32 || key === 'ArrowDown' || code === 40) {
          evt.preventDefault();
          openSearchDropdown(cfg);
        }
      });
      search.addEventListener('input', function() {
        renderSearchDropdownList(cfg);
      });
      search.addEventListener('keydown', function(evt) {
        var key = evt && evt.key;
        var code = evt && (evt.which || evt.keyCode) || 0;
        if (key === 'Escape' || code === 27) {
          evt.preventDefault();
          closeSearchDropdown(cfg);
          try { cfg.ui.trigger.focus(); } catch(_e) {}
          return;
        }
        if ((key === 'Enter' || code === 13) && cfg.ui.list.firstChild && cfg.ui.list.firstChild.getAttribute && cfg.ui.list.firstChild.getAttribute('data-value')) {
          var firstValue = cfg.ui.list.firstChild.getAttribute('data-value') || '';
          var currentValue = cfg.select.value || '';
          evt.preventDefault();
          cfg.select.value = firstValue;
          closeSearchDropdown(cfg);
          if (cfg.select.value !== currentValue) dispatchSelectChange(cfg.select);
          else syncSearchDropdown(cfg);
        }
      });
      list.addEventListener('click', function(evt) {
        var target = evt.target || evt.srcElement;
        var nextValue = '';
        var prevValue = cfg.select.value || '';
        while (target && target !== list && (!target.getAttribute || !target.getAttribute('data-value'))) {
          target = target.parentNode;
        }
        if (!target || target === list || !target.getAttribute) return;
        nextValue = target.getAttribute('data-value') || '';
        cfg.select.value = nextValue;
        closeSearchDropdown(cfg);
        if (cfg.select.value !== prevValue) dispatchSelectChange(cfg.select);
        else syncSearchDropdown(cfg);
      });
      cfg.select.addEventListener('change', function() {
        syncSearchDropdown(cfg);
      });
      document.addEventListener('click', function(evt) {
        var target = evt.target || evt.srcElement;
        if (!cfg.ui || !cfg.ui.wrapper) return;
        if (cfg.ui.wrapper.contains(target)) return;
        closeSearchDropdown(cfg);
      });
      syncSearchDropdown(cfg);
    }

    function setSearchInputDisabled(disabled) {
      if (!searchQ || !searchInputGated) return;
      searchQ.disabled = !!disabled;
      if (disabled) {
        searchQ.setAttribute('aria-disabled', 'true');
        closeTypeahead();
      } else {
        searchQ.removeAttribute('aria-disabled');
      }
    }

    function setSelectLoading(sel, on) {
      if (!sel) return;
      if (on) {
        sel.setAttribute('data-ps-fetching', 'true');
        sel.setAttribute('aria-busy', 'true');
      } else {
        sel.removeAttribute('data-ps-fetching');
        sel.removeAttribute('aria-busy');
      }
    }

    function clearActiveStep() {
      var makeCfg = getSelectStoreByElement(makeSel);
      var yearCfg = getSelectStoreByElement(yearSel);
      var modelCfg = getSelectStoreByElement(modelSel);
      if (makeSel) makeSel.removeAttribute('data-ps-step-active');
      if (yearSel) yearSel.removeAttribute('data-ps-step-active');
      if (modelSel) modelSel.removeAttribute('data-ps-step-active');
      if (makeCfg && makeCfg.ui) {
        makeCfg.ui.trigger.removeAttribute('data-ps-step-active');
        applySearchTriggerVisualState(makeCfg);
      }
      if (yearCfg && yearCfg.ui) {
        yearCfg.ui.trigger.removeAttribute('data-ps-step-active');
        applySearchTriggerVisualState(yearCfg);
      }
      if (modelCfg && modelCfg.ui) {
        modelCfg.ui.trigger.removeAttribute('data-ps-step-active');
        applySearchTriggerVisualState(modelCfg);
      }
    }

    function activateStep(sel, shouldFocus) {
      var cfg = getSelectStoreByElement(sel);
      clearActiveStep();
      if (!sel || sel.disabled || sel.getAttribute('data-ps-fetching') === 'true') return;
      sel.setAttribute('data-ps-step-active', 'true');
      if (cfg && cfg.ui) {
        cfg.ui.trigger.setAttribute('data-ps-step-active', 'true');
        applySearchTriggerVisualState(cfg);
      }
      if (shouldFocus) {
        try {
          if (cfg && cfg.ui) cfg.ui.trigger.focus();
          else sel.focus();
        } catch(_e) {}
      }
    }

    function refreshStepGuidance(shouldFocus) {
      var target = null;
      var makeVal = makeSel ? String(makeSel.value || '').trim() : '';
      var yearVal = yearSel ? String(yearSel.value || '').trim() : '';
      var modelVal = modelSel ? String(modelSel.value || '').trim() : '';
      if (makeSel && !makeSel.disabled && !makeVal) target = makeSel;
      else if (yearSel && !yearSel.disabled && !yearVal) target = yearSel;
      else if (modelSel && !modelSel.disabled && !modelVal) target = modelSel;
      activateStep(target, shouldFocus);
    }

    function resetYears() {
      if (!yearSel) return;
      yearSel.innerHTML = '<option value="" disabled selected>Select Year</option>';
      setSelectLoading(yearSel, false);
      setSelectDisabled(yearSel, true);
      resetSelectStore('year');
      syncSearchDropdown(selectStores.year);
    }

    function resetModels() {
      if (!modelSel) return;
      modelSel.innerHTML = '<option value="" disabled selected>Select Model</option>';
      setSelectLoading(modelSel, false);
      setSelectDisabled(modelSel, true);
      resetSelectStore('model');
      syncSearchDropdown(selectStores.model);
    }

    // ---- Load Makes ----
    function loadMakes(onLoaded, preferActiveCookie, activeSelectionSource, restoreToken) {
      var loadComplete = typeof onLoaded === 'function' ? onLoaded : null;
      var makesRequestId = 0;
      if (restoreToken && !isEditRestoreTokenActive(restoreToken)) return;
      makesLoadRequestSeq += 1;
      makesRequestId = makesLoadRequestSeq;
      yearsLoadRequestSeq += 1;
      modelsLoadRequestSeq += 1;
      resetSubmittedSearchRestore();
      clearActiveStep();
      makeSel.innerHTML = '<option value="">Loading...</option>';
      setSelectLoading(makeSel, true);
      setSelectDisabled(makeSel, true);
      resetSelectStore('make');
      syncSearchDropdown(selectStores.make);
      setSearchInputDisabled(true);
      resetYears();
      resetModels();

      cachedGet(apiBase() + 'shopify/makes', function(data) {
        if (makesRequestId !== makesLoadRequestSeq) return;
        if (restoreToken && !isEditRestoreTokenActive(restoreToken)) return;
        var names = [];
        var makeCfg = selectStores.make;
        var selectedMake = '';
        var shouldFocusHydratedStep = false;
        if (data) {
          var arr = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : []);
          for (var i = 0; i < arr.length; i++) {
            var v = arr[i];
            if (typeof v === 'string') names.push(buildOptionItem(v, v));
            else if (v && (v.name || v.label)) names.push(buildOptionItem(v.name || v.label, v.name || v.label));
          }
        }
        names.sort(function(a, b) {
          var aText = String((a && a.text) || '').toLowerCase();
          var bText = String((b && b.text) || '').toLowerCase();
          if (aText < bText) return -1;
          if (aText > bText) return 1;
          return 0;
        });
        makeCfg.options = names;

        // Preselect from cookie
        if (hydrateFromCookie) {
          try {
            var cc = getHydrationSelectionSource(preferActiveCookie, activeSelectionSource);
            selectedMake = resolveOptionValue(names, cc.make, false);
            if (!selectedMake && window.__psSearchVehicleHandoffApplied) selectedMake = resolveHandoffMake(names, cc.make);
            if (selectedMake) state.make = selectedMake;
            if (window.__psSearchVehicleHandoffApplied) {
              if (selectedMake && selectedMake !== cc.make) writeCookie({
                make: selectedMake, make_slug: toSlug(selectedMake), year: cc.year || '', model: cc.model || ''
              });
              else if (!selectedMake && cc.make) writeUrlMakeContextCookie({
                make: '', make_slug: '', year: '', model: '', color: '', code: '', hex: '', factory_code: '', color_slug: '', image: '',
                valid: false, shopify_page: '', products: [], q: '', vehicle_scope: false, ps_from_search: false
              });
            }
          } catch(e) {}
        }
        shouldFocusHydratedStep = !!selectedMake;
        renderSelectOptions(makeCfg, selectedMake);
        setSelectLoading(makeSel, false);
        setSelectDisabled(makeSel, names.length === 0);
        syncSearchDropdown(makeCfg);

        if (hydrateFromCookie) {
          loadYears(makeSel.value || '', {
            noPreselect: false,
            focusNext: shouldFocusHydratedStep,
            preferActiveCookie: preferActiveCookie,
            activeSelectionSource: activeSelectionSource,
            restoreToken: restoreToken,
            onHydrated: function() {
              maybeRestoreSubmittedSearch(12);
              if (loadComplete) {
                try { loadComplete(); } catch(_e) {}
              }
            }
          });
        } else {
          setSearchInputDisabled(!makeSel.value);
          refreshStepGuidance(true);
          if (loadComplete) {
            try { loadComplete(); } catch(_e2) {}
          }
        }
      });
    }

    // ---- Load Years ----
    // opts.noPreselect: skip cookie-based year preselect
    function loadYears(makeVal, opts) {
      var onLoaded = opts && typeof opts.onLoaded === 'function' ? opts.onLoaded : null;
      var onHydrated = opts && typeof opts.onHydrated === 'function' ? opts.onHydrated : null;
      var restoreToken = opts && opts.restoreToken ? opts.restoreToken : null;
      var yearsRequestId = 0;
      function finish(result) {
        if (!onLoaded) return;
        try { onLoaded(result || {}); } catch(_e) {}
      }
      function finishHydration(result) {
        if (!onHydrated) return;
        try { onHydrated(result || {}); } catch(_e) {}
      }
      if (!yearSel) {
        var missingYearResult = { status: 'missing-year-select', years: [], selectedYear: '', yearFound: false, make: '' };
        finish(missingYearResult);
        finishHydration(missingYearResult);
        return;
      }
      var m = String(makeVal || '').trim();
      setSearchInputDisabled(!m);
      var skipPre = !!(opts && opts.noPreselect);
      var focusNext = !(opts && opts.focusNext === false);
      if (!m) {
        resetYears();
        resetModels();
        refreshStepGuidance(focusNext);
        var noMakeResult = { status: 'no-make', years: [], selectedYear: '', yearFound: false, make: '' };
        finish(noMakeResult);
        finishHydration(noMakeResult);
        return;
      }
      clearActiveStep();
      setSelectLoading(yearSel, true);
      setSelectDisabled(yearSel, true);
      yearSel.innerHTML = '<option value="">Loading years...</option>';
      syncSearchDropdown(selectStores.year);
      resetModels();
      yearsLoadRequestSeq += 1;
      yearsRequestId = yearsLoadRequestSeq;

      var slug = m.toLowerCase().replace(/\s+/g, '-');
      cachedGet(apiBase() + 'shopify/years/' + encodeURIComponent(slug), function(data, statusCode) {
        var years = [];
        var yearValues = [];
        var cc = null;
        var hasCookieColorContext = false;
        var shouldLoadModels = false;
        var shouldHydrateFallbackSearch = false;
        var yearCfg = selectStores.year;
        var selectedYear = '';
        var status = statusCode >= 200 && statusCode < 300 ? 'ok' : 'error';
        if (yearsRequestId !== yearsLoadRequestSeq) {
          var staleYearsResult = { status: 'stale', years: [], selectedYear: '', yearFound: false, make: m };
          finish(staleYearsResult);
          finishHydration(staleYearsResult);
          return;
        }
        if (restoreToken && !isEditRestoreTokenActive(restoreToken)) {
          var staleRestoreResult = { status: 'stale', years: [], selectedYear: '', yearFound: false, make: m };
          finish(staleRestoreResult);
          finishHydration(staleRestoreResult);
          return;
        }
        if (data) {
          var arr = Array.isArray(data) ? data : (data && Array.isArray(data.years) ? data.years : []);
          var map = {};
          for (var i = 0; i < arr.length; i++) {
            var n = parseInt(arr[i], 10);
            if (!isNaN(n) && !map[n]) {
              map[n] = 1;
              years.push(buildOptionItem(n, n));
            }
          }
        }
        years.sort(function(a, b) { return parseInt(b.value, 10) - parseInt(a.value, 10); });
        for (var j = 0; j < years.length; j++) {
          yearValues.push(String(years[j].value || ''));
        }

        // Preselect from cookie and chain to model load
        if (!skipPre && hydrateFromCookie) {
          try {
            cc = getHydrationSelectionSource(
              !!(opts && opts.preferActiveCookie),
              opts && opts.activeSelectionSource
            ) || {};
            hasCookieColorContext = !!(cc && (cc.color || cc.code || cc.color_slug));
            selectedYear = resolveOptionValue(years, cc.year, false);
            if (selectedYear) {
              state.year = selectedYear;
            }
            if (cc.make && selectedYear) {
              if (makeSel && cc.make) makeSel.value = cc.make;
              state.make = cc.make;
              shouldLoadModels = true;
            } else if (cc.make && !hasCookieColorContext && !getSubmittedSearchRestoreScope()) {
              shouldHydrateFallbackSearch = true;
            }
          } catch(_e) {}
        }
        yearCfg.options = years;
        renderSelectOptions(yearCfg, selectedYear);
        setSelectLoading(yearSel, false);
        setSelectDisabled(yearSel, years.length === 0);
        syncSearchDropdown(yearCfg);
        if (shouldLoadModels) {
          var hydratedYearsResult = {
            status: status,
            years: yearValues,
            selectedYear: String(selectedYear || ''),
            yearFound: !!selectedYear,
            make: String(m || '')
          };
          finish(hydratedYearsResult);
          loadModels(cc.make, cc.year, {
            fromCookie: true,
            autoSearch: true,
            focusNext: focusNext,
            preferActiveCookie: !!(opts && opts.preferActiveCookie),
            activeSelectionSource: opts && opts.activeSelectionSource,
            restoreToken: restoreToken,
            onLoaded: function(modelResult) {
              finishHydration(modelResult || hydratedYearsResult);
            }
          });
          return;
        }
        if (shouldHydrateFallbackSearch && !hasHistorySearchSnapshot()) {
          runHydratedScopeSearch({
            make: String((cc && cc.make) || ''),
            year: '',
            model: '',
            q: cc && cc.q != null ? String(cc.q) : ''
          }, false);
        }
        refreshStepGuidance(focusNext);
        var finalYearsResult = {
          status: status,
          years: yearValues,
          selectedYear: String(selectedYear || ''),
          yearFound: !!selectedYear,
          make: String(m || '')
        };
        finish(finalYearsResult);
        finishHydration(finalYearsResult);
      });
    }

    // ---- Load Models ----
    // opts.autoSearch: auto-trigger color search if cookie has full context
    function loadModels(makeVal, yearVal, opts) {
      var onLoaded = opts && typeof opts.onLoaded === 'function' ? opts.onLoaded : null;
      var restoreToken = opts && opts.restoreToken ? opts.restoreToken : null;
      var forcedModel = opts && opts.forceModel != null ? String(opts.forceModel).replace(/^\s+|\s+$/g, '') : '';
      var hasForcedModel = !!forcedModel;
      var preventAutoSelect = !!(opts && opts.preventAutoSelect);
      var suppressAutoSearch = !!(opts && opts.suppressAutoSearch);
      var modelsRequestId = 0;
      function finish(result) {
        if (!onLoaded) return;
        try { onLoaded(result || {}); } catch(_e) {}
      }
      if (!modelSel) {
        finish({ status: 'missing-model-select', models: [], selectedModel: '', modelFound: false, make: '', year: '' });
        return;
      }
      var m = String(makeVal || '').trim();
      var y = String(yearVal || '').trim();
      var focusNext = !(opts && opts.focusNext === false);
      if (!m || !y) {
        resetModels();
        refreshStepGuidance(focusNext);
        finish({ status: 'missing-scope', models: [], selectedModel: '', modelFound: false, make: m, year: y });
        return;
      }
      clearActiveStep();
      setSelectLoading(modelSel, true);
      setSelectDisabled(modelSel, true);
      modelSel.innerHTML = '<option value="" disabled selected>Select Model</option>';
      syncSearchDropdown(selectStores.model);
      var autoSearch = !!(opts && opts.autoSearch);
      modelsLoadRequestSeq += 1;
      modelsRequestId = modelsLoadRequestSeq;

      cachedGet(apiBase() + 'shopify/models/' + encodeURIComponent(m) + '/' + encodeURIComponent(y), function(data, statusCode) {
        var models = [];
        var modelValues = [];
        var modelCfg = selectStores.model;
        var selectedModel = '';
        var loadStatus = statusCode >= 200 && statusCode < 300 ? 'ok' : 'error';
        if (modelsRequestId !== modelsLoadRequestSeq) {
          finish({ status: 'stale', models: [], selectedModel: '', modelFound: false, make: m, year: y });
          return;
        }
        if (restoreToken && !isEditRestoreTokenActive(restoreToken)) {
          finish({ status: 'stale', models: [], selectedModel: '', modelFound: false, make: m, year: y });
          return;
        }
        if (data) {
          models = Array.isArray(data) ? data : (data && Array.isArray(data.models) ? data.models : []);
        }
        for (var i = 0; i < models.length; i++) {
          models[i] = normalizeOptionItem(models[i]);
        }
        models = models.filter(function(item) {
          return !!item;
        });
        models.sort(function(a, b) {
          var aText = String((a && a.text) || '').toLowerCase();
          var bText = String((b && b.text) || '').toLowerCase();
          if (aText < bText) return -1;
          if (aText > bText) return 1;
          return 0;
        });
        if (models.length > 1) {
          for (i = 1; i < models.length; i++) {
            if (models[i].value === models[i - 1].value && models[i].text === models[i - 1].text) {
              models.splice(i, 1);
              i--;
            }
          }
        }
        for (i = 0; i < models.length; i++) {
          modelValues.push(String(models[i].value || ''));
        }
        modelCfg.options = models;
        renderSelectOptions(modelCfg, '');
        setSelectLoading(modelSel, false);
        setSelectDisabled(modelSel, models.length === 0);
        syncSearchDropdown(modelCfg);

        // Auto-select single model
        if (models.length === 1) {
          if (hasForcedModel) {
            selectedModel = resolveOptionValue(models, forcedModel, true);
            if (selectedModel) {
              renderSelectOptions(modelCfg, selectedModel);
              syncSearchDropdown(modelCfg);
              state.model = selectedModel;
              clearActiveStep();
              refreshStepGuidance(focusNext);
              finish({
                status: loadStatus,
                models: modelValues,
                selectedModel: String(selectedModel || ''),
                modelFound: !!selectedModel,
                make: String(m || ''),
                year: String(y || '')
              });
              return;
            }
            if (modelSel) modelSel.value = '';
            state.model = '';
            clearActiveStep();
            refreshStepGuidance(focusNext);
            finish({
              status: loadStatus,
              models: modelValues,
              selectedModel: '',
              modelFound: false,
              make: String(m || ''),
              year: String(y || '')
            });
            return;
          }
          if (preventAutoSelect) {
            if (modelSel) modelSel.value = '';
            state.model = '';
            clearActiveStep();
            refreshStepGuidance(focusNext);
            finish({
              status: loadStatus,
              models: modelValues,
              selectedModel: '',
              modelFound: false,
              make: String(m || ''),
              year: String(y || '')
            });
            return;
          }
          renderSelectOptions(modelCfg, models[0].value);
          syncSearchDropdown(modelCfg);
          state.model = models[0].value;
          clearActiveStep();
          var historySnapshot = getHistorySearchSnapshot();
          var cCk = readCookie() || {};
          if (historySnapshot) {
            state.q = String(historySnapshot.q || '').replace(/^\s+|\s+$/g, '');
            if (searchQ) searchQ.value = state.q;
            refreshStepGuidance(focusNext);
            finish({
              status: loadStatus,
              models: modelValues,
              selectedModel: String(state.model || ''),
              modelFound: !!state.model,
              make: String(m || ''),
              year: String(y || '')
            });
            return;
          }
          var hasColor = cCk.make && (cCk.color || cCk.code || cCk.color_slug);
          if (!suppressAutoSearch && !hasColor) {
            try { writeSearchContextCookie({ ps_from_search: false }); } catch(_e) {}
            doSearch(false, false);
          } else if (!suppressAutoSearch && searchOnSelectedHydrate) {
            // PDP mode: always show the color grid even if cookie has color context,
            // so the user can pick a color. Skip cookie write to preserve existing data.
            doSearch(false, false, null, true);
          }
          finish({
            status: loadStatus,
            models: modelValues,
            selectedModel: String(state.model || ''),
            modelFound: !!state.model,
            make: String(m || ''),
            year: String(y || '')
          });
          return;
        }

        // Preselect model from cookie.
        // Match against the raw models JS array (not the DOM options)
        // to avoid any browser quirks with reading option values right
        // after innerHTML.  Compute selectedIndex arithmetically.
        var cc = null;
        var historySnapshot = getHistorySearchSnapshot();
        try {
          cc = getHydrationSelectionSource(
            !!(opts && opts.preferActiveCookie),
            opts && opts.activeSelectionSource
          ) || {};
        } catch(_e) {}
        if (hasForcedModel) {
          selectedModel = resolveOptionValue(models, forcedModel, true);
          if (selectedModel) {
            renderSelectOptions(modelCfg, selectedModel);
            syncSearchDropdown(modelCfg);
            state.model = selectedModel;
          } else {
            if (modelSel) modelSel.value = '';
            state.model = '';
            refreshStepGuidance(focusNext);
            finish({
              status: loadStatus,
              models: modelValues,
              selectedModel: '',
              modelFound: false,
              make: String(m || ''),
              year: String(y || '')
            });
            return;
          }
        } else if (cc && cc.model) {
          var cookieModel = String(cc.model).trim();
          selectedModel = resolveOptionValue(models, cookieModel, true);
          if (selectedModel) {
            renderSelectOptions(modelCfg, selectedModel);
            syncSearchDropdown(modelCfg);
            state.model = selectedModel;
          }
        }

        // Auto-search if cookie has full vehicle context.
        // navigate mode (PDP): always show grid regardless of color.
        // search mode (homepage): only auto-search when no color saved.
        if (historySnapshot) {
          state.q = String(historySnapshot.q || '').replace(/^\s+|\s+$/g, '');
          if (searchQ) searchQ.value = state.q;
          refreshStepGuidance(focusNext);
          finish({
            status: loadStatus,
            models: modelValues,
            selectedModel: String(state.model || ''),
            modelFound: !!state.model,
            make: String(m || ''),
            year: String(y || '')
          });
          return;
        }
        if (autoSearch && cc && cc.make && cc.year) {
          state.make = cc.make;
          state.year = String(cc.year);
          if (makeSel) makeSel.value = state.make;
          if (yearSel) yearSel.value = state.year;
          if (searchQ && cc.q != null) searchQ.value = String(cc.q);
          var noCookieColor = !cc.code && !cc.color;
          var shouldFallbackSearch = !state.model && !getSubmittedSearchRestoreScope();
          if (noCookieColor || searchOnSelectedHydrate) {
            if (state.model || shouldFallbackSearch) {
              runHydratedScopeSearch({
                make: state.make || cc.make,
                year: state.year || String(cc.year),
                model: state.model || '',
                q: cc.q != null ? String(cc.q) : String((searchQ && searchQ.value) || '')
              }, !noCookieColor);
            }
          }
        }
        refreshStepGuidance(focusNext);
        finish({
          status: loadStatus,
          models: modelValues,
          selectedModel: String(state.model || ''),
          modelFound: !!state.model,
          make: String(m || ''),
          year: String(y || '')
        });
      });
    }

    // ==========================================================
    // SEARCH / FETCH COLORS
    // ==========================================================

    function getTypeaheadScope() {
      return {
        make: String((makeSel && makeSel.value) || '').replace(/^\s+|\s+$/g, ''),
        year: String((yearSel && yearSel.value) || '').replace(/^\s+|\s+$/g, ''),
        model: String((modelSel && modelSel.value) || '').replace(/^\s+|\s+$/g, '')
      };
    }

    function getTypeaheadCacheKey(term, scope) {
      return [
        String(scope.make || '').toLowerCase(),
        String(scope.year || '').toLowerCase(),
        String(scope.model || '').toLowerCase(),
        String(term || '').toLowerCase()
      ].join('|');
    }

    function buildWorkerResultsUrl(term, scope, page, perPageValue) {
      var params = [];
      function pushParam(key, value) {
        if (!value && value !== 0) return;
        value = String(value).replace(/^\s+|\s+$/g, '');
        if (!value) return;
        params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      }
      pushParam('q', term);
      pushParam('page', page || 1);
      if (perPageValue) pushParam('per_page', perPageValue);
      pushParam('predictive', 'color_scoped');
      pushParam('make', scope.make);
      pushParam('year', scope.year);
      pushParam('model', scope.model);
      return '/search/results?' + params.join('&');
    }

    function buildPredictiveWorkerResultsUrl(term, scope, perPageValue) {
      var params = [];
      function pushParam(key, value) {
        if (!value && value !== 0) return;
        value = String(value).replace(/^\s+|\s+$/g, '');
        if (!value) return;
        params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      }
      pushParam('q', term);
      if (perPageValue) pushParam('per_page', perPageValue);
      pushParam('predictive', 'color_scoped');
      pushParam('make', scope.make);
      pushParam('year', scope.year);
      pushParam('model', scope.model);
      return '/search/results?' + params.join('&');
    }

    function buildScopedSearchPageUrl(term, scope) {
      var params = [];
      function pushParam(key, value) {
        if (!value && value !== 0) return;
        value = String(value).replace(/^\s+|\s+$/g, '');
        if (!value) return;
        params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      }
      pushParam('q', term);
      pushParam('make', scope.make);
      pushParam('year', scope.year);
      pushParam('model', scope.model);
      return '/search' + (params.length ? '?' + params.join('&') : '');
    }

    function getCommittedSearchBackend() {
      return getCommittedSearchTerm(state.q) ? 'typesense' : 'hub';
    }

    function getCommittedSearchTerm(value) {
      value = String(value || '').replace(/^\s+|\s+$/g, '');
      return value.length >= keywordSearchMinChars ? value : '';
    }

    function hasActiveKeywordSearch() {
      var qVal = String((searchQ && searchQ.value) || '').replace(/^\s+|\s+$/g, '');
      return state.backend === 'typesense' && !!getCommittedSearchTerm(qVal);
    }

    function refreshKeywordResultsForVehicleChange() {
      if (!hasActiveKeywordSearch()) return false;
      if (!makeSel || !makeSel.value) return false;
      showError('');
      showEmpty(false);
      showBtnSpinner = false;
      doSearch(false, true);
      return true;
    }

    function restoreVehicleResultsForClearedKeywords() {
      var hasKeywordResults = false;
      var qVal = String((searchQ && searchQ.value) || '').replace(/^\s+|\s+$/g, '');
      if (qVal.length >= keywordSearchMinChars) return false;
      hasKeywordResults = state.backend === 'typesense' || hasHistorySearchSnapshot();
      if (!hasKeywordResults) return false;
      state.make = String((makeSel && makeSel.value) || '').replace(/^\s+|\s+$/g, '');
      state.year = String((yearSel && yearSel.value) || '').replace(/^\s+|\s+$/g, '');
      state.model = String((modelSel && modelSel.value) || '').replace(/^\s+|\s+$/g, '');
      if (!state.make) return false;
      clearTypeaheadSuggestions();
      showError('');
      showEmpty(false);
      showBtnSpinner = false;
      doSearch(false, true);
      return true;
    }

    function getSearchScopeFromState() {
      return {
        make: String(state.make || '').replace(/^\s+|\s+$/g, ''),
        year: String(state.year || '').replace(/^\s+|\s+$/g, ''),
        model: String(state.model || '').replace(/^\s+|\s+$/g, '')
      };
    }

    function parseWorkerColorTitle(title, makeName) {
      var rawTitle = String(title || '').replace(/\s+Touch Up Paint\s*$/i, '').replace(/^\s+|\s+$/g, '');
      var colorText = rawTitle;
      var codeText = '';
      var codeMatch = null;

      if (makeName) {
        colorText = colorText.replace(new RegExp('^' + escRegExp(makeName) + '\\s+', 'i'), '');
      }

      codeMatch = colorText.match(/\(([^)]+)\)\s*$/);
      if (codeMatch) {
        codeText = String(codeMatch[1] || '').replace(/^\s+|\s+$/g, '');
        colorText = String(colorText.substring(0, codeMatch.index) || '').replace(/^\s+|\s+$/g, '');
      }

      if (!colorText) colorText = rawTitle || String(title || '');

      return {
        color: colorText,
        code: codeText
      };
    }

    function normalizeWorkerResultItem(result, scope) {
      var href = normalizeColorHubUrl(result && result.url ? result.url : '');
      var match = null;
      var meta = null;
      var makeName = trimValue(result && (result.make_name || result.make) ? (result.make_name || result.make) : ((scope && scope.make) || ''));
      var structuredColorName = trimValue(result && (result.color_name || result.color) ? (result.color_name || result.color) : '');
      var structuredColorCode = trimValue(result && (result.color_code || result.code) ? (result.color_code || result.code) : '');
      var parsed = parseWorkerColorTitle(result && result.title ? result.title : '', makeName);
      var colorName = structuredColorName || parsed.color || href;
      var colorCode = structuredColorCode || parsed.code || '';

      if (!href) return null;

      match = href.match(/\/colors\/([^\/]+)\/([^\/]+)/i);
      meta = extractMeta(result && result.products ? result.products : null);

      return {
        make_name: makeName,
        make_slug: match && match[1] ? String(match[1]).toLowerCase() : toSlug(makeName),
        color_name: colorName,
        color_slug: match && match[2] ? String(match[2]).toLowerCase() : buildColorSlug(colorName, colorCode),
        color_code: colorCode,
        color_rgb: normalizeColorRgb(result && (result.color_rgb || result.hex) ? (result.color_rgb || result.hex) : '') || '#CCCCCC',
        is_tricoat: /tricoat/i.test(String(result && result.title ? result.title : '')) || /tricoat/i.test(String(colorName || '')),
        shopify_page: href,
        products: result && result.products ? result.products : null,
        pen_url: meta.pen.url, bottle_url: meta.bottle.url,
        spray_url: meta.spray.url, pro_url: meta.pro.url,
        pen_img: meta.pen.img, bottle_img: meta.bottle.img,
        spray_img: meta.spray.img, pro_img: meta.pro.img,
        pen_name: meta.pen.name, bottle_name: meta.bottle.name,
        spray_name: meta.spray.name, pro_name: meta.pro.name,
        pen_price: meta.pen.price, bottle_price: meta.bottle.price,
        spray_price: meta.spray.price, pro_price: meta.pro.price
      };
    }

    function getHistoryStateObject() {
      var historyState = null;
      if (!window.history) return null;
      historyState = window.history.state;
      if (!historyState || typeof historyState !== 'object') return {};
      return historyState;
    }

    function replaceHistoryState(nextState) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try { window.history.replaceState(nextState, '', window.location.href); } catch(_e) {}
    }

    function copySnapshotItem(item) {
      return {
        make_name: item && item.make_name ? item.make_name : '',
        make_slug: item && item.make_slug ? item.make_slug : '',
        color_name: item && item.color_name ? item.color_name : '',
        color_slug: item && item.color_slug ? item.color_slug : '',
        color_code: item && item.color_code ? item.color_code : '',
        color_rgb: item && item.color_rgb ? item.color_rgb : '',
        is_tricoat: !!(item && item.is_tricoat),
        shopify_page: item && item.shopify_page ? item.shopify_page : '',
        products: item && item.products ? item.products : null,
        pen_url: item && item.pen_url ? item.pen_url : '',
        bottle_url: item && item.bottle_url ? item.bottle_url : '',
        spray_url: item && item.spray_url ? item.spray_url : '',
        pro_url: item && item.pro_url ? item.pro_url : '',
        pen_img: item && item.pen_img ? item.pen_img : '',
        bottle_img: item && item.bottle_img ? item.bottle_img : '',
        spray_img: item && item.spray_img ? item.spray_img : '',
        pro_img: item && item.pro_img ? item.pro_img : '',
        pen_name: item && item.pen_name ? item.pen_name : '',
        bottle_name: item && item.bottle_name ? item.bottle_name : '',
        spray_name: item && item.spray_name ? item.spray_name : '',
        pro_name: item && item.pro_name ? item.pro_name : '',
        pen_price: item && item.pen_price ? item.pen_price : '',
        bottle_price: item && item.bottle_price ? item.bottle_price : '',
        spray_price: item && item.spray_price ? item.spray_price : '',
        pro_price: item && item.pro_price ? item.pro_price : ''
      };
    }

    function copySnapshotItems(items) {
      var out = [];
      var i = 0;
      for (i = 0; i < items.length; i++) {
        out.push(copySnapshotItem(items[i]));
      }
      return out;
    }

    function copyTwoToneGroups(groups) {
      try {
        return JSON.parse(JSON.stringify(groups || []));
      } catch(_e) {}
      return [];
    }

    function getHistorySearchSnapshot() {
      var historyState = getHistoryStateObject();
      var snapshots = null;
      var snapshot = null;

      if (!historyState) return null;

      snapshots = historyState.psColorSearchSnapshots;
      if (!snapshots || typeof snapshots !== 'object') return null;

      snapshot = snapshots[historySnapshotKey];
      if (!snapshot || typeof snapshot !== 'object') return null;
      if (String(snapshot.backend || '') !== 'typesense') return null;
      if (!String(snapshot.q || '').replace(/^\s+|\s+$/g, '')) return null;

      return snapshot;
    }

    function hasHistorySearchSnapshot() {
      return !!getHistorySearchSnapshot();
    }

    function writeHistorySearchSnapshot(items, isEmpty, twoToneGroups) {
      var historyState = getHistoryStateObject();
      var snapshots = null;

      if (!historyState) return;
      if (state.backend !== 'typesense' || !state.q || isSensitiveSearchValue(state.q)) {
        clearHistorySearchSnapshot();
        return;
      }

      snapshots = historyState.psColorSearchSnapshots;
      if (!snapshots || typeof snapshots !== 'object') snapshots = {};

      snapshots[historySnapshotKey] = {
        backend: 'typesense',
        make: state.make || '',
        year: state.year || '',
        model: state.model || '',
        q: state.q || '',
        page: state.page || 1,
        pages: state.pages || 1,
        empty: !!isEmpty,
        items: copySnapshotItems(items || []),
        twoToneGroups: copyTwoToneGroups(twoToneGroups || [])
      };

      historyState.psColorSearchSnapshots = snapshots;
      replaceHistoryState(historyState);
    }

    function clearHistorySearchSnapshot() {
      var historyState = getHistoryStateObject();
      var snapshots = null;

      if (!historyState) return;

      snapshots = historyState.psColorSearchSnapshots;
      if (!snapshots || typeof snapshots !== 'object' || !snapshots.hasOwnProperty(historySnapshotKey)) return;

      delete snapshots[historySnapshotKey];
      historyState.psColorSearchSnapshots = snapshots;
      replaceHistoryState(historyState);
    }

    function getHydrationSelectionSource(preferActiveCookie, activeSelectionSource) {
      var cookieSource = readCookie() || {};
      if (activeSelectionSource && activeSelectionSource.make) return activeSelectionSource;
      if (preferActiveCookie && hasActiveColorSelection(cookieSource)) return cookieSource;
      return getHistorySearchSnapshot() || cookieSource;
    }

    function bumpEditRestoreToken() {
      editRestoreToken += 1;
      return editRestoreToken;
    }

    function isEditRestoreTokenActive(token) {
      if (!token) return true;
      return editRestoreToken === token;
    }

    function normalizeEditRestoreSource(raw) {
      var out = { make: '', year: '', model: '', q: '', yearMalformed: false };
      out.make = String(raw && raw.make != null ? raw.make : '').replace(/^\s+|\s+$/g, '');
      out.year = String(raw && raw.year != null ? raw.year : '').replace(/^\s+|\s+$/g, '');
      out.model = String(raw && raw.model != null ? raw.model : '').replace(/^\s+|\s+$/g, '');
      out.q = String(raw && raw.q != null ? raw.q : '').replace(/^\s+|\s+$/g, '');
      out.q = isSensitiveSearchValue(out.q) ? '' : out.q;
      out.yearMalformed = !!(raw && raw.yearMalformed === true);
      if (!out.make) {
        out.year = '';
        out.model = '';
        return out;
      }
      if (out.year && !/^\d+$/.test(out.year)) {
        out.year = '';
        out.model = '';
        out.yearMalformed = true;
      }
      if (!out.year) out.model = '';
      return out;
    }

    function mergeEditRestoreSource(raw, submitted) {
      var merged = raw && typeof raw === 'object' ? raw : {};
      var submittedScope = submitted && typeof submitted === 'object' ? submitted : null;
      var mergedMake = '';
      var submittedMake = '';
      if (!submittedScope) return merged;
      mergedMake = String(merged.make || '').replace(/^\s+|\s+$/g, '');
      submittedMake = String(submittedScope.make || '').replace(/^\s+|\s+$/g, '');
      if (!submittedMake) return merged;
      if (mergedMake && mergedMake !== submittedMake) return merged;
      if (!mergedMake) merged.make = submittedMake;
      if (!String(merged.year || '').replace(/^\s+|\s+$/g, '') && submittedScope.year != null) {
        merged.year = String(submittedScope.year || '').replace(/^\s+|\s+$/g, '');
      }
      if (!String(merged.model || '').replace(/^\s+|\s+$/g, '') && submittedScope.model != null) {
        merged.model = String(submittedScope.model || '').replace(/^\s+|\s+$/g, '');
      }
      if (!String(merged.q || '').replace(/^\s+|\s+$/g, '') && submittedScope.q != null) {
        merged.q = String(submittedScope.q || '').replace(/^\s+|\s+$/g, '');
      }
      return merged;
    }

    function readEditRestoreScope() {
      var raw = '';
      var parsed = null;
      var normalized = null;
      var ts = 0;
      try {
        if (!window.sessionStorage) return null;
        raw = window.sessionStorage.getItem(editRestoreStateKey);
      } catch(_e) {
        return null;
      }
      if (!raw) return null;
      try {
        parsed = JSON.parse(raw);
      } catch(_e2) {
        try { window.sessionStorage.removeItem(editRestoreStateKey); } catch(_e3) {}
        return null;
      }
      normalized = normalizeEditRestoreSource(parsed || {});
      if (!normalized || !normalized.make) {
        try { window.sessionStorage.removeItem(editRestoreStateKey); } catch(_e4) {}
        return null;
      }
      ts = parsed && parsed.ts ? parseInt(parsed.ts, 10) : 0;
      if (!isNaN(ts) && ts > 0 && (Date.now() - ts) > submittedSearchMaxAgeMs) {
        try { window.sessionStorage.removeItem(editRestoreStateKey); } catch(_e5) {}
        return null;
      }
      return normalized;
    }

    function writeEditRestoreScope(scope) {
      var normalized = normalizeEditRestoreSource(scope || {});
      if (!normalized || !normalized.make) {
        return;
      }
      normalized.ts = Date.now();
      try {
        if (window.sessionStorage) window.sessionStorage.setItem(editRestoreStateKey, JSON.stringify(normalized));
      } catch(_e2) {}
    }

    function clearSubmittedSearchRestoreTimer() {
      if (submittedSearchRestoreTimer) {
        clearTimeout(submittedSearchRestoreTimer);
        submittedSearchRestoreTimer = null;
      }
    }

    function stopSubmittedSearchRestore() {
      submittedSearchRestoreBlocked = true;
      clearSubmittedSearchRestoreTimer();
    }

    function resetSubmittedSearchRestore() {
      submittedSearchRestoreBlocked = false;
      clearSubmittedSearchRestoreTimer();
    }

    function normalizeSubmittedSearchScope(raw) {
      var out = { make: '', year: '', model: '', q: '' };
      out.make = String(raw && raw.make != null ? raw.make : '').replace(/^\s+|\s+$/g, '');
      out.year = String(raw && raw.year != null ? raw.year : '').replace(/^\s+|\s+$/g, '');
      out.model = String(raw && raw.model != null ? raw.model : '').replace(/^\s+|\s+$/g, '');
      out.q = String(raw && raw.q != null ? raw.q : '').replace(/^\s+|\s+$/g, '');
      out.q = isSensitiveSearchValue(out.q) ? '' : out.q;
      if (!out.make) return null;
      if (out.year && !/^\d+$/.test(out.year)) {
        out.year = '';
        out.model = '';
      }
      if (!out.year) out.model = '';
      return out;
    }

    function readSubmittedSearchScope() {
      var raw = '';
      var keys = [];
      var key = '';
      var parsed = null;
      var normalized = null;
      var i = 0;
      try {
        if (!window.sessionStorage) return null;
      } catch(_e) {
        return null;
      }
      keys = [submittedSearchStateKey, submittedSearchGlobalStateKey];
      for (i = 0; i < keys.length; i++) {
        key = keys[i];
        try {
          raw = window.sessionStorage.getItem(key);
        } catch(_e2) {
          raw = '';
        }
        if (!raw) continue;
        try {
          parsed = JSON.parse(raw);
        } catch(_e3) {
          try { window.sessionStorage.removeItem(key); } catch(_e4) {}
          continue;
        }
        normalized = normalizeSubmittedSearchScope(parsed || {});
        if (!normalized) {
          try { window.sessionStorage.removeItem(key); } catch(_e5) {}
          continue;
        }
        normalized.ts = parsed && parsed.ts ? parseInt(parsed.ts, 10) : 0;
        if (isNaN(normalized.ts)) normalized.ts = 0;
        if (!normalized.ts || (Date.now() - normalized.ts) > submittedSearchMaxAgeMs) {
          try { window.sessionStorage.removeItem(key); } catch(_e6) {}
          continue;
        }
        return normalized;
      }
      return null;
    }

    function clearSubmittedSearchScope() {
      submittedSearchCapturePending = false;
      submittedSearchCaptureScope = null;
      try {
        if (window.sessionStorage) {
          window.sessionStorage.removeItem(submittedSearchStateKey);
          window.sessionStorage.removeItem(submittedSearchGlobalStateKey);
        }
      } catch(_e) {}
    }

    function writeSubmittedSearchScope(scope) {
      var normalized = normalizeSubmittedSearchScope(scope || {});
      if (!normalized) {
        clearSubmittedSearchScope();
        return;
      }
      submittedSearchCapturePending = false;
      submittedSearchCaptureScope = null;
      normalized.ts = Date.now();
      try {
        if (window.sessionStorage) {
          var json = JSON.stringify(normalized);
          window.sessionStorage.setItem(submittedSearchStateKey, json);
          window.sessionStorage.setItem(submittedSearchGlobalStateKey, json);
        }
      } catch(_e) {}
    }

    function finalizeSubmittedSearchCapture(hasResults) {
      var scope = submittedSearchCaptureScope;
      if (!submittedSearchCapturePending) return;
      submittedSearchCapturePending = false;
      submittedSearchCaptureScope = null;
      if (hasResults && scope && scope.make) {
        writeSubmittedSearchScope(scope);
        return;
      }
      clearSubmittedSearchScope();
    }

    function getSubmittedSearchRestoreScope() {
      var cc = readCookie() || {};
      var saved = readSubmittedSearchScope();
      if (!saved) return null;
      if (cc && (cc.color || cc.code || cc.color_slug)) return null;
      if (cc && cc.make) {
        var cookieMake = String(cc.make || '').replace(/^\s+|\s+$/g, '');
        if (cookieMake && cookieMake !== saved.make) return null;
      }
      return saved;
    }

    function maybeRestoreSubmittedSearch(retries) {
      var cc = null;
      var savedScope = null;
      var makeVal = '';
      var yearRequired = '';
      var modelRequired = '';
      var yearVal = '';
      var modelVal = '';
      var qVal = '';
      if (submittedSearchRestoreBlocked) return false;
      if (hasHistorySearchSnapshot()) return false;
      savedScope = getSubmittedSearchRestoreScope();
      if (!savedScope) return false;
      cc = readCookie() || {};
      makeVal = String((makeSel && makeSel.value) || savedScope.make || cc.make || '').replace(/^\s+|\s+$/g, '');
      if (!makeVal) {
        clearSubmittedSearchScope();
        return false;
      }
      yearRequired = String(savedScope.year || '').replace(/^\s+|\s+$/g, '');
      modelRequired = String(savedScope.model || '').replace(/^\s+|\s+$/g, '');
      yearVal = String((yearSel && yearSel.value) || '').replace(/^\s+|\s+$/g, '');
      modelVal = String((modelSel && modelSel.value) || '').replace(/^\s+|\s+$/g, '');
      if (yearRequired && !yearVal) {
        if (retries > 0) {
          clearSubmittedSearchRestoreTimer();
          submittedSearchRestoreTimer = setTimeout(function() { maybeRestoreSubmittedSearch(retries - 1); }, 180);
        } else {
          clearSubmittedSearchScope();
        }
        return false;
      }
      if (yearRequired && yearVal !== yearRequired) {
        clearSubmittedSearchScope();
        return false;
      }
      if (modelRequired && !modelVal) {
        if (retries > 0) {
          clearSubmittedSearchRestoreTimer();
          submittedSearchRestoreTimer = setTimeout(function() { maybeRestoreSubmittedSearch(retries - 1); }, 180);
        } else {
          clearSubmittedSearchScope();
        }
        return false;
      }
      if (modelRequired && modelVal !== modelRequired) {
        clearSubmittedSearchScope();
        return false;
      }
      if (inFlight) {
        if (retries > 0) {
          clearSubmittedSearchRestoreTimer();
          submittedSearchRestoreTimer = setTimeout(function() { maybeRestoreSubmittedSearch(retries - 1); }, 180);
        }
        return false;
      }
      qVal = String((savedScope.q != null ? savedScope.q : (searchQ ? searchQ.value : '')) || '');
      if (makeSel) makeSel.value = makeVal;
      if (yearSel) yearSel.value = yearVal;
      if (modelSel) modelSel.value = modelVal;
      if (searchQ) searchQ.value = qVal;
      showError('');
      showEmpty(false);
      stopSubmittedSearchRestore();
      doSearch(false, false, null, true, true, {
        make: makeVal,
        year: yearVal,
        model: modelVal,
        q: qVal
      });
      return true;
    }

    function abortTypeaheadRequest() {
      if (typeaheadState.pendingXhr && typeaheadState.pendingXhr.readyState !== 4) {
        try { typeaheadState.pendingXhr.abort(); } catch(_e) {}
      }
      typeaheadState.pendingXhr = null;
    }

    function ensureTypeaheadUi() {
      var host = null;
      if (!searchQ || typeaheadState.shell) return;
      host = searchQ.parentNode;
      while (host && host !== root && String(host.className || '').indexOf(pfx + '-field-search-' + uid) < 0) {
        host = host.parentNode;
      }
      if (!host) host = searchQ.parentNode;
      if (!host) return;
      typeaheadState.host = host;
      typeaheadState.shell = document.createElement('div');
      typeaheadState.shell.id = pfx + '-typeahead-' + uid;
      typeaheadState.shell.className = pfx + '-typeahead-' + uid;
      typeaheadState.shell.setAttribute('role', 'listbox');
      typeaheadState.shell.setAttribute('aria-label', 'Suggested colors');
      typeaheadState.list = document.createElement('div');
      typeaheadState.list.className = pfx + '-typeahead-list-' + uid;
      typeaheadState.message = document.createElement('div');
      typeaheadState.message.className = pfx + '-typeahead-empty-' + uid;
      typeaheadState.footer = document.createElement('div');
      typeaheadState.footer.className = pfx + '-typeahead-footer-' + uid;
      typeaheadState.shell.appendChild(typeaheadState.list);
      typeaheadState.shell.appendChild(typeaheadState.message);
      typeaheadState.shell.appendChild(typeaheadState.footer);
      typeaheadState.shell.addEventListener('mousedown', function(evt) {
        evt.preventDefault();
      });
      typeaheadState.shell.addEventListener('click', function(evt) {
        var target = evt.target || evt.srcElement;
        while (target && target !== typeaheadState.shell && (!target.getAttribute || !target.getAttribute('data-ps-typeahead-index'))) {
          target = target.parentNode;
        }
        if (!target || target === typeaheadState.shell || !target.getAttribute) return;
        applyTypeaheadSuggestion(parseInt(target.getAttribute('data-ps-typeahead-index'), 10));
      });
      host.appendChild(typeaheadState.shell);
      searchQ.setAttribute('aria-controls', typeaheadState.shell.id);
      searchQ.setAttribute('aria-haspopup', 'listbox');
      searchQ.setAttribute('aria-autocomplete', 'list');
      searchQ.setAttribute('aria-expanded', 'false');
    }

    function closeTypeahead() {
      if (!typeaheadState.shell) return;
      typeaheadState.shell.removeAttribute('data-open');
      searchQ.setAttribute('aria-expanded', 'false');
      searchQ.removeAttribute('aria-activedescendant');
      typeaheadState.activeIndex = -1;
    }

    function clearTypeaheadSuggestions() {
      abortTypeaheadRequest();
      typeaheadState.requestId++;
      typeaheadState.suggestions = [];
      typeaheadState.seeAllUrl = '';
      typeaheadState.totalFound = 0;
      typeaheadState.activeIndex = -1;
      if (typeaheadState.list) typeaheadState.list.innerHTML = '';
      if (typeaheadState.message) typeaheadState.message.innerHTML = '';
      if (typeaheadState.footer) typeaheadState.footer.innerHTML = '';
      if (typeaheadState.message) typeaheadState.message.removeAttribute('data-ps-sensitive-guidance');
      closeTypeahead();
    }

    function showSensitiveSearchGuidance(sensitiveState) {
      ensureTypeaheadUi();
      if (!sensitiveState || !typeaheadState.message) return;
      abortTypeaheadRequest();
      typeaheadState.requestId++;
      typeaheadState.suggestions = [];
      typeaheadState.seeAllUrl = '';
      typeaheadState.totalFound = 0;
      typeaheadState.activeIndex = -1;
      if (typeaheadState.list) typeaheadState.list.innerHTML = '';
      if (typeaheadState.footer) typeaheadState.footer.innerHTML = '';
      typeaheadState.message.innerHTML = '<div>' + esc(sensitiveState.text || 'Use the help page for this search.') +
        ' <a href="' + esc(sensitiveState.url) + '">Get help</a></div>';
      typeaheadState.message.setAttribute('data-ps-sensitive-guidance', 'true');
      openTypeahead();
    }

    function clearSensitiveSearchGuidance() {
      if (!typeaheadState.message || !typeaheadState.message.getAttribute('data-ps-sensitive-guidance')) return;
      typeaheadState.message.innerHTML = '';
      typeaheadState.message.removeAttribute('data-ps-sensitive-guidance');
      closeTypeahead();
    }

    function openTypeahead() {
      if (!typeaheadState.shell) return;
      typeaheadState.shell.setAttribute('data-open', 'true');
      searchQ.setAttribute('aria-expanded', 'true');
    }

    function buildTypeaheadSuggestion(result, scope, rank) {
      var title = result && result.title ? String(result.title) : '';
      var label = title.replace(/\s+Touch Up Paint\s*$/i, '');
      var metaParts = [];
      var href = normalizeColorHubUrl(result && result.url ? result.url : '');
      if (!href) return null;
      if (!label) label = title || href;
      if (scope.year) metaParts.push(scope.year);
      if (scope.model) metaParts.push(scope.model);
      if (scope.make) metaParts.unshift(scope.make);
      return {
        key: String((result && result.id) || href || rank),
        label: label,
        url: href,
        meta: metaParts.join(' ' + String.fromCharCode(8226) + ' '),
        hex: normalizeColorRgb(result && (result.color_rgb || result.hex) ? (result.color_rgb || result.hex) : ''),
        rank: rank
      };
    }

    function renderTypeaheadSeeAll(seeAllUrl, totalFound) {
      if (!typeaheadState.footer) return;
      typeaheadState.seeAllUrl = seeAllUrl || '';
      typeaheadState.totalFound = totalFound || 0;
      if (!typeaheadState.seeAllUrl || typeaheadState.totalFound <= 10) {
        typeaheadState.footer.innerHTML = '';
        return;
      }
      typeaheadState.footer.innerHTML =
        '<a class="' + pfx + '-typeahead-see-all-' + uid + '" href="' + esc(typeaheadState.seeAllUrl) + '">' +
        'See all colors' +
        '</a>';
    }

    function renderTypeaheadSuggestions(suggestions, term, seeAllUrl, totalFound) {
      var html = [];
      var i;
      ensureTypeaheadUi();
      typeaheadState.suggestions = suggestions || [];
      typeaheadState.activeIndex = -1;
      if (!typeaheadState.list || !typeaheadState.message) return;
      if (!typeaheadState.suggestions.length) {
        typeaheadState.list.innerHTML = '';
        renderTypeaheadSeeAll('', 0);
        typeaheadState.message.innerHTML =
          '<div class="' + pfx + '-typeahead-empty-row-' + uid + '">No close matches found for "' + esc(term) + '".</div>';
        openTypeahead();
        return;
      }
      typeaheadState.message.innerHTML = '';
      for (i = 0; i < typeaheadState.suggestions.length; i++) {
        html.push(
          '<button type="button" id="' + pfx + '-typeahead-option-' + uid + '-' + i + '" class="' + pfx + '-typeahead-item-' + uid + '" role="option" aria-selected="false" data-ps-typeahead-index="' + i + '">' +
          '<span class="' + pfx + '-typeahead-swatch-' + uid + '" style="background:' + esc(typeaheadState.suggestions[i].hex || 'transparent') + '"></span>' +
          '<span class="' + pfx + '-typeahead-copy-' + uid + '">' +
          '<span class="' + pfx + '-typeahead-title-' + uid + '">' + esc(typeaheadState.suggestions[i].label) + '</span>' +
          '<span class="' + pfx + '-typeahead-meta-' + uid + '">' + esc(typeaheadState.suggestions[i].meta || 'Touch-up paint color') + '</span>' +
          '</span>' +
          '</button>'
        );
      }
      typeaheadState.list.innerHTML = html.join('');
      renderTypeaheadSeeAll(seeAllUrl, totalFound);
      openTypeahead();
    }

    function setTypeaheadActiveIndex(index) {
      var items = null;
      var i;
      if (!typeaheadState.list) return;
      items = typeaheadState.list.querySelectorAll('[data-ps-typeahead-index]');
      if (!items.length) return;
      if (index < 0) index = items.length - 1;
      if (index >= items.length) index = 0;
      typeaheadState.activeIndex = index;
      for (i = 0; i < items.length; i++) {
        if (i === index) {
          items[i].setAttribute('aria-selected', 'true');
          items[i].setAttribute('data-active', 'true');
        } else {
          items[i].setAttribute('aria-selected', 'false');
          items[i].removeAttribute('data-active');
        }
      }
      searchQ.setAttribute('aria-activedescendant', pfx + '-typeahead-option-' + uid + '-' + index);
    }

    function applyTypeaheadSuggestion(index) {
      var suggestion = typeaheadState.suggestions[index];
      if (!suggestion) return;
      closeTypeahead();
      if (suggestion.url) window.location.href = suggestion.url;
    }

    function requestTypeaheadSuggestions(term) {
      var sensitiveState = getSensitiveSearchState(term);
      var scope = getTypeaheadScope();
      var cacheKey = '';
      var url = '';
      var requestId;
      if (sensitiveState) {
        showSensitiveSearchGuidance(sensitiveState);
        return;
      }
      clearSensitiveSearchGuidance();
      cacheKey = getTypeaheadCacheKey(term, scope);
      url = buildPredictiveWorkerResultsUrl(term, scope, 10);
      if (!scope.make || !term || term.length < 2) {
        clearTypeaheadSuggestions();
        return;
      }
      ensureTypeaheadUi();
      if (typeaheadState.cache.hasOwnProperty(cacheKey)) {
        renderTypeaheadSuggestions(
          typeaheadState.cache[cacheKey].suggestions,
          term,
          typeaheadState.cache[cacheKey].seeAllUrl,
          typeaheadState.cache[cacheKey].totalFound
        );
        return;
      }
      abortTypeaheadRequest();
      requestId = ++typeaheadState.requestId;
      typeaheadState.pendingXhr = new XMLHttpRequest();
      typeaheadState.pendingXhr.open('GET', url, true);
      typeaheadState.pendingXhr.onreadystatechange = function() {
        var data = null;
        var raw = [];
        var seen = {};
        var suggestions = [];
        var totalFound = 0;
        var seeAllUrl = '';
        var i;
        if (this.readyState !== 4) return;
        typeaheadState.pendingXhr = null;
        if (requestId !== typeaheadState.requestId) return;
        if (!(this.status >= 200 && this.status < 300)) {
          clearTypeaheadSuggestions();
          return;
        }
        try { data = JSON.parse(this.responseText || '{}'); } catch(_e) { data = null; }
        raw = data && data.suggestions ? data.suggestions : (data && data.results ? data.results : []);
        totalFound = parseInt(data && data.totalFound, 10);
        if (isNaN(totalFound) || totalFound < 0) totalFound = raw.length;
        seeAllUrl = buildScopedSearchPageUrl(term, scope);
        for (i = 0; i < raw.length; i++) {
          var suggestion = null;
          var href = normalizeColorHubUrl(raw[i] && raw[i].url ? raw[i].url : '');
          if (!href) continue;
          if (seen[href]) continue;
          seen[href] = true;
          suggestion = buildTypeaheadSuggestion(raw[i], scope, i);
          if (!suggestion) continue;
          suggestions.push(suggestion);
          if (suggestions.length >= 10) break;
        }
        typeaheadState.cache[cacheKey] = {
          suggestions: suggestions,
          seeAllUrl: seeAllUrl,
          totalFound: totalFound
        };
        renderTypeaheadSuggestions(suggestions, term, seeAllUrl, totalFound);
      };
      typeaheadState.pendingXhr.send(null);
    }

    var debouncedTypeaheadRequest = debounce(function() {
      requestTypeaheadSuggestions(String(searchQ && searchQ.value ? searchQ.value : '').replace(/^\s+|\s+$/g, ''));
    }, 250);

    function getPagerWrapDisplayMode() {
      if (pfx === 'pscs') {
        if (typeof window !== 'undefined' && window.matchMedia) {
          return window.matchMedia('(max-width: 989px)').matches ? 'flex' : 'grid';
        }
        return 'grid';
      }
      return 'flex';
    }

    function hasActiveColorSelection(cc) {
      var candidate = cc || readCookie() || {};
      return !!(candidate && candidate.make && (candidate.code || candidate.color || candidate.color_slug));
    }

    function shouldHideFallbackActionRow() {
      return !!(directGridSearchMode && showPickedSummary && hasActiveColorSelection());
    }

    function hasCommittedColorCards() {
      return !!((state.lastItems && state.lastItems.length) || (state.lastTwoToneGroups && state.lastTwoToneGroups.length));
    }

    function setPagerWrapDisplay(show) {
      if (!pagerWrap) return;
      // Keep fallback actions hidden while a real selected color is active.
      if (show && shouldHideFallbackActionRow()) {
        pagerWrap.style.setProperty('display', 'none', 'important');
        return;
      }
      if (show && pfx === 'pscs' && !hasCommittedColorCards()) {
        pagerWrap.style.setProperty('display', 'none', 'important');
        return;
      }
      pagerWrap.style.setProperty('display', show ? getPagerWrapDisplayMode() : 'none', 'important');
    }

    function updatePagerUi() {
      renderPager();
      if (pager) pager.style.display = state.pages > 1 ? 'flex' : 'none';
      if (prevBtn) {
        prevBtn.disabled = state.page <= 1;
        prevBtn.setAttribute('aria-disabled', prevBtn.disabled ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = state.page >= state.pages;
        nextBtn.setAttribute('aria-disabled', nextBtn.disabled ? 'true' : 'false');
      }
      setPagerWrapDisplay(true);
    }

    function renderCommittedResults(items, backend, twoToneGroups) {
      state.backend = backend || getCommittedSearchBackend();
      state.lastItems = items || [];
      state.lastTwoToneGroups = twoToneGroups && twoToneGroups.length ? twoToneGroups : [];
      showError('');
      showEmpty(false);

      if (!state.lastItems.length && !state.lastTwoToneGroups.length) {
        clearGrid();
        showEmpty(true);
        setPagerWrapDisplay(true);
        restoreChangeButtonIfLoading();
        finalizeSubmittedSearchCapture(false);
        if (state.backend === 'typesense') writeHistorySearchSnapshot([], true);
        else clearHistorySearchSnapshot();
        return;
      }

      renderColorGrid(state.lastItems, state.lastTwoToneGroups);
      if (picked) picked.style.display = 'none';
      if (grid) grid.style.display = '';
      updatePagerUi();
      restoreChangeButtonIfLoading();
      finalizeSubmittedSearchCapture(true);

      if (state.backend === 'typesense') writeHistorySearchSnapshot(state.lastItems, false, state.lastTwoToneGroups);
      else clearHistorySearchSnapshot();
    }

    function runHydratedScopeSearch(scope, skipCookieWrite) {
      var normalized = scope && typeof scope === 'object' ? scope : {};
      var makeVal = String(normalized.make || '').replace(/^\s+|\s+$/g, '');
      var yearVal = String(normalized.year || '').replace(/^\s+|\s+$/g, '');
      var modelVal = String(normalized.model || '').replace(/^\s+|\s+$/g, '');
      var qVal = String(normalized.q || '').replace(/^\s+|\s+$/g, '');
      if (!makeVal) return false;
      try {
        doSearch(false, false, null, !!skipCookieWrite, true, {
          make: makeVal,
          year: yearVal,
          model: modelVal,
          q: qVal
        });
        return true;
      } catch(_e) {}
      return false;
    }

    function doSearch(isManual, overlay, cb, skipCookie, allowDuringFlight, forcedScope) {
      var requestedQuery = forcedScope && typeof forcedScope === 'object'
        ? String(forcedScope.q || '')
        : String((searchQ && searchQ.value) || '');
      var sensitiveState = getSensitiveSearchState(requestedQuery);
      if (sensitiveState) {
        // Sensitive text stays out of color-search requests, cookies, and restore state.
        clearSubmittedSearchScope();
        clearHistorySearchSnapshot();
        showSensitiveSearchGuidance(sensitiveState);
        showBtnSpinner = false;
        return;
      }
      if (inFlight) {
        if (!allowDuringFlight) return;
        abortPending();
      }
      if (!cb) {
        if (picked) picked.style.display = 'none';
        if (grid) grid.style.display = '';
      }
      if (forcedScope && typeof forcedScope === 'object') {
        state.make = String(forcedScope.make || '').replace(/^\s+|\s+$/g, '');
        state.year = String(forcedScope.year || '').replace(/^\s+|\s+$/g, '');
        state.model = String(forcedScope.model || '').replace(/^\s+|\s+$/g, '');
        state.q = getCommittedSearchTerm(String(forcedScope.q || '').replace(/^\s+|\s+$/g, ''));
        if (makeSel) makeSel.value = state.make;
        if (yearSel) yearSel.value = state.year;
        if (modelSel) modelSel.value = state.model;
        if (searchQ) searchQ.value = String(forcedScope.q || '');
      } else {
        state.make = (makeSel.value || '').trim();
        state.year = ((yearSel && yearSel.value) || '').trim();
        state.model = ((modelSel && modelSel.value) || '').trim();
        state.q = getCommittedSearchTerm(String((searchQ && searchQ.value) || '').replace(/^\s+|\s+$/g, ''));
      }
      state.page = 1;
      state.backend = getCommittedSearchBackend();
      closeTypeahead();
      writeEditRestoreScope({
        make: state.make,
        year: state.year,
        model: state.model,
        q: state.q
      });
      if (isManual) {
        submittedSearchCapturePending = true;
        submittedSearchCaptureScope = {
          make: state.make,
          year: state.year,
          model: state.model,
          q: state.q
        };
      } else {
        submittedSearchCapturePending = false;
        submittedSearchCaptureScope = null;
      }

      if (!state.make) {
        finalizeSubmittedSearchCapture(false);
        clearHistorySearchSnapshot();
        clearGrid();
        if (grid) grid.innerHTML = '<div style="text-align:center;padding:16px;color:#999">Please select a make.</div>';
        if (pager) pager.style.display = 'none';
        if (cb && typeof cb === 'function') cb(false);
        return;
      }

      // Write search context cookie (clear old color context).
      // Skip during cookie-based hydration to avoid clearing existing color context.
      if (!skipCookie) {
        try { writeSearchContextCookie({ ps_from_search: !!isManual }); } catch(_e) {}
      }

      if (state.backend !== 'typesense') clearHistorySearchSnapshot();

      showBtnSpinner = !!isManual;
      try {
        fetchResults(!!overlay, cb);
      } catch(e) {
        finalizeSubmittedSearchCapture(false);
        setLoading(false);
        showError('Connection error. Please try again.');
        if (cb && typeof cb === 'function') cb(false);
      }
    }

    function restoreChangeButtonIfLoading() {
      var changeBtn = document.getElementById(pfx + '-change-' + uid);
      var defaultHtml = '';
      if (!changeBtn) return;
      defaultHtml = String(changeBtn.getAttribute('data-ps-original-label-html') || '');
      if (changeBtn.style.pointerEvents === 'none') changeBtn.style.pointerEvents = '';
      if (defaultHtml && String(changeBtn.innerHTML || '').indexOf('Loading...') === 0) {
        changeBtn.innerHTML = defaultHtml;
      }
    }

    function buildHubColorsUrl(pageValue, perValue, includeTwoToneGroups) {
      var params = { page: pageValue || state.page, per_page: perValue || state.per };
      if (includeTwoToneGroups === true) params.include_two_tone_groups = 1;
      return apiBase() + 'shopify/colors/' +
        encodeURIComponent(String(state.make || '').toLowerCase()) +
        '?' + qs(params) +
        (state.make ? '&make=' + encodeURIComponent(state.make) : '') +
        (state.year ? '&year=' + encodeURIComponent(state.year) : '') +
        (state.model ? '&model=' + encodeURIComponent(state.model) : '') +
        (state.q ? '&q=' + encodeURIComponent(state.q) : '');
    }

    function shouldFetchTwoToneGroupsForCurrentPage() {
      var page = parseInt(state.page, 10);
      var pages = parseInt(state.pages, 10);
      return !!state.make && !isNaN(page) && !isNaN(pages) && pages > 0 && page >= pages;
    }

    function fetchHubTwoToneGroups(requestId, cb) {
      var xhr = null;
      if (!state.make) {
        cb([]);
        return;
      }
      xhr = new XMLHttpRequest();
      xhr.open('GET', buildHubColorsUrl(1, state.per, true), true);
      xhr.onreadystatechange = function() {
        var data = null;
        if (xhr.readyState !== 4) return;
        if (requestId !== fetchRequestSeq) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          try { data = JSON.parse(xhr.responseText || '{}'); } catch(_e) { data = {}; }
          cb(normalizeTwoToneGroups(data && data.two_tone_groups ? data.two_tone_groups : []));
          return;
        }
        cb([]);
      };
      xhr.send(null);
    }

    function fetchHubResults(isPaging, cb) {
      var requestId = 0;
      var waitingForTwoToneGroups = false;
      setLoading(true, !!isPaging);
      if (!isPaging && grid) {
        grid.innerHTML = '';
        if (pager) pager.style.display = 'none';
      }

      var url = buildHubColorsUrl(state.page, state.per, false);

      abortPending();
      fetchRequestSeq += 1;
      requestId = fetchRequestSeq;
      var xhr = new XMLHttpRequest();
      pendingXhr = xhr;
      xhr.open('GET', url, true);
      xhr.onreadystatechange = function() {
        var succeeded = false;
        if (xhr.readyState !== 4) return;
        if (requestId !== fetchRequestSeq) return;
        pendingXhr = null;
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            var data;
            try { data = JSON.parse(xhr.responseText || '[]'); } catch(_e) { data = []; }
            var raw = Array.isArray(data) ? data : (data && data.data ? data.data : []);
            var twoToneGroups = [];

            // Build items with metadata
            var items = [];
            for (var i = 0; i < raw.length; i++) {
              var it = raw[i] || {};
              var meta = extractMeta(it.products);
              items.push({
                make_name: it.make_name || it.make || state.make,
                make_slug: it.make_slug || '',
                color_name: it.color_name || it.color || '',
                color_slug: it.color_slug || '',
                color_code: it.color_code || it.code || '',
                color_rgb: normalizeColorRgb(it.color_rgb || it.hex || '') || '#CCCCCC',
                is_tricoat: it.is_tricoat === true,
                shopify_page: it.shopify_page || '',
                products: it.products || null,
                pen_url: meta.pen.url, bottle_url: meta.bottle.url,
                spray_url: meta.spray.url, pro_url: meta.pro.url,
                pen_img: meta.pen.img, bottle_img: meta.bottle.img,
                spray_img: meta.spray.img, pro_img: meta.pro.img,
                pen_name: meta.pen.name, bottle_name: meta.bottle.name,
                spray_name: meta.spray.name, pro_name: meta.pro.name,
                pen_price: meta.pen.price, bottle_price: meta.bottle.price,
                spray_price: meta.spray.price, pro_price: meta.pro.price
              });
            }

            // Pagination
            var total = data && data.total ? data.total : items.length;
            var per = data && data.per_page ? data.per_page : state.per;
            var page = data && data.current_page ? data.current_page : state.page;
            state.pages = data && data.last_page ? data.last_page : (total && per ? Math.max(1, Math.ceil(total / per)) : 1);
            state.page = page;

            if (shouldFetchTwoToneGroupsForCurrentPage()) {
              succeeded = true;
              waitingForTwoToneGroups = true;
              fetchHubTwoToneGroups(requestId, function(groups) {
                if (requestId !== fetchRequestSeq) return;
                pendingXhr = null;
                renderCommittedResults(items, 'hub', groups);
                setLoading(false);
                if (cb && typeof cb === 'function') cb(true);
              });
            } else {
              renderCommittedResults(items, 'hub', twoToneGroups);
              succeeded = true;
            }
          } else {
            finalizeSubmittedSearchCapture(false);
            showError('Error loading colors. Please try again.');
            if (pager) pager.style.display = 'none';
          }
        } finally {
          if (waitingForTwoToneGroups) return;
          setLoading(false);
          if (cb && typeof cb === 'function') cb(succeeded);
        }
      };
      xhr.send(null);
    }

    function fetchTypesenseResults(isPaging, cb) {
      var scope = getSearchScopeFromState();
      var url = buildWorkerResultsUrl(state.q, scope, state.page, state.per);
      var requestId = 0;

      setLoading(true, !!isPaging);
      if (!isPaging && grid) {
        grid.innerHTML = '';
        if (pager) pager.style.display = 'none';
      }

      abortPending();
      fetchRequestSeq += 1;
      requestId = fetchRequestSeq;
      var xhr = new XMLHttpRequest();
      pendingXhr = xhr;
      xhr.open('GET', url, true);
      xhr.onreadystatechange = function() {
        var data = null;
        var raw = [];
        var items = [];
        var page = state.page;
        var pages = 1;
        var per = state.per;
        var i = 0;
        var waitingForTwoToneGroups = false;
        var succeeded = false;

        if (xhr.readyState !== 4) return;
        if (requestId !== fetchRequestSeq) return;

        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { data = JSON.parse(xhr.responseText || '{}'); } catch(_e) { data = {}; }
            raw = data && data.results ? data.results : [];
            for (i = 0; i < raw.length; i++) {
              var item = normalizeWorkerResultItem(raw[i], scope);
              if (item) items.push(item);
            }

            page = parseInt(data && data.page, 10);
            if (isNaN(page) || page < 1) page = state.page;
            pages = parseInt(data && data.totalPages, 10);
            if (isNaN(pages) || pages < 1) pages = 1;
            per = parseInt(data && data.perPage, 10);
            if (!isNaN(per) && per > 0) state.per = per;

            state.page = page;
            state.pages = pages;
            if (shouldFetchTwoToneGroupsForCurrentPage()) {
              succeeded = true;
              waitingForTwoToneGroups = true;
              fetchHubTwoToneGroups(requestId, function(twoToneGroups) {
                if (requestId !== fetchRequestSeq) return;
                pendingXhr = null;
                renderCommittedResults(items, 'typesense', twoToneGroups);
                setLoading(false);
                if (cb && typeof cb === 'function') cb(true);
              });
            } else {
              renderCommittedResults(items, 'typesense', []);
              succeeded = true;
            }
          } else {
            finalizeSubmittedSearchCapture(false);
            showError('Error loading colors. Please try again.');
            if (pager) pager.style.display = 'none';
          }
        } finally {
          if (waitingForTwoToneGroups) return;
          pendingXhr = null;
          setLoading(false);
          if (cb && typeof cb === 'function') cb(succeeded);
        }
      };
      xhr.send(null);
    }

    function fetchResults(isPaging, cb) {
      if (state.backend === 'typesense') {
        fetchTypesenseResults(isPaging, cb);
        return;
      }
      fetchHubResults(isPaging, cb);
    }

    // ==========================================================
    // CARD RENDERING
    // ==========================================================

    function buildSaveColorHelpOptions(candidate) {
      var cc = readCookie() || {};
      var picked = candidate || {};
      return {
        source_context: 'color_grid',
        trigger_type: 'help_click',
        source_page_url: window.location && window.location.href ? window.location.href : '',
        make: trimValue(state.make || cc.make || ''),
        year: trimValue(state.year || cc.year || ''),
        model: trimValue(state.model || cc.model || ''),
        search_term: trimValue(state.q || cc.q || ''),
        candidate_color: trimValue(picked.color || ''),
        candidate_code: trimValue(picked.code || '')
      };
    }

    function tryOpenSaveColorHelp(options) {
      var api = window.PS && window.PS.SaveColor ? window.PS.SaveColor : null;
      if (!api) return false;
      if (typeof api.openHelp === 'function') {
        try {
          return !!api.openHelp(options || {});
        } catch(_e) {}
      }
      if (typeof api.open === 'function') {
        try {
          return !!api.open(options || {});
        } catch(_e2) {}
      }
      return false;
    }

    function requestDelayedSaveColor(callback) {
      var theme = window.paintscratchTheme || {};
      if (typeof theme.loadDelayedSitewideCapture !== 'function') return false;
      try {
        theme.loadDelayedSitewideCapture(callback);
        return true;
      } catch(_e) {
        return false;
      }
    }

    function openSaveColorHelp(options) {
      var opts = options || {};
      if (tryOpenSaveColorHelp(opts)) return true;
      if (requestDelayedSaveColor(function() { tryOpenSaveColorHelp(opts); })) return true;
      return false;
    }

    function normalizeColorResultItem(it, fallbackMake, fallbackMakeSlug) {
      var meta = extractMeta(it && it.products ? it.products : null);
      it = it || {};
      return {
        make_name: it.make_name || it.make || fallbackMake || state.make,
        make_slug: it.make_slug || fallbackMakeSlug || '',
        color_name: it.color_name || it.color || '',
        color_slug: it.color_slug || '',
        color_code: it.color_code || it.code || '',
        color_rgb: normalizeColorRgb(it.color_rgb || it.hex || '') || '#CCCCCC',
        is_tricoat: it.is_tricoat === true,
        years: Array.isArray(it.years) ? it.years.slice(0) : [],
        models: Array.isArray(it.models) ? it.models.slice(0) : [],
        factory_codes: Array.isArray(it.factory_codes) ? it.factory_codes.slice(0) : [],
        shopify_page: it.shopify_page || '',
        products: it.products || null,
        pen_url: meta.pen.url, bottle_url: meta.bottle.url,
        spray_url: meta.spray.url, pro_url: meta.pro.url,
        pen_img: meta.pen.img, bottle_img: meta.bottle.img,
        spray_img: meta.spray.img, pro_img: meta.pro.img,
        pen_name: meta.pen.name, bottle_name: meta.bottle.name,
        spray_name: meta.spray.name, pro_name: meta.pro.name,
        pen_price: meta.pen.price, bottle_price: meta.bottle.price,
        spray_price: meta.spray.price, pro_price: meta.pro.price
      };
    }

    function readCardContextArray(card, attributeName) {
      var raw = card && card.getAttribute ? card.getAttribute(attributeName) : '';
      var parsed = null;
      if (!raw) return [];
      try { parsed = JSON.parse(raw); } catch(_e) {}
      return Array.isArray(parsed) ? parsed.slice(0) : [];
    }

    function normalizeTwoToneComponent(component, group) {
      var resultType = String(component && component.result_type ? component.result_type : '').toLowerCase();
      var normalized = normalizeColorResultItem(component || {}, group.make_name, group.make_slug);
      normalized.result_type = resultType || 'color';
      normalized.available = normalized.result_type === 'color' && component && component.available === true;
      normalized.disabled_label = normalized.result_type === 'unpainted' ? 'Unpainted component' : 'Not available online';
      return normalized;
    }

    function normalizeTwoToneGroups(rawGroups) {
      var groups = [];
      var source = Array.isArray(rawGroups) ? rawGroups : [];
      var i = 0;
      var j = 0;
      for (i = 0; i < source.length; i++) {
        var rawGroup = source[i] || {};
        var rawComponents = Array.isArray(rawGroup.components) ? rawGroup.components : [];
        var group = {
          label: rawGroup.label || 'Two Tone Color Combination',
          two_tone_code: rawGroup.two_tone_code || rawGroup.source_color_code || '',
          paint_code_label: rawGroup.paint_code_label || '',
          make_name: rawGroup.make_name || state.make || '',
          make_slug: rawGroup.make_slug || toSlug(rawGroup.make_name || state.make || ''),
          components: []
        };
        if (!group.paint_code_label && group.two_tone_code) {
          group.paint_code_label = 'Paint Code: ' + group.two_tone_code;
        }
        for (j = 0; j < rawComponents.length; j++) {
          group.components.push(normalizeTwoToneComponent(rawComponents[j], group));
        }
        groups.push(group);
      }
      return groups;
    }

    function buildColorCardHtml(h, extraClass) {
      var mSlug = h.make_slug || toSlug(h.make_name);
      var cSlugVal = h.color_slug || buildColorSlug(h.color_name, h.color_code);
      var sw = h.color_rgb ? 'background:' + esc(h.color_rgb) : 'background:transparent';
      var displayCode = normalizeColorCodeDisplay(h.color_code);
      var displayName = normalizeColorNameDisplay(h.color_name);
      var labelText = colorCardAccessibleLabel(h.color_code, h.color_name);
      var titleText = displayCode + ', ' + displayName;
      var cardContextLabel = buildVehicleContextLabel(state, h.make_name);
      var cardHubHref = buildColorHubUrl({
        shopify_page: h.shopify_page || '',
        make_slug: mSlug,
        color_slug: cSlugVal,
        make: h.make_name,
        color: h.color_name,
        code: h.color_code
      }, {});
      var href = '#';
      var directUrl = '';
      var prodEntry = null;
      var p = 0;
      var sl = '';
      var className = pfx + '-card-' + uid + (extraClass ? ' ' + extraClass : '');
      var dataAttrs = '';

      if (navigateOnSelect && productBase) {
        if (h.products) {
          if (typeof h.products === 'object' && !Array.isArray(h.products)) {
            prodEntry = h.products[productBase] || h.products['paint-' + productBase] || null;
          } else if (Array.isArray(h.products)) {
            for (p = 0; p < h.products.length; p++) {
              sl = String(h.products[p].type_slug || h.products[p].slug || '').toLowerCase();
              if (sl === productBase || sl === 'paint-' + productBase || sl.indexOf(productBase) === 0) {
                prodEntry = h.products[p];
                break;
              }
            }
          }
          if (prodEntry && prodEntry.url) directUrl = normalizeScopedColorHubUrl(prodEntry.url, true) || prodEntry.url;
        }
        href = directUrl || ('/colors/' + encodeURIComponent(mSlug) + '/' + encodeURIComponent(cSlugVal) + '/' + productBase);
      } else if (navigateOnSelect) {
        href = cardHubHref || ('/colors/' + encodeURIComponent(mSlug) + '/' + encodeURIComponent(cSlugVal) + '/');
      }

      dataAttrs =
        ' data-is-tricoat="' + (h.is_tricoat ? '1' : '0') + '"' +
        ' data-make="' + esc(h.make_name) + '"' +
        ' data-make-slug="' + esc(mSlug) + '"' +
        ' data-color="' + esc(h.color_name) + '"' +
        ' data-color-slug="' + esc(cSlugVal) + '"' +
        ' data-code="' + esc(h.color_code) + '"' +
        ' data-hex="' + esc(h.color_rgb) + '"' +
        ' data-years="' + esc(JSON.stringify(h.years || [])) + '"' +
        ' data-models="' + esc(JSON.stringify(h.models || [])) + '"' +
        ' data-factory-codes="' + esc(JSON.stringify(h.factory_codes || [])) + '"' +
        ' data-shopify-page="' + esc(cardHubHref || '') + '"' +
        ' data-pen-url="' + esc(h.pen_url) + '"' +
        ' data-bottle-url="' + esc(h.bottle_url) + '"' +
        ' data-spray-url="' + esc(h.spray_url) + '"' +
        ' data-pro-url="' + esc(h.pro_url) + '"' +
        ' data-pen-img="' + esc(h.pen_img || '') + '"' +
        ' data-bottle-img="' + esc(h.bottle_img || '') + '"' +
        ' data-spray-img="' + esc(h.spray_img || '') + '"' +
        ' data-pro-img="' + esc(h.pro_img || '') + '"' +
        ' data-pen-name="' + esc(h.pen_name || '') + '"' +
        ' data-bottle-name="' + esc(h.bottle_name || '') + '"' +
        ' data-spray-name="' + esc(h.spray_name || '') + '"' +
        ' data-pro-name="' + esc(h.pro_name || '') + '"' +
        ' data-pen-price="' + esc(h.pen_price || '') + '"' +
        ' data-bottle-price="' + esc(h.bottle_price || '') + '"' +
        ' data-spray-price="' + esc(h.spray_price || '') + '"' +
        ' data-pro-price="' + esc(h.pro_price || '') + '"';

      if (cardStyle === 'row') {
        return '<a href="' + esc(href) + '" class="' + className + '"' + dataAttrs + ' aria-label="' + esc(labelText) + '" title="' + esc(titleText) + '">' +
          '<div class="' + pfx + '-card-row-' + uid + '">' +
          '<span class="' + pfx + '-swatch-' + uid + '" style="' + sw + '"></span>' +
          '<div class="' + pfx + '-card-body-' + uid + '">' +
          '<div class="' + pfx + '-card-label-' + uid + '">' + esc(cardContextLabel) + '</div>' +
          '<div class="' + pfx + '-card-code-' + uid + '">' + colorIdentityHtml(displayCode, displayName) + '</div>' +
          '</div>' +
          '<span class="' + pfx + '-card-chevron-' + uid + '" aria-hidden="true">&gt;</span>' +
          '</div>' +
          '</a>';
      }

      return '<a href="' + esc(href) + '" class="' + className + '"' + dataAttrs + ' aria-label="' + esc(labelText) + '" title="' + esc(titleText) + '">' +
        '<span class="' + pfx + '-swatch-' + uid + '" style="' + sw + '"></span>' +
        '<span class="' + pfx + '-card-text-' + uid + '">' +
        '<span class="' + pfx + '-card-code-' + uid + '">' + colorIdentityHtml(displayCode, displayName) + '</span>' +
        '</span>' +
        '</a>';
    }

    function renderTwoToneComponent(component) {
      var sw = component.color_rgb ? 'background:' + esc(component.color_rgb) : 'background:transparent';
      if (component.available) {
        return buildColorCardHtml(component, pfx + '-two-tone-component-card-' + uid);
      }
      return '<div class="' + pfx + '-two-tone-component-disabled-' + uid + '">' +
        '<div class="' + pfx + '-card-row-' + uid + '">' +
        '<span class="' + pfx + '-swatch-' + uid + '" style="' + sw + '"></span>' +
        '<div class="' + pfx + '-card-body-' + uid + '">' +
        '<div class="' + pfx + '-card-title-' + uid + '">' + esc(component.color_name || component.color_code || component.disabled_label) + '</div>' +
        '<div class="' + pfx + '-card-meta-' + uid + '">' + esc(component.disabled_label) + '</div>' +
        '</div>' +
        '</div>' +
        '</div>';
    }

    function buildTwoToneGroupTitle(group) {
      var code = normalizeColorCodeDisplay(group && group.two_tone_code ? group.two_tone_code : '');
      var label = group && group.label ? group.label : 'Two Tone Color Combination';
      return code && code !== 'Code unavailable' ? code + ' - ' + label : label;
    }

    function renderTwoToneGroup(group) {
      var html = [];
      var i = 0;
      var title = buildTwoToneGroupTitle(group);
      var caption = buildVehicleContextLabel(state, group.make_name);
      html.push(
        '<div class="' + pfx + '-two-tone-group-' + uid + '" data-ps-two-tone-group="1" role="group" aria-label="' + esc(title) + '">' +
        '<div class="' + pfx + '-two-tone-group-head-' + uid + '">' +
        '<div class="' + pfx + '-two-tone-group-caption-' + uid + '">' + esc(caption) + '</div>' +
        '<div class="' + pfx + '-two-tone-group-label-' + uid + '">' + esc(title) + '</div>' +
        '</div>' +
        '<div class="' + pfx + '-two-tone-components-' + uid + '">'
      );
      for (i = 0; i < group.components.length; i++) {
        html.push(renderTwoToneComponent(group.components[i]));
      }
      html.push('</div></div>');
      return html.join('');
    }

    function renderColorGrid(items, twoToneGroups) {
      if (!grid) return;
      var html = [];
      var helpOptions = null;
      var groups = twoToneGroups && twoToneGroups.length ? twoToneGroups : [];
      var i = 0;
      for (i = 0; i < items.length; i++) {
        html.push(buildColorCardHtml(items[i], ''));
      }
      for (i = 0; i < groups.length; i++) {
        html.push(renderTwoToneGroup(groups[i]));
      }
      if (showHelpCard) {
        helpOptions = buildSaveColorHelpOptions();
        html.push(
          '<a href="#" class="' + pfx + '-card-' + uid + ' ' + pfx + '-card-help-' + uid + '"' +
          ' data-ps-help-card="1"' +
          ' data-source-context="color_grid"' +
          ' data-candidate-color="' + esc(helpOptions.candidate_color || '') + '"' +
          ' data-candidate-code="' + esc(helpOptions.candidate_code || '') + '"' +
          ' role="button" aria-label="Help me choose the right color">' +
          '<div class="' + pfx + '-card-row-' + uid + '">' +
          '<span class="' + pfx + '-swatch-' + uid + '" style="display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#f5f8fd 0%,#e9eef7 100%);color:#1b3556;font-size:24px;font-weight:700;">?</span>' +
          '<div class="' + pfx + '-card-body-' + uid + '">' +
          '<div class="' + pfx + '-card-title-' + uid + '">Help me choose the right color</div>' +
          '<div class="' + pfx + '-card-meta-' + uid + '">Get expert guidance by email.</div>' +
          '</div>' +
          '<span class="' + pfx + '-card-chevron-' + uid + '" aria-hidden="true">&gt;</span>' +
          '</div>' +
          '</a>'
        );
      }
      grid.innerHTML = html.join('');
    }

    function restoreSearchResultsFromHistory(snapshot) {
      var snap = snapshot || getHistorySearchSnapshot();
      var customHeader = document.getElementById(selectedHeaderId);
      var page = 1;
      var pages = 1;

      if (!snap) return false;

      page = parseInt(snap.page, 10);
      if (isNaN(page) || page < 1) page = 1;
      pages = parseInt(snap.pages, 10);
      if (isNaN(pages) || pages < 1) pages = 1;

      state.make = String(snap.make || '').replace(/^\s+|\s+$/g, '');
      state.year = String(snap.year || '').replace(/^\s+|\s+$/g, '');
      state.model = String(snap.model || '').replace(/^\s+|\s+$/g, '');
      state.q = String(snap.q || '').replace(/^\s+|\s+$/g, '');
      state.page = page;
      state.pages = pages;
      state.backend = 'typesense';
      state.lastItems = Array.isArray(snap.items) ? copySnapshotItems(snap.items) : [];
      state.lastTwoToneGroups = Array.isArray(snap.twoToneGroups) ? copyTwoToneGroups(snap.twoToneGroups) : [];

      if (makeSel && state.make) makeSel.value = state.make;
      if (yearSel && state.year) yearSel.value = state.year;
      if (modelSel && state.model) modelSel.value = state.model;
      if (searchQ) searchQ.value = state.q;

      setSearchInputDisabled(!state.make);
      closeTypeahead();
      showError('');
      showEmpty(false);
      if (searchUI) searchUI.setAttribute('style', 'display: block !important');
      if (picked) picked.style.display = 'none';
      if (customHeader) customHeader.style.setProperty('display', 'none', 'important');
      if (grid) grid.style.display = '';

      if (!state.lastItems.length && !state.lastTwoToneGroups.length) {
        clearGrid();
        showEmpty(true);
        setPagerWrapDisplay(true);
        return true;
      }

      renderColorGrid(state.lastItems, state.lastTwoToneGroups);
      updatePagerUi();
      return true;
    }

    // ==========================================================
    // PAGINATION
    // ==========================================================

    function renderPager() {
      if (!pagesWrap) return;
      var total = state.pages, cur = state.page, html = [];
      function btn(p) {
        var active = p === cur;
        html.push(
          '<button type="button" class="' + pfx + '-pagebtn-' + uid +
          (active ? ' is-active' : '') + '" data-page="' + p + '" ' +
          (active ? 'aria-current="page"' : 'aria-label="Go to page ' + p + '"') +
          '>' + p + '</button>'
        );
      }
      function ell() {
        html.push('<span class="' + pfx + '-ellipsis-' + uid + '">...</span>');
      }
      if (total <= 7) {
        for (var i = 1; i <= total; i++) btn(i);
      } else {
        btn(1);
        if (cur > 4) ell();
        var start = Math.max(2, cur - 1);
        var end = Math.min(total - 1, cur + 1);
        for (var j = start; j <= end; j++) btn(j);
        if (cur < total - 3) ell();
        btn(total);
      }
      pagesWrap.innerHTML = html.join('');
    }

    // ==========================================================
    // B-STATE: PICKED SUMMARY
    // ==========================================================

    function renderPickedSummary(detail) {
      if (!picked || !showPickedSummary) return;

      if (searchUI) searchUI.style.display = 'none';
      setPagerWrapDisplay(false);
      var customHeader = document.getElementById(selectedHeaderId);
      if (customHeader) customHeader.style.setProperty('display', 'block', 'important');

      var colorText = detail && detail.color ? String(detail.color) : '';
      var codeText = detail && detail.code ? String(detail.code) : '';

      if (!colorText && !codeText) {
        if (searchUI) searchUI.style.display = '';
        setPagerWrapDisplay(true);
        if (picked) picked.style.display = 'none';
        scheduleSelectedColorCollectionCardTitles(null);
        return;
      }

      var swatchBase = '';
      try {
        if (window.paintscratchTheme && window.paintscratchTheme.psUrl) {
          swatchBase = String(window.paintscratchTheme.psUrl).replace(/\/+$/, '') + '/img/swatch/';
        }
      } catch(_e) {}

      var swatch = detail && detail.swatch_url
        ? detail.swatch_url
        : (detail && detail.hex ? swatchBase + String(detail.hex).toUpperCase() + '.jpg' : '');

      var shopLink = detail && detail.shopify_page ? String(detail.shopify_page) : '';
      var swatchColorName = detail && detail.color ? String(detail.color) : 'this color';
      var swatchImg = swatch
        ? '<img src="' + esc(swatch) + '" alt="Selected color swatch" loading="eager">'
        : '<span class="' + pfx + '-compact-swatch-fallback-' + uid + '" aria-hidden="true">?</span>';
      var swatchHtml = shopLink
        ? '<a href="' + esc(shopLink) + '" class="' + pfx + '-compact-swatch-link-' + uid + '" title="View products in ' + esc(swatchColorName) + '">' + swatchImg + '</a>'
        : swatchImg;
      var ctaHtml = shopLink
        ? '<a href="' + esc(shopLink) + '" class="' + pfx + '-compact-cta-' + uid + '" title="Shop ' + esc(swatchColorName) + '">Shop this color &gt;</a>'
        : '';
      var selectionName = normalizeColorNameDisplay(colorText);
      var selectionCode = normalizeColorCodeDisplay(codeText);
      var selectionContext = buildVehicleContextLabel(state, detail && detail.make);

      picked.innerHTML =
        '<div class="' + pfx + '-compact-label-' + uid + '">CURRENT SELECTION:</div>' +
        '<div class="' + pfx + '-compact-bar-' + uid + '">' +
        '<div class="' + pfx + '-compact-main-' + uid + '">' +
        '<div class="' + pfx + '-compact-swatch-' + uid + '">' + swatchHtml + '</div>' +
        '<div class="' + pfx + '-compact-info-' + uid + '">' +
        '<div class="' + pfx + '-compact-code-label-' + uid + '">' + esc(selectionContext) + '</div>' +
        '<div class="' + pfx + '-compact-code-' + uid + '">' + colorIdentityHtml(selectionCode, selectionName) + '</div>' +
        '<div class="' + pfx + '-compact-meta-row-' + uid + '">' +
        '<button type="button" class="' + pfx + '-compact-edit-' + uid + '" id="' + pfx + '-change-' + uid + '"><span class="' + pfx + '-compact-edit-label-' + uid + '">Change color</span> <span aria-hidden="true">&#9662;</span></button>' +
        '</div>' +
        '</div>' +
        '</div>' +
        '<div class="' + pfx + '-compact-footer-' + uid + '">' + ctaHtml + '</div>' +
        '</div>';

      picked.style.display = 'block';
      scheduleSelectedColorCollectionCardTitles(detail);
    }

    function scheduleSelectedColorCollectionCardTitles(detail) {
      syncSelectedColorCollectionCardTitles(detail);
      setTimeout(function() { syncSelectedColorCollectionCardTitles(detail); }, 100);
      setTimeout(function() { syncSelectedColorCollectionCardTitles(detail); }, 500);
      setTimeout(function() { syncSelectedColorCollectionCardTitles(detail); }, 1500);
      setTimeout(function() { syncSelectedColorCollectionCardTitles(detail); }, 4000);
      setTimeout(function() { syncSelectedColorCollectionCardTitles(detail); }, 9000);
    }

    function normalizeSelectedColorCollectionCardProductType(originalTitle) {
      var text = trimValue(originalTitle);
      var isTricoat = false;
      if (!text) return '';
      text = text.replace(/^Custom Color\s+/i, '');
      text = text.replace(/^Exact-Match\s+/i, '');
      if (/^Tricoat\b/i.test(text)) isTricoat = true;
      text = text.replace(/^Tricoat\s+/i, '');
      text = text.replace(/^Touch[\s-]+Up\s+Paint\s+/i, '');
      text = text.replace(/^Touch[\s-]+Up\s+/i, '');
      if (/^(Paint\s+)?Pen\s+Kit\b/i.test(text)) {
        text = 'Paint Pen Kit';
      } else if (/^(Paint\s+)?Bottle\s+Kit\b/i.test(text)) {
        text = 'Paint Bottle Kit';
      } else if (/^Spray\s+Can\s+Kit\b/i.test(text)) {
        text = 'Spray Can Kit';
      } else if (/^(Paint\s+)?Pen\b/i.test(text)) {
        text = 'Paint Pen';
      } else if (/^(Paint\s+)?Bottle\b/i.test(text)) {
        text = 'Paint Bottle';
      } else if (/^Spray\s+Can\b/i.test(text)) {
        text = 'Spray Can';
      } else if (/^(PRO|Professional\s+Size|Professional\s+Sizes)\b/i.test(text)) {
        text = 'PRO Sizes';
      }
      text = trimValue(text);
      if (isTricoat && text && !/^Tricoat\b/i.test(text)) {
        text = 'Tricoat ' + text;
      }
      return text;
    }

    function buildSelectedColorCollectionCardTitle(prefix, originalTitle) {
      var text = normalizeSelectedColorCollectionCardProductType(originalTitle);
      if (!prefix) return text;
      return text ? text + ' ' + prefix : prefix;
    }

    function syncSelectedColorCollectionCardTitles(detail) {
      var prefix = '';
      var identity = '';
      var cards = null;
      var i = 0;
      if (detail && detail.color && detail.code) {
        identity = String(detail.color);
        prefix = 'in ' + identity + ' (' + String(detail.code) + ')';
      }
      try {
        cards = document.querySelectorAll('[data-ps-custom-color-card="1"]');
      } catch(_e) {
        cards = null;
      }
      if (!cards || !cards.length) return;
      for (i = 0; i < cards.length; i++) {
        var card = cards[i];
        var labels = null;
        var titleEl = null;
        var originalTitle = '';
        var nextTitle = '';
        if (!card || !card.querySelector) continue;
        try {
          labels = card.querySelectorAll('[data-ps-custom-color-label="1"]');
        } catch(_e2) {
          labels = null;
        }
        if (labels && labels.length) {
          var j = 0;
          for (j = 0; j < labels.length; j++) {
            if (!labels[j] || !labels[j].setAttribute) continue;
            labels[j].textContent = '';
            labels[j].setAttribute('hidden', 'hidden');
          }
        }
        titleEl = card.querySelector('a[id^="CardLink-"]');
        if (!titleEl) titleEl = card.querySelector('.card__heading a.full-unstyled-link');
        if (!titleEl) continue;
        originalTitle = String(titleEl.getAttribute('data-ps-original-card-title') || '');
        if (!originalTitle) {
          originalTitle = trimValue(titleEl.textContent || '');
          if (originalTitle) titleEl.setAttribute('data-ps-original-card-title', originalTitle);
        }
        if (!originalTitle) continue;
        nextTitle = buildSelectedColorCollectionCardTitle(prefix, originalTitle);
        if (trimValue(titleEl.textContent || '') !== nextTitle) {
          titleEl.textContent = nextTitle;
        }
      }
    }

    function scrollPickedIntoView() {
      try {
        if (!picked || (picked.style && picked.style.display === 'none')) return;
        var r = picked.getBoundingClientRect ? picked.getBoundingClientRect() : null;
        if (!r) return;
        var vh = window.innerHeight || document.documentElement.clientHeight || 0;
        var desiredTop = 2;
        if (Math.abs(r.top - desiredTop) <= 24) return;
        var curTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        var targetTop = curTop + r.top;
        try { window.scrollTo({ top: targetTop, behavior: 'smooth' }); } catch(_e) { window.scrollTo(0, targetTop); }
      } catch(_e) {}
    }

    // ==========================================================
    // PRODUCT ROW REWRITING (homepage search mode)
    // ==========================================================

    function setWrapperDisplay(el, value) {
      if (!el || !el.style) return;
      el.style.setProperty('display', value, 'important');
    }

    function setWrapperListDisplay(elements, value) {
      var i = 0;
      if (!elements || !elements.length) return;
      for (i = 0; i < elements.length; i++) {
        setWrapperDisplay(elements[i], value);
      }
    }

    function getWrappers() {
      function firstById(ids) {
        var i = 0;
        for (i = 0; i < ids.length; i++) {
          if (ids[i] && document.getElementById(ids[i])) return document.getElementById(ids[i]);
        }
        return null;
      }
      function allById(ids) {
        var out = [];
        var seen = {};
        var i = 0;
        var id = '';
        var el = null;
        for (i = 0; i < ids.length; i++) {
          id = String(ids[i] || '');
          if (!id || seen[id]) continue;
          seen[id] = true;
          el = document.getElementById(id);
          if (el) out.push(el);
        }
        return out;
      }
      var kitsReg = firstById([prodRowRegId, 'ps-kits-regular', 'ps_kits_regular', 'ps_search_products_regular']);
      var kitsTri = firstById([prodRowTriId, 'ps-kits-tricoat', 'ps_kits_tricoat', 'ps_search_products_tricoat']);
      var productsReg = firstById(['ps_products_regular', 'ps-products-regular']);
      var productsTri = firstById(['ps_products_tricoat', 'ps-products-tricoat']);
      var unknownIds = prodRowUnknownIds.slice(0);
      unknownIds.push('unknown_color_products_collection');
      unknownIds.push('unknown-color-products-collection');
      return {
        reg: kitsReg,
        tri: kitsTri,
        kits: { reg: kitsReg, tri: kitsTri },
        products: { reg: productsReg, tri: productsTri },
        unknown: allById(unknownIds)
      };
    }

    function showProductRows(isTricoat) {
      if (!doRewriteProducts) return;
      var w = getWrappers();
      if (!w.reg && !w.tri && !w.unknown.length && !(w.products && (w.products.reg || w.products.tri))) return;
      setWrapperDisplay(w.reg, isTricoat ? 'none' : 'block');
      setWrapperDisplay(w.tri, isTricoat ? 'block' : 'none');
      if (w.products) {
        setWrapperDisplay(w.products.reg, isTricoat ? 'none' : 'block');
        setWrapperDisplay(w.products.tri, isTricoat ? 'block' : 'none');
      }
      setWrapperListDisplay(w.unknown, 'none');
    }

    function isCatalyzedSprayOnly(colorName) {
      return /\(catalyzed spray(?: can)? only\)/.test(String(colorName || '').toLowerCase());
    }

    var CATALYZED_ALLOWED_BASES = (function() {
      var out = {};
      var raw = 'spray';
      var parts = null;
      var i = 0;
      try {
        if (window.paintscratchTheme && window.paintscratchTheme.psCatalyzedAllowedBases) {
          raw = window.paintscratchTheme.psCatalyzedAllowedBases;
        }
      } catch(_e) {}
      parts = String(raw || 'spray').toLowerCase().split(',');
      for (i = 0; i < parts.length; i++) {
        var key = String(parts[i] || '').replace(/^\s+|\s+$/g, '');
        if (key) out[key] = true;
      }
      if (!out.spray) out.spray = true;
      return out;
    })();

    function isCatalyzedAllowedBase(base) {
      return !!(base && CATALYZED_ALLOWED_BASES[base]);
    }

    function getColorProductBaseMap() {
      var raw = null;
      if (getColorProductBaseMap.cache) return getColorProductBaseMap.cache;
      try {
        if (window.paintscratchTheme && paintscratchTheme.psColorProductBaseMap) {
          raw = paintscratchTheme.psColorProductBaseMap;
        }
      } catch(_e) {}
      if (raw && typeof raw === 'object') {
        getColorProductBaseMap.cache = raw;
        return raw;
      }
      getColorProductBaseMap.cache = {
        pen: 'pen',
        'pen-tricoat': 'pen',
        'touch-up-paint-pen': 'pen',
        bottle: 'bottle',
        'bottle-tricoat': 'bottle',
        'touch-up-paint-bottle-2oz': 'bottle',
        spray: 'spray',
        'spray-tricoat': 'spray',
        'touch-up-paint-spray-can': 'spray',
        pro: 'pro',
        'pro-tricoat': 'pro',
        'touch-up-paint-pro-gallon': 'pro',
        'kit-pen': 'pen',
        'kit-pen-tricoat': 'pen',
        'touch-up-paint-kit-pen': 'pen',
        'kit-bottle': 'bottle',
        'kit-bottle-tricoat': 'bottle',
        'touch-up-paint-kit-bottle': 'bottle',
        'kit-spray': 'spray',
        'kit-spray-tricoat': 'spray',
        'touch-up-paint-kit-spray': 'spray'
      };
      return getColorProductBaseMap.cache;
    }

    function extractProductLookupToken(raw) {
      var s = String(raw || '').toLowerCase();
      var parts = null;
      if (!s) return '';
      if (s.indexOf('shopify://products/') > -1) {
        return s.split('shopify://products/').pop().split('?')[0].split('#')[0].split('/')[0];
      }
      if (s.indexOf('/products/') > -1) {
        return s.split('/products/').pop().split('?')[0].split('#')[0].split('/')[0];
      }
      if (s.indexOf('/colors/') > -1) {
        parts = s.split('/colors/').pop().split('?')[0].split('#')[0].split('/');
        if (parts.length > 2) return parts[2];
      }
      s = s.split('?')[0].split('#')[0];
      s = s.replace(/^\/+/, '').replace(/\/+$/, '');
      if (s.indexOf('/') > -1) {
        parts = s.split('/');
        s = parts[parts.length - 1];
      }
      return s;
    }

    function normalizeProductBase(raw) {
      var token = extractProductLookupToken(raw);
      var map = getColorProductBaseMap();
      if (token && map[token]) return map[token];
      if (token === 'pen' || token === 'bottle' || token === 'spray' || token === 'pro') return token;
      return '';
    }

    function containsKitHint(raw) {
      var s = String(raw || '').toLowerCase();
      if (!s) return false;
      if (s === 'kit') return true;
      if (s.indexOf('kit-') === 0) return true;
      if (s.indexOf('-kit-') > -1) return true;
      if (/[-_]kit$/.test(s)) return true;
      if (s.indexOf('touch-up-paint-kit-') === 0) return true;
      return false;
    }

    function containsTricoHint(raw) {
      var s = String(raw || '').toLowerCase();
      if (!s) return false;
      if (s.indexOf('tricoat') > -1) return true;
      if (s.indexOf('-trico') > -1) return true;
      return false;
    }

    function buildKitSlug(base, isTricoat) {
      var b = String(base || '').toLowerCase();
      if (!b) return '';
      return 'kit-' + b;
    }

    function toKitColorUrl(srcUrl, base, isTricoat) {
      var str = String(srcUrl || '');
      var kitSlug = buildKitSlug(base, !!isTricoat);
      var m = null;
      if (!str || !kitSlug) return '';
      m = str.match(/(\/colors\/[^\/]+\/[^\/]+\/)([^\/\?#]+)\/?([\?#].*)?$/i);
      if (!m) return '';
      return m[1] + kitSlug + (m[3] || '');
    }

    function inferBaseFromHref(href) {
      var str = String(href || '');
      var token = '';
      var m = str.match(/\/products\/([^\/\?#]+)/i);
      if (m && m[1]) token = m[1];
      if (!token) {
        m = str.match(/\/colors\/[^\/]+\/[^\/]+\/([^\/\?#]+)/i);
        if (m && m[1]) token = m[1];
      }
      return token ? normalizeProductBase(token) : '';
    }

    function findProductRow(node) {
      var cur = node;
      var guard = 0;
      while (cur && guard++ < 10) {
        var cn = String(cur.className || '');
        if (cn.indexOf('image-with-text__grid') > -1) return cur;
        if (cn.indexOf('grid__item') > -1) return cur;
        cur = cur.parentNode;
      }
      return null;
    }

    function inferBaseFromRow(row) {
      var base = '';
      var form = null;
      var links = null;
      var i = 0;
      if (!row || !row.getAttribute) return '';
      base = normalizeProductBase(row.getAttribute('data-ps-base') || '');
      if (base) return base;
      form = row.querySelector ? row.querySelector('form[data-ps-product-base]') : null;
      if (form && form.getAttribute) {
        base = normalizeProductBase(form.getAttribute('data-ps-product-base') || '');
        if (base) return base;
      }
      links = row.querySelectorAll ? row.querySelectorAll('a') : [];
      for (i = 0; i < links.length; i++) {
        base = inferBaseFromHref(links[i].getAttribute ? links[i].getAttribute('href') : '');
        if (base) return base;
      }
      return '';
    }

    function setExactImage(imgEl, url) {
      if (!imgEl) return;
      imgEl.removeAttribute('srcset');
      imgEl.removeAttribute('sizes');
      try {
        var toRemove = [];
        for (var ai = 0; ai < imgEl.attributes.length; ai++) {
          if (/^data-/.test(imgEl.attributes[ai].name)) toRemove.push(imgEl.attributes[ai].name);
        }
        for (var ri = 0; ri < toRemove.length; ri++) imgEl.removeAttribute(toRemove[ri]);
      } catch(_e) {}
      try {
        var cls = (imgEl.className || '').split(/\s+/);
        var keep = [];
        for (var ci = 0; ci < cls.length; ci++) {
          if (!/^(lazyload|lazyloaded|lazyautosizes|js-lazy-image)$/i.test(cls[ci])) keep.push(cls[ci]);
        }
        imgEl.className = keep.join(' ');
      } catch(_e) {}
      var pic = imgEl.parentNode && imgEl.parentNode.nodeName && imgEl.parentNode.nodeName.toLowerCase() === 'picture' ? imgEl.parentNode : null;
      if (pic) {
        var sources = pic.querySelectorAll('source');
        for (var si = 0; si < sources.length; si++) sources[si].removeAttribute('srcset');
      }
      imgEl.setAttribute('src', url);
      imgEl.setAttribute('srcset', url);
      imgEl.setAttribute('loading', 'eager');
      imgEl.removeAttribute('width');
      imgEl.removeAttribute('height');
    }

    function findInteractiveInRow(target, row) {
      var cur = target;
      while (cur && cur !== row) {
        var tag = String(cur.tagName || '').toLowerCase();
        if (tag === 'a' || tag === 'button') return cur;
        if (cur.getAttribute && cur.getAttribute('role') === 'button') return cur;
        if (cur.getAttribute && String(cur.getAttribute('type') || '').toLowerCase() === 'submit') return cur;
        cur = cur.parentNode;
      }
      return null;
    }

    function ensureRowInteractivityHandler(row) {
      if (!row || !row.addEventListener) return;
      if (row.getAttribute('data-ps-catalyzed-card-handler') === '1') return;
      row.addEventListener('click', function(evt) {
        var blocked = row.getAttribute('data-ps-catalyzed-card-disabled') === '1';
        if (!blocked || !evt || !evt.target) return;
        var interactive = findInteractiveInRow(evt.target, row);
        if (!interactive) return;
        if (interactive.getAttribute && interactive.getAttribute('data-ps-catalyzed-allow-nav') === '1') return;
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopPropagation) evt.stopPropagation();
        if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      }, true);
      row.addEventListener('keydown', function(evt) {
        var blocked = row.getAttribute('data-ps-catalyzed-card-disabled') === '1';
        var key = String((evt && evt.key) || '');
        if (!blocked || !evt || !evt.target) return;
        if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') return;
        var interactive = findInteractiveInRow(evt.target, row);
        if (!interactive) return;
        if (interactive.getAttribute && interactive.getAttribute('data-ps-catalyzed-allow-nav') === '1') return;
        if (evt.preventDefault) evt.preventDefault();
        if (evt.stopPropagation) evt.stopPropagation();
        if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      }, true);
      row.setAttribute('data-ps-catalyzed-card-handler', '1');
    }

    function setRowLinkState(link, blocked) {
      var href = '';
      if (!link || !link.getAttribute) return;
      if (link.getAttribute('data-ps-catalyzed-allow-nav') === '1') return;
      if (blocked) {
        href = String(link.getAttribute('href') || '');
        if (href && !link.getAttribute('data-ps-original-href')) link.setAttribute('data-ps-original-href', href);
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
        link.setAttribute('data-ps-catalyzed-link-disabled', '1');
        return;
      }
      if (link.getAttribute('data-ps-catalyzed-link-disabled') !== '1') return;
      href = String(link.getAttribute('data-ps-original-href') || '');
      if (href) link.setAttribute('href', href);
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
      link.removeAttribute('data-ps-catalyzed-link-disabled');
    }

    function setRowButtonState(btn, blocked) {
      var span = null;
      var origDisabled = false;
      var origLabel = '';
      if (!btn || !btn.getAttribute) return;
      span = btn.querySelector ? btn.querySelector('span') : null;
      if (!btn.getAttribute('data-ps-original-disabled')) {
        btn.setAttribute('data-ps-original-disabled', btn.disabled ? '1' : '0');
      }
      if (!btn.getAttribute('data-ps-original-label')) {
        btn.setAttribute('data-ps-original-label', span ? String(span.textContent || '') : String(btn.textContent || ''));
      }
      if (blocked) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-ps-catalyzed-blocked', '1');
        if (span) span.textContent = 'Spray Can Only';
        else btn.textContent = 'Spray Can Only';
        return;
      }
      origDisabled = btn.getAttribute('data-ps-original-disabled') === '1';
      origLabel = String(btn.getAttribute('data-ps-original-label') || '');
      btn.disabled = origDisabled;
      btn.setAttribute('aria-disabled', origDisabled ? 'true' : 'false');
      btn.removeAttribute('data-ps-catalyzed-blocked');
      if (origLabel) {
        if (span) span.textContent = origLabel;
        else btn.textContent = origLabel;
      }
    }

    function setCatalyzedRowState(row, blocked) {
      if (!row || !row.querySelectorAll) return;
      row.style.display = blocked ? 'none' : '';
      row.setAttribute('data-ps-catalyzed-card-disabled', blocked ? '1' : '0');
      row.setAttribute('data-ps-catalyzed-warning', blocked ? '1' : '0');
      ensureRowInteractivityHandler(row);

      var links = row.querySelectorAll('a');
      for (var li = 0; li < links.length; li++) {
        setRowLinkState(links[li], blocked);
      }

      var forms = row.querySelectorAll('form[data-type="add-to-cart-form"]');
      for (var fi = 0; fi < forms.length; fi++) {
        var form = forms[fi];
        var btn =
          form.querySelector('[data-ps-atc-button="true"], button[name="add"][type="submit"], button[type="submit"]') ||
          null;
        form.setAttribute('data-ps-catalyzed-quickadd-disabled', blocked ? '1' : '0');
        if (btn) setRowButtonState(btn, blocked);
      }

      var fallbackButtons = row.querySelectorAll('button.quick-add__submit');
      for (var bi = 0; bi < fallbackButtons.length; bi++) {
        setRowButtonState(fallbackButtons[bi], blocked);
      }

      var note = row.querySelector('[data-ps-catalyzed-note="1"]');
      if (!blocked && note && note.parentNode) note.parentNode.removeChild(note);
    }

    function rewriteProductLinks(container, urlMap, imgMap, nameMap, textData, isTricoat) {
      if (!container || !urlMap || !doRewriteProducts) return;

      var isCatalyzedOnly = isCatalyzedSprayOnly(textData && textData.color);
      var isTricoColor = !!isTricoat;
      var containerId = String((container && container.id) || '').toLowerCase();
      var featuredId = container && container.getAttribute ? String(container.getAttribute('data-ps-featured-id') || '').toLowerCase() : '';
      var forceKitCards = false;
      var passId = String(new Date().getTime());
      var links = container.querySelectorAll('a');
      var i = 0;

      if (containerId.indexOf('ps_kits_') === 0 || containerId.indexOf('ps-kits-') === 0) forceKitCards = true;
      if (!forceKitCards && (featuredId.indexOf('ps_kits_') === 0 || featuredId.indexOf('ps-kits-') === 0)) forceKitCards = true;
      if (!links || !links.length) return;

      for (i = 0; i < links.length; i++) {
        var a = links[i];
        var base = (a.getAttribute('data-ps-base') || '').toLowerCase();
        var currentHref = '';
        var isKitCard = false;
        var targetHref = '';
        var isTricoFromHref = false;
        var isTricoFromMap = false;
        var node = a;
        var row = null;
        var guard = 0;
        var rowBase = '';
        var imgUrl = '';
        var nameVal = '';

        if (!base) {
          currentHref = a.getAttribute('href') || '';
          base = inferBaseFromHref(currentHref);
          if (base) a.setAttribute('data-ps-base', base);
        }

        if (!base) {
          var baseForm = null;
          var searchNode = a;
          var formGuard = 0;
          while (searchNode && formGuard++ < 8) {
            if (searchNode.querySelector) {
              baseForm = searchNode.querySelector('form[data-ps-product-base]');
              if (baseForm) break;
            }
            searchNode = searchNode.parentNode;
          }
          if (baseForm && baseForm.getAttribute) {
            base = normalizeProductBase(baseForm.getAttribute('data-ps-product-base') || '');
            if (base) a.setAttribute('data-ps-base', base);
          }
        }

        while (node && guard++ < 8) {
          row = findProductRow(node);
          if (row) break;
          node = node.parentNode;
        }

        if (row && row.getAttribute('data-ps-catalyzed-pass') !== passId) {
          rowBase = normalizeProductBase(row.getAttribute('data-ps-base') || base || inferBaseFromRow(row));
          if (rowBase) row.setAttribute('data-ps-base', rowBase);
          setCatalyzedRowState(row, !!(isCatalyzedOnly && rowBase && !isCatalyzedAllowedBase(rowBase)));
          row.setAttribute('data-ps-catalyzed-pass', passId);
        }

        if (isCatalyzedOnly && base && !isCatalyzedAllowedBase(base)) continue;
        if (!base) continue;

        currentHref = String(a.getAttribute('href') || '');
        isKitCard = forceKitCards || containsKitHint(currentHref);
        isTricoFromHref = containsTricoHint(currentHref);
        isTricoFromMap = containsTricoHint(urlMap[base] || '');

        if (isKitCard) {
          targetHref = toKitColorUrl(urlMap[base] || currentHref, base, !!(isTricoColor || isTricoFromHref || isTricoFromMap));
          if (targetHref) a.setAttribute('href', targetHref);
        } else if (urlMap[base]) {
          targetHref = normalizeScopedColorHubUrl(urlMap[base], sameHostColorHub) || urlMap[base] || '';
          a.setAttribute('href', targetHref);
        } else {
          continue;
        }

        if (textData && textData.make && textData.color && textData.code && a.parentNode) {
          var matchInfo = a.parentNode.querySelector('.ps-match-info');
          if (matchInfo) {
            matchInfo.textContent = 'Matched to ' + textData.make + ' ' + textData.color + ' (' + textData.code + ')';
            matchInfo.style.display = 'block';
          }
        }

        if (!row || isKitCard) continue;
        imgUrl = (imgMap && imgMap[base]) || '';
        if (imgUrl) setExactImage(row.querySelector('.image-with-text__media img'), imgUrl);
        nameVal = (nameMap && nameMap[base]) || '';
        if (nameVal) {
          var h2 = row.querySelector('.image-with-text__heading');
          if (h2) h2.textContent = nameVal;
        }
      }
    }

    function syncProductRows(isTricoat, urlMap, imgMap, nameMap, textData) {
      showProductRows(!!isTricoat);
      var w = getWrappers();
      var visibleKit = isTricoat ? w.tri : w.reg;
      var visibleProducts = w.products ? (isTricoat ? w.products.tri : w.products.reg) : null;
      if (visibleKit) rewriteProductLinks(visibleKit, urlMap, imgMap, nameMap, textData, !!isTricoat);
      if (visibleProducts) rewriteProductLinks(visibleProducts, urlMap, imgMap, nameMap, textData, !!isTricoat);
    }

    function cancelSelectedColorHydration() {
      hydrationRequestSeq += 1;
      if (selectedFormHydrationActive) {
        selectedFormHydrationActive = false;
        bumpEditRestoreToken();
      }
      if (hydrationXhr && hydrationXhr.readyState !== 4) {
        try { hydrationXhr.abort(); } catch(_e) {}
      }
      hydrationXhr = null;
    }

    function selectedFormNeedsHydration(selection) {
      var selectedMake = trimValue(selection && selection.make).toLowerCase();
      var selectedYear = trimValue(selection && selection.year);
      var selectedModel = trimValue(selection && selection.model).toLowerCase();
      if (!makeSel || !selectedMake) return false;
      if (trimValue(makeSel.value).toLowerCase() !== selectedMake) return true;
      if (yearSel && trimValue(yearSel.value) !== selectedYear) return true;
      if (modelSel && trimValue(modelSel.value).toLowerCase() !== selectedModel) return true;
      return false;
    }

    // Fetch full make+color details and render B-state.
    function fetchAndRenderMakeColor(makeSlug, colSlug) {
      var requestId;
      if (!makeSlug || !colSlug) return;
      cancelSelectedColorHydration();
      requestId = hydrationRequestSeq;
      var url = apiBase() + 'shopify/cars/' + encodeURIComponent(makeSlug) + '/' + encodeURIComponent(colSlug);
      var xhr = new XMLHttpRequest();
      hydrationXhr = xhr;
      xhr.open('GET', url, true);
      xhr.onreadystatechange = function() {
        if (xhr.readyState !== 4) return;
        if (requestId !== hydrationRequestSeq) return;
        if (hydrationXhr === xhr) hydrationXhr = null;
        var data = null, isValid = false;
        var cc = readCookie() || {};
        var maps = null;
        var isTri = false;
        var textData = null;
        var ms = '';
        var cs = '';
        var detail = null;
        var customHeader = null;
        var selectedMakeName = '';
        var cookieMatchesSelectedMake = false;
        var reconciliationToken = 0;
        if (xhr.status >= 200 && xhr.status < 300) {
          try { data = JSON.parse(xhr.responseText || '{}'); } catch(_e) {}
          if (data && data.valid !== false) isValid = true;
        }
        if (!isValid) {
          try {
            writeCookie({ make: cc.make || '', year: cc.year || '', model: cc.model || '', q: cc.q || '',
              color: '', code: '', hex: '', color_slug: '', valid: false, shopify_page: '', products: [] });
          } catch(_e2) {}
          if (searchUI) searchUI.setAttribute('style', 'display: block !important');
          if (picked) picked.style.display = 'none';
          customHeader = document.getElementById(selectedHeaderId);
          if (customHeader) customHeader.style.setProperty('display', 'none', 'important');
          try { document.dispatchEvent(new CustomEvent('ps:color:invalid')); } catch(_e3) {}
          syncVisibility();
          return;
        }

        maps = buildMapsFromMakeColor(data);
        isTri = !!data.is_tricoat;
        selectedMakeName = data.make || cc.make || '';
        cookieMatchesSelectedMake = !!(
          trimValue(cc.make) &&
          trimValue(cc.make).toLowerCase() === trimValue(selectedMakeName).toLowerCase()
        );
        textData = { make: selectedMakeName, color: data.color || cc.color || '', code: data.code || cc.code || '' };
        syncProductRows(isTri, maps.url, maps.img, maps.name, textData);

        ms = data.make_slug || makeSlug || '';
        cs = data.color_slug || colSlug || '';
        detail = {
          make: selectedMakeName, color: data.color || cc.color || '',
          code: data.code || cc.code || '', hex: normHex(data.hex || cc.hex || ''),
          year: cookieMatchesSelectedMake ? (cc.year || '') : '',
          model: cookieMatchesSelectedMake ? (cc.model || '') : '',
          q: cookieMatchesSelectedMake ? (cc.q || '') : '',
          years: Array.isArray(data.years) ? data.years.slice(0) : [],
          models: Array.isArray(data.models) ? data.models.slice(0) : [],
          factory_codes: Array.isArray(data.factory_codes) ? data.factory_codes.slice(0) : [],
          isGuaranteed: data.isGuaranteed !== false,
          is_tricoat: isTri,
          make_slug: ms, color_slug: cs,
          shopify_page: buildColorHubUrl({
            shopify_page: data.shopify_page || '',
            make_slug: ms,
            color_slug: cs,
            make: data.make || cc.make || '',
            color: data.color || cc.color || '',
            code: data.code || cc.code || ''
          }, cc),
          products: []
        };
        if (data.products && typeof data.products === 'object') {
          if (Array.isArray(data.products)) {
            for (var i = 0; i < data.products.length; i++) {
              var pr = data.products[i] || {};
              detail.products.push({ type_slug: normalizeProductBase(pr.slug || pr.type_slug || ''),
                url: pr.url || '', imageUrl: pr.image_thumb || pr.imageUrl || (pr.images && pr.images[0]) || '', price: pr.price || '' });
            }
          } else {
            for (var k in data.products) {
              if (!data.products.hasOwnProperty(k)) continue;
              var e = data.products[k] || {};
              detail.products.push({ type_slug: normalizeProductBase(k),
                url: e.url || '', imageUrl: e.image_thumb || e.imageUrl || (e.images && e.images[0]) || '', price: e.price || '' });
            }
          }
        }
        function finishSelectedColorHydration() {
          if (requestId !== hydrationRequestSeq) return;
          makesLoadRequestSeq += 1;
          yearsLoadRequestSeq += 1;
          modelsLoadRequestSeq += 1;
          selectedFormHydrationActive = false;
          try { document.dispatchEvent(new CustomEvent('ps:color:validated', { detail: detail })); } catch(_e) {}
          if (onColorValidated) onColorValidated(detail);
          renderPickedSummary(detail);
          if (grid) grid.style.display = 'none';
          if (pager) pager.style.display = 'none';
          setPagerWrapDisplay(false);
          wireChangeButton(detail);
        }
        if (selectedFormNeedsHydration(detail)) {
          clearHistorySearchSnapshot();
          selectedFormHydrationActive = true;
          reconciliationToken = bumpEditRestoreToken();
          loadMakes(finishSelectedColorHydration, true, detail, reconciliationToken);
          return;
        }
        finishSelectedColorHydration();
      };
      xhr.send(null);
    }

    // ==========================================================
    // TEXT REPLACEMENT (homepage: "vehicle" -> make name)
    // ==========================================================

    var _lastReplaceMake = '';
    function formatVehicleCopyMake(makeName) {
      var value = String(makeName || '').trim();
      if (!value) return '';
      value = value.replace(/(^|[\s-])(amc|amg|bmc|bmw|gmc|drv|mg|ral|rv|seat)(?=$|[\s-])/gi, function(match, prefix, token) {
        return prefix + token.toUpperCase();
      });
      return value.replace(/(^|[\s-])([a-z])/g, function(match, prefix, letter) {
        return prefix + letter.toUpperCase();
      });
    }

    function updateVehicleCopy(makeName, colorName) {
      if (!doReplaceText) return;
      try {
        var m = formatVehicleCopyMake(makeName);
        var c = String(colorName || '').trim();
        if (!m) return;
        var prev = _lastReplaceMake;
        function escReg(s) { return String(s || '').replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); }
        var selectors = [
          '.rich-text__heading', '.rich-text__caption', '.rich-text__text p',
          '.image-with-text__heading', '.image-with-text__text',
          '.banner__heading', '.banner__text',
          '.section-header__title', '.section-header__subtitle', '.h1', '.h2'
        ];
        var nodes = document.querySelectorAll(selectors.join(','));
        for (var i = 0; i < nodes.length; i++) {
          var nodeEl = nodes[i];
          if (root && nodeEl.closest && nodeEl.closest('#' + (root.id || '_none_'))) continue;
          var origHtml = nodeEl.getAttribute('data-ps-orig-html');
          if (!origHtml) {
            origHtml = nodeEl.innerHTML || '';
            var plain = nodeEl.textContent || '';
            var isRelevant = /\bvehicle\b/i.test(plain);
            if (!isRelevant) {
              for (var r = 0; r < textReplaceList.length; r++) {
                if (new RegExp('\\b' + escReg(textReplaceList[r]) + '\\b', 'i').test(plain)) { isRelevant = true; break; }
              }
            }
            if (!isRelevant) {
              for (var a = 0; a < textAppendList.length; a++) {
                if (new RegExp('\\b' + escReg(textAppendList[a]) + '\\b', 'i').test(plain)) { isRelevant = true; break; }
              }
            }
            if (!isRelevant) continue;
            nodeEl.setAttribute('data-ps-orig-html', origHtml);
          }
          function applyRewrites(s) {
            var t = String(s == null ? '' : s);
            t = t.replace(/\bvehicle's\b/gi, m + "'s");
            t = t.replace(/\bvehicle\b/gi, m);
            if (c) {
              for (var x = 0; x < textReplaceList.length; x++) {
                t = t.replace(new RegExp('\\b' + escReg(textReplaceList[x]) + '\\b', 'gi'), c);
              }
              for (var y = 0; y < textAppendList.length; y++) {
                t = t.replace(new RegExp('\\b' + escReg(textAppendList[y]) + '\\b', 'gi'), '$& ' + c);
              }
            }
            if (prev) {
              t = t.replace(new RegExp('\\b' + escReg(prev + "'s") + '\\b', 'gi'), m + "'s");
              t = t.replace(new RegExp('\\b' + escReg(prev) + '\\b', 'gi'), m);
            }
            return t;
          }
          try {
            var wrap = document.createElement('div');
            wrap.innerHTML = String(origHtml || '');
            if (document.createTreeWalker && window.NodeFilter) {
              var walker = document.createTreeWalker(wrap, NodeFilter.SHOW_TEXT, null, false);
              var tn = walker.nextNode();
              while (tn) { tn.nodeValue = applyRewrites(tn.nodeValue); tn = walker.nextNode(); }
            } else {
              (function walk(n) {
                if (!n) return;
                if (n.nodeType === 3) { n.nodeValue = applyRewrites(n.nodeValue); return; }
                var ch = n.firstChild;
                while (ch) { walk(ch); ch = ch.nextSibling; }
              })(wrap);
            }
            nodeEl.innerHTML = wrap.innerHTML;
          } catch(_eInner) {
            nodeEl.textContent = applyRewrites(nodeEl.textContent || '');
          }
        }
        _lastReplaceMake = m;
      } catch(_e) {}
    }

    function replaceSelectionTokenString(value, makeName, colorName, codeValue) {
      var out = String(value == null ? '' : value);
      out = out.replace(/\[MAKE\]/g, String(makeName || ''));
      out = out.replace(/\[COLOR\]/g, String(colorName || ''));
      out = out.replace(/\[CODE\]/g, String(codeValue || ''));
      return out;
    }

    function toCtaAriaLabel(text) {
      return String(text || '').replace(/>/g, '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    }

    function applyStructuredSelectionCopy(data) {
      var wrappers = null;
      var i = 0;
      var hasSelection = false;
      var mk = String((data && data.make) || '').trim();
      var cl = String((data && data.color) || '').trim();
      var cd = String((data && data.code) || '').trim();
      var wrapper = null;
      var titleNode = null;
      var descNode = null;
      var ctaNode = null;
      var defaultTitle = '';
      var personalizedTitle = '';
      var defaultDesc = '';
      var personalizedDesc = '';
      var defaultCta = '';
      var personalizedCta = '';
      var nextTitle = '';
      var nextDesc = '';
      var nextCta = '';

      wrappers = document.querySelectorAll('[data-ps-selection-copy="1"]');
      if (!wrappers || !wrappers.length) return;

      hasSelection = !!(mk && (cl || cd));
      for (i = 0; i < wrappers.length; i++) {
        wrapper = wrappers[i];
        if (!wrapper || !wrapper.getAttribute) continue;

        titleNode = wrapper.querySelector('[data-ps-selection-copy-target="title"]');
        descNode = wrapper.querySelector('[data-ps-selection-copy-target="description"]');
        ctaNode = wrapper.querySelector('[data-ps-selection-copy-target="view-all"]');

        defaultTitle = String(wrapper.getAttribute('data-ps-selection-default-title') || '');
        personalizedTitle = String(wrapper.getAttribute('data-ps-selection-personalized-title') || defaultTitle);
        defaultDesc = String(wrapper.getAttribute('data-ps-selection-default-description') || '');
        personalizedDesc = String(wrapper.getAttribute('data-ps-selection-personalized-description') || defaultDesc);
        defaultCta = String(wrapper.getAttribute('data-ps-selection-default-view-all') || '');
        personalizedCta = String(wrapper.getAttribute('data-ps-selection-personalized-view-all') || defaultCta);

        nextTitle = hasSelection ? replaceSelectionTokenString(personalizedTitle, mk, cl, cd) : defaultTitle;
        nextDesc = hasSelection ? replaceSelectionTokenString(personalizedDesc, mk, cl, cd) : defaultDesc;
        nextCta = hasSelection ? replaceSelectionTokenString(personalizedCta, mk, cl, cd) : defaultCta;

        if (titleNode && titleNode.innerHTML !== nextTitle) titleNode.innerHTML = nextTitle;
        if (descNode && descNode.innerHTML !== nextDesc) descNode.innerHTML = nextDesc;
        if (ctaNode) {
          if (ctaNode.textContent !== nextCta) ctaNode.textContent = nextCta;
          ctaNode.setAttribute('aria-label', toCtaAriaLabel(nextCta));
        }
      }
    }

    function applySelectionTokens(data) {
      if (!replaceSelectionTokens) return;
      try {
        var mk = String((data && data.make) || '').trim();
        var cl = String((data && data.color) || '').trim();
        var cd = String((data && data.code) || '').trim();
        var nodes = document.querySelectorAll('*');
        var i = 0;

        for (i = 0; i < nodes.length; i++) {
          var el = nodes[i];
          var orig = '';
          var txt = '';
          if (!el || !el.getAttribute || !el.setAttribute) continue;
          if (el.children && el.children.length) continue;
          if (root && el.closest && el.closest('#' + (root.id || '_none_'))) continue;

          orig = String(el.getAttribute('data-ps-token-orig') || '');
          if (!orig) {
            txt = String(el.textContent || '');
            if (txt.indexOf('[MAKE]') < 0 && txt.indexOf('[COLOR]') < 0 && txt.indexOf('[CODE]') < 0) continue;
            orig = txt;
            el.setAttribute('data-ps-token-orig', orig);
          }

          txt = String(orig || '');
          txt = txt.replace(/\[MAKE\]/g, mk).replace(/\[COLOR\]/g, cl).replace(/\[CODE\]/g, cd);
          if (String(el.textContent || '') !== txt) el.textContent = txt;
        }
      } catch(_e) {}
    }

    function normalizeColorHubUrl(url) {
      var str = String(url || '').trim();
      var abs = null;
      var m = null;
      if (!str) return '';
      if (str.indexOf('/colors/') === 0) {
        return /\/$/.test(str) ? str : str + '/';
      }
      if (/^https?:\/\//i.test(str)) {
        abs = document.createElement('a');
        abs.href = str;
        if (String(abs.pathname || '').indexOf('/colors/') === 0) {
          if (sameHostColorHub) return /\/$/.test(abs.pathname) ? abs.pathname : abs.pathname + '/';
          return abs.href;
        }
      }
      m = str.match(/(\/colors\/[^\/]+\/[^\/]+)\/?/i);
      if (!m || !m[1]) return '';
      return m[1] + '/';
    }

    function buildColorHubUrl(data, cookieData) {
      var d = data || {};
      var cc = cookieData || {};
      var fromShopifyPage = normalizeColorHubUrl(d.shopify_page || cc.shopify_page || '');
      var ms = '';
      var cs = '';
      if (fromShopifyPage) return fromShopifyPage;

      ms = String(d.make_slug || cc.make_slug || '').toLowerCase();
      if (!ms) ms = toSlug(d.make || cc.make || '');

      cs = String(d.color_slug || cc.color_slug || '').toLowerCase();
      if (!cs) cs = buildColorSlug(d.color || cc.color || '', d.code || cc.code || '');

      if (!ms || !cs) return '';
      return '/colors/' + ms + '/' + cs + '/';
    }

    function applyColorHubLinks(data) {
      try {
        var cc = readCookie() || {};
        var href = buildColorHubUrl(data || {}, cc);
        var links = null;
        var i = 0;
        if (!href) return;
        links = document.querySelectorAll('a[data-ps-color-hub-link="1"]');
        if (!links || !links.length) return;
        for (i = 0; i < links.length; i++) {
          if (String(links[i].getAttribute('href') || '') !== href) {
            links[i].setAttribute('href', href);
          }
        }
      } catch(_e) {}
    }

    function ensureKitHubCta(wrapper) {
      var collectionNode = null;
      var ctaWrap = null;
      var ctaLink = null;
      var wrapClass = '';
      var linkClass = '';
      var selectionEnabled = false;
      var defaultLabel = '';
      var personalizedLabel = '';
      var txt = '';
      if (!wrapper || !wrapper.querySelector) return;

      collectionNode = wrapper.querySelector('.collection');
      if (!collectionNode) return;
      selectionEnabled = !!(wrapper.getAttribute && wrapper.getAttribute('data-ps-selection-copy') === '1');
      defaultLabel = selectionEnabled
        ? String(wrapper.getAttribute('data-ps-selection-default-view-all') || 'Shop all products')
        : 'Shop all products';
      personalizedLabel = selectionEnabled
        ? String(wrapper.getAttribute('data-ps-selection-personalized-view-all') || 'Shop all products in [CODE] - [COLOR] >')
        : 'Shop all products in [CODE] - [COLOR] >';

      ctaLink = collectionNode.querySelector('.collection__view-all a');
      if (ctaLink) {
        ctaWrap = ctaLink.parentNode;
        if (ctaWrap && ctaWrap.getAttribute) {
          wrapClass = String(ctaWrap.className || '');
          if (wrapClass.indexOf('ps-kit-hub-cta') < 0) {
            ctaWrap.className = (wrapClass ? wrapClass + ' ' : '') + 'ps-kit-hub-cta';
          }
        }

        linkClass = String(ctaLink.className || '');
        if (linkClass.indexOf('ps-kit-hub-cta__link') < 0) {
          ctaLink.className = (linkClass ? linkClass + ' ' : '') + 'ps-kit-hub-cta__link';
        }

        ctaLink.setAttribute('data-ps-color-hub-link', '1');
        if (String(ctaLink.getAttribute('href') || '') === '') ctaLink.setAttribute('href', '/colors/');
        txt = String(ctaLink.textContent || '').replace(/^\s+|\s+$/g, '');
        if (selectionEnabled) {
          ctaLink.setAttribute('data-ps-selection-copy-target', 'view-all');
          if (!txt) txt = defaultLabel;
        } else {
          if (txt.indexOf('[COLOR]') < 0 && txt.indexOf('Shop all products in') !== 0) {
            txt = personalizedLabel;
          } else if (txt.indexOf('>') < 0) {
            txt = txt + ' >';
          }
        }
        ctaLink.textContent = txt;
        ctaLink.setAttribute('aria-label', toCtaAriaLabel(txt));
        return;
      }
    }

    function ensureKitHubCtas() {
      if (!ensureKitHubCtasEnabled) return;
      var w = getWrappers();
      if (w && w.kits && w.kits.reg) ensureKitHubCta(w.kits.reg);
      if (w && w.kits && w.kits.tri) ensureKitHubCta(w.kits.tri);
    }

    // ==========================================================
    // COOKIE CONTEXT + VISIBILITY + HYDRATION
    // ==========================================================

    function writeSearchContextCookie(extra, useFallback) {
      try {
        var payload = {};
        var current = (useFallback && readCookie()) || {};
        function hasVal(v) { return v && String(v).trim() !== ''; }
        var mVal = makeSel && makeSel.value ? String(makeSel.value).trim() : '';
        if (hasVal(mVal)) { payload.make = mVal; payload.make_slug = toSlug(mVal); }
        else if (useFallback && current.make) {
          payload.make = current.make;
          if (current.make_slug) payload.make_slug = current.make_slug;
        }
        var yVal = yearSel && yearSel.value ? String(yearSel.value).trim() : '';
        if (hasVal(yVal)) payload.year = yVal;
        else if (useFallback && current.year) payload.year = String(current.year).trim();
        var modelVal = modelSel && modelSel.value ? String(modelSel.value).trim() : '';
        if (hasVal(modelVal)) payload.model = modelVal;
        else if (useFallback && current.model) payload.model = current.model;
        var qVal = String((searchQ && searchQ.value) || '').replace(/^\s+|\s+$/g, '');
        if (isSensitiveSearchValue(qVal) || (useFallback && isSensitiveSearchValue(current.q))) payload.q = '';
        else if (hasVal(qVal)) payload.q = qVal;
        else if (useFallback && current.q) payload.q = current.q;
        payload.color = ''; payload.code = ''; payload.hex = '';
        payload.color_slug = ''; payload.image = ''; payload.valid = false;
        payload.shopify_page = ''; payload.products = [];
        if (extra && typeof extra === 'object') {
          for (var k in extra) { if (extra.hasOwnProperty(k)) payload[k] = extra[k]; }
        }
        if (isSensitiveSearchValue(payload.q)) payload.q = '';
        writeCookie(payload);
      } catch(_e) {}
    }

    function notifyColorCleared() {
      try {
        document.dispatchEvent(new CustomEvent('ps:color:cleared'));
      } catch(_e1) {
        try {
          if (typeof document.createEvent === 'function') {
            var ev = document.createEvent('Event');
            ev.initEvent('ps:color:cleared', true, true);
            document.dispatchEvent(ev);
          } else {
            syncVisibility();
          }
        } catch(_e2) {
          syncVisibility();
        }
      }
    }

    function beginEdit(activeSelectionSource) {
      var hasActiveSelectionSource = !!(activeSelectionSource && activeSelectionSource.make);
      var shouldReloadSelectedForm = selectedFormHydrationActive || (
        hasActiveSelectionSource && selectedFormNeedsHydration(activeSelectionSource)
      );
      var editToken = 0;
      cancelSelectedColorHydration();
      stopSubmittedSearchRestore();
      clearSubmittedSearchScope();
      editToken = bumpEditRestoreToken();
      writeEditRestoreScope({
        make: hasActiveSelectionSource ? activeSelectionSource.make : ((makeSel && makeSel.value) || state.make || ''),
        year: hasActiveSelectionSource ? (activeSelectionSource.year || '') : ((yearSel && yearSel.value) || state.year || ''),
        model: hasActiveSelectionSource ? (activeSelectionSource.model || '') : ((modelSel && modelSel.value) || state.model || ''),
        q: hasActiveSelectionSource ? (activeSelectionSource.q || '') : ((searchQ && searchQ.value) || state.q || '')
      });
      try { writeSearchContextCookie({ ps_from_search: false }, true); } catch(_e) {}
      notifyColorCleared();
      if (picked) picked.style.display = 'none';
      if (searchUI) searchUI.setAttribute('style', 'display: block !important');
      if (grid) grid.style.display = '';
      setPagerWrapDisplay(true);
      try { syncVisibility(); } catch(_e2) {}
      if (hasActiveSelectionSource && shouldReloadSelectedForm) {
        clearHistorySearchSnapshot();
        loadMakes(null, true, activeSelectionSource, editToken);
      } else if (shouldReloadSelectedForm) loadMakes();
    }

    function syncVisibility() {
      if (!showPickedSummary) return;
      var historySnapshot = getHistorySearchSnapshot();
      var cc = readCookie() || {};
      var has = hasActiveColorSelection(cc);
      var customHeader = document.getElementById(selectedHeaderId);
      var w = null;
      if (historySnapshot) {
        if (searchUI) searchUI.setAttribute('style', 'display: block !important');
        setPagerWrapDisplay(true);
        if (picked) picked.style.display = 'none';
        if (customHeader) customHeader.style.setProperty('display', 'none', 'important');
        try {
          w = getWrappers();
          if (w) {
            setWrapperDisplay(w.reg, 'none');
            setWrapperDisplay(w.tri, 'none');
            if (w.products) {
              setWrapperDisplay(w.products.reg, 'none');
              setWrapperDisplay(w.products.tri, 'none');
            }
            setWrapperListDisplay(w.unknown, 'block');
          }
        } catch(_e1) {}
        ensureKitHubCtas();
        applyStructuredSelectionCopy(null);
        return;
      }
      if (has) {
        if (searchUI) searchUI.style.display = 'none';
        setPagerWrapDisplay(false);
        if (customHeader) customHeader.style.setProperty('display', 'block', 'important');
        try {
          w = getWrappers();
          if (isHomeTemplate && w) {
            setWrapperDisplay(w.reg, 'block');
            setWrapperDisplay(w.tri, 'none');
            if (w.products) {
              setWrapperDisplay(w.products.reg, 'block');
              setWrapperDisplay(w.products.tri, 'none');
            }
            setWrapperListDisplay(w.unknown, 'none');
          } else if (w) {
            setWrapperListDisplay(w.unknown, 'none');
          }
        } catch(_e2) {}
      } else {
        if (searchUI) searchUI.setAttribute('style', 'display: block !important');
        setPagerWrapDisplay(true);
        if (picked) picked.style.display = 'none';
        if (customHeader) customHeader.style.setProperty('display', 'none', 'important');
        try {
          w = getWrappers();
          if (w) {
            setWrapperDisplay(w.reg, 'none');
            setWrapperDisplay(w.tri, 'none');
            if (w.products) {
              setWrapperDisplay(w.products.reg, 'none');
              setWrapperDisplay(w.products.tri, 'none');
            }
            setWrapperListDisplay(w.unknown, 'block');
          }
        } catch(_e3) {}
      }
      ensureKitHubCtas();
      if (has) applyStructuredSelectionCopy({ make: cc.make, color: cc.color, code: cc.code });
      else applyStructuredSelectionCopy(null);
      if (cc && cc.make) {
        updateVehicleCopy(cc.make, cc.color);
        if (has) applySelectionTokens({ make: cc.make, color: cc.color, code: cc.code });
        applyColorHubLinks(cc);
      }
    }

    function autoHydrateFromCookie(retries) {
      if (_autoHydrated) return;
      if (hasHistorySearchSnapshot()) return;
      var cc = readCookie() || {};
      var ms = (cc.make_slug ? String(cc.make_slug).toLowerCase() : '') || toSlug(cc.make);
      var cs = (cc.color_slug ? String(cc.color_slug).toLowerCase() : '') || buildColorSlug(cc.color, cc.code);
      var wrappers = getWrappers();
      var hasWrappers = !!(
        wrappers && (
          wrappers.reg ||
          wrappers.tri ||
          (wrappers.products && (wrappers.products.reg || wrappers.products.tri))
        )
      );
      if (ms && cs && (hasWrappers || isHomeTemplate)) {
        _autoHydrated = true;
        fetchAndRenderMakeColor(ms, cs);
        return;
      }
      if (retries > 0) {
        setTimeout(function() { autoHydrateFromCookie(retries - 1); }, 200);
      }
    }

    function recoverSearchUiFromBlankState() {
      var customHeader = null;
      var wrappers = null;
      if (!showPickedSummary || !hydrateFromCookie) return;
      if (hasHistorySearchSnapshot()) return;
      if (!searchUI || !picked) return;
      if (picked.style && picked.style.display !== 'none' && trimValue(picked.innerHTML || '')) return;
      if (searchUI.style && searchUI.style.display === 'none') {
        searchUI.setAttribute('style', 'display: block !important');
      } else if (!searchUI.style || !searchUI.style.display) {
        searchUI.setAttribute('style', 'display: block !important');
      } else {
        return;
      }
      picked.style.display = 'none';
      picked.innerHTML = '';
      customHeader = document.getElementById(selectedHeaderId);
      if (customHeader) customHeader.style.setProperty('display', 'none', 'important');
      setPagerWrapDisplay(true);
      try {
        wrappers = getWrappers();
        if (wrappers) {
          setWrapperDisplay(wrappers.reg, 'none');
          setWrapperDisplay(wrappers.tri, 'none');
          if (wrappers.products) {
            setWrapperDisplay(wrappers.products.reg, 'none');
            setWrapperDisplay(wrappers.products.tri, 'none');
          }
          setWrapperListDisplay(wrappers.unknown, 'block');
        }
      } catch(_e) {}
    }

    // ==========================================================
    // "CHANGE MY SELECTION" BUTTON
    // ==========================================================

    function wireChangeButton() {
      var btn = document.getElementById(pfx + '-change-' + uid);
      if (!btn) return;
      btn.onclick = function() {
        var historySource = null;
        var cookieSource = null;
        var submittedSource = null;
        var durableSource = null;
        var uiSource = null;
        stopSubmittedSearchRestore();
        historySource = normalizeEditRestoreSource(getHistorySearchSnapshot() || {});
        cookieSource = normalizeEditRestoreSource(readCookie() || {});
        submittedSource = normalizeEditRestoreSource(readSubmittedSearchScope() || {});
        durableSource = normalizeEditRestoreSource(readEditRestoreScope() || {});
        var rawSource = mergeEditRestoreSource(historySource, cookieSource);
        rawSource = mergeEditRestoreSource(rawSource, durableSource);
        rawSource = mergeEditRestoreSource(rawSource, submittedSource);
        uiSource = normalizeEditRestoreSource({
          make: (makeSel && makeSel.value) || state.make || '',
          year: (yearSel && yearSel.value) || state.year || '',
          model: (modelSel && modelSel.value) || state.model || '',
          q: (searchQ && searchQ.value) || state.q || ''
        });
        rawSource = mergeEditRestoreSource(rawSource, uiSource);
        var restore = normalizeEditRestoreSource(rawSource);
        // Defensive fallback: if history/session snapshots are partial, recover
        // missing vehicle scope from ps_cc when make matches.
        if (cookieSource && restore && restore.make && cookieSource.make === restore.make) {
          if (!restore.year && cookieSource.year) restore.year = cookieSource.year;
          if (!restore.model && restore.year && cookieSource.year === restore.year && cookieSource.model) {
            restore.model = cookieSource.model;
          }
          if (!restore.q && cookieSource.q) restore.q = cookieSource.q;
        }
        clearSubmittedSearchScope();
        var token = bumpEditRestoreToken();
        var didClearEditContext = false;
        var originalBtnHtml = btn.innerHTML;
        btn.setAttribute('data-ps-original-label-html', originalBtnHtml);
        function clearEditContext() {
          if (didClearEditContext) return;
          didClearEditContext = true;
          try { writeSearchContextCookie({ ps_from_search: false }, true); } catch(_e) {}
          notifyColorCleared();
        }
        function resetChangeButton() {
          btn.innerHTML = originalBtnHtml;
          btn.style.pointerEvents = '';
        }
        btn.innerHTML = 'Loading... <span class="' + pfx + '-spinner-' + uid + '" style="display:inline-block;vertical-align:middle;margin-left:8px;width:14px;height:14px;border-width:2px;"></span>';
        btn.style.pointerEvents = 'none';
        var onDone = function() {
          resetChangeButton();
          if (picked) picked.style.display = 'none';
          var ch = document.getElementById(selectedHeaderId);
          if (ch) ch.style.setProperty('display', 'none', 'important');
          if (searchUI) searchUI.setAttribute('style', 'display: block !important');
          if (grid) grid.style.display = '';
          setPagerWrapDisplay(true);
        };
        function syncRestoreDropdown(cfg) {
          try { syncSearchDropdown(cfg); } catch(_e) {}
        }
        function runMakeOnlyRestoreSearch() {
          state.year = '';
          state.model = '';
          if (yearSel) yearSel.value = '';
          if (modelSel) modelSel.value = '';
          syncRestoreDropdown(selectStores.year);
          syncRestoreDropdown(selectStores.model);
          if (searchQ) searchQ.value = restore.q;
          clearEditContext();
          doSearch(false, false, onDone, false, true, {
            make: state.make,
            year: '',
            model: '',
            q: restore.q
          });
        }
        function runYearOnlyRestoreSearch() {
          state.model = '';
          if (modelSel) modelSel.value = '';
          syncRestoreDropdown(selectStores.model);
          if (searchQ) searchQ.value = restore.q;
          clearEditContext();
          doSearch(false, false, function() {
            if (!isEditRestoreTokenActive(token)) return;
            if (!hasCommittedColorCards()) {
              runMakeOnlyRestoreSearch();
              return;
            }
            onDone();
          }, false, true, {
            make: state.make,
            year: state.year,
            model: '',
            q: restore.q
          });
        }
        function finishEditableFallback(mode) {
          if (!isEditRestoreTokenActive(token)) return;
          state.page = 1;
          state.pages = 1;
          showError('');
          clearGrid();
          showEmpty(true);
          if (mode === 'no-make') {
            state.make = '';
            state.year = '';
            state.model = '';
            if (makeSel) makeSel.value = '';
            if (yearSel) yearSel.value = '';
            if (modelSel) modelSel.value = '';
            resetYears();
            resetModels();
            setSearchInputDisabled(true);
          } else if (mode === 'no-year') {
            state.year = '';
            state.model = '';
            if (yearSel) yearSel.value = '';
            if (modelSel) modelSel.value = '';
            resetModels();
            setSearchInputDisabled(false);
          } else {
            state.model = '';
            if (modelSel) modelSel.value = '';
            setSearchInputDisabled(false);
          }
          refreshStepGuidance(true);
          clearEditContext();
          onDone();
        }

        state.page = 1;
        state.pages = 1;
        showError('');
        if (searchQ) searchQ.value = restore.q;
        if (!restore.make) {
          finishEditableFallback('no-make');
          return;
        }
        state.make = restore.make;
        if (makeSel) makeSel.value = restore.make;
        setSearchInputDisabled(false);

        loadYears(restore.make, {
          noPreselect: true,
          focusNext: false,
          restoreToken: token,
          onLoaded: function() {
            var targetYear = '';
            var resolvedYear = '';
            var targetModel = '';
            if (!isEditRestoreTokenActive(token)) return;
            targetYear = String(restore.year || '').replace(/^\s+|\s+$/g, '');
            if (!targetYear) {
              runMakeOnlyRestoreSearch();
              return;
            }
            if (yearSel) yearSel.value = targetYear;
            syncRestoreDropdown(selectStores.year);
            resolvedYear = yearSel ? String(yearSel.value || '').replace(/^\s+|\s+$/g, '') : targetYear;
            if (!resolvedYear) {
              runMakeOnlyRestoreSearch();
              return;
            }
            if (targetYear !== resolvedYear) {
              runMakeOnlyRestoreSearch();
              return;
            }

            state.year = resolvedYear;
            if (yearSel) yearSel.value = resolvedYear;
            syncRestoreDropdown(selectStores.year);
            if (searchQ) searchQ.value = restore.q;
            state.model = '';
            if (modelSel) modelSel.value = '';
            syncRestoreDropdown(selectStores.model);
            targetModel = String(restore.model || '').replace(/^\s+|\s+$/g, '');
            if (!targetModel) {
              loadModels(restore.make, resolvedYear, {
                focusNext: false,
                autoSearch: false,
                restoreToken: token,
                preventAutoSelect: true,
                onLoaded: function() {
                  if (!isEditRestoreTokenActive(token)) return;
                  if (searchQ) searchQ.value = restore.q;
                  clearEditContext();
                  doSearch(false, false, function() {
                    if (!isEditRestoreTokenActive(token)) return;
                    if (!hasCommittedColorCards()) {
                      runMakeOnlyRestoreSearch();
                      return;
                    }
                    onDone();
                  }, false, true, {
                    make: state.make,
                    year: state.year,
                    model: '',
                    q: restore.q
                  });
                }
              });
              return;
            }

            loadModels(restore.make, resolvedYear, {
              focusNext: false,
              autoSearch: false,
              forceModel: targetModel,
              restoreToken: token,
              onLoaded: function(modelResult) {
                var resolvedModel = '';
                if (!isEditRestoreTokenActive(token)) return;
                if (searchQ) searchQ.value = restore.q;
                resolvedModel = String(modelResult && modelResult.selectedModel || '').replace(/^\s+|\s+$/g, '');
                if (!resolvedModel || !(modelResult && modelResult.modelFound)) {
                  runYearOnlyRestoreSearch();
                  return;
                }
                state.model = resolvedModel;
                if (modelSel) modelSel.value = resolvedModel;
                syncRestoreDropdown(selectStores.model);
                if (modelSel && String(modelSel.value || '') !== resolvedModel) {
                  runYearOnlyRestoreSearch();
                  return;
                }
                if (!state.make || !state.year || !state.model) {
                  runYearOnlyRestoreSearch();
                  return;
                }
                clearEditContext();
                doSearch(false, false, onDone, false, true, {
                  make: state.make,
                  year: state.year,
                  model: state.model,
                  q: restore.q
                });
              }
            });
          }
        });
      };
    }

    // ==========================================================
    // EVENT BINDING
    // ==========================================================

    // Make change
    makeSel.addEventListener('change', function() {
      var isStickyRelay = makeSel.getAttribute('data-ps-sticky-relay') === 'true';
      stopSubmittedSearchRestore();
      clearSubmittedSearchScope();
      bumpEditRestoreToken();
      cancelActiveSearch();
      var shouldRefreshKeywordResults = hasActiveKeywordSearch();
      state.make = makeSel.value || '';
      state.year = '';
      state.model = '';
      clearTypeaheadSuggestions();
      clearHistorySearchSnapshot();
      setSearchInputDisabled(!state.make);
      clearActiveStep();
      if (yearSel) { yearSel.value = ''; resetYears(); }
      if (modelSel) { modelSel.value = ''; resetModels(); }
      if (shouldRefreshKeywordResults && state.make) showEmpty(false);
      else clearGrid();
      showError('');

      if (doReplaceText) { try { updateVehicleCopy(state.make, ''); } catch(_e) {} }
      if (grid) grid.style.display = '';
      if (picked) { picked.style.display = 'none'; picked.innerHTML = ''; }
      if (doRewriteProducts) {
        try {
          var w = getWrappers();
          setWrapperDisplay(w.reg, 'none');
          setWrapperDisplay(w.tri, 'none');
          if (w.products) {
            setWrapperDisplay(w.products.reg, 'none');
            setWrapperDisplay(w.products.tri, 'none');
          }
        } catch(_e) {}
      }

      loadYears(state.make, { noPreselect: true, focusNext: makeSel.getAttribute('data-ps-sticky-relay') !== 'true' });

      try { writeSearchContextCookie({ ps_from_search: false, model: '', year: '' }); } catch(_e) {}
      try { syncVisibility(); } catch(_e) {}
      if (!isStickyRelay && shouldRefreshKeywordResults && state.make) refreshKeywordResultsForVehicleChange();
    });

    // Year change
    if (yearSel) {
      yearSel.addEventListener('change', function() {
        var isStickyRelay = yearSel.getAttribute('data-ps-sticky-relay') === 'true';
        stopSubmittedSearchRestore();
        clearSubmittedSearchScope();
        bumpEditRestoreToken();
        cancelActiveSearch();
        var shouldRefreshKeywordResults = hasActiveKeywordSearch();
        state.year = yearSel.value || '';
        state.model = '';
        clearTypeaheadSuggestions();
        clearHistorySearchSnapshot();
        clearActiveStep();
        if (modelSel) { modelSel.value = ''; resetModels(); }
        if (shouldRefreshKeywordResults && state.make && state.year) showEmpty(false);
        else clearGrid();
        showError('');
        try { writeSearchContextCookie({ ps_from_search: false, model: '' }); } catch(_e) {}
        loadModels(state.make, state.year, {
          focusNext: !isStickyRelay,
          suppressAutoSearch: isStickyRelay
        });
        if (!isStickyRelay && shouldRefreshKeywordResults && state.make && state.year) refreshKeywordResultsForVehicleChange();
      });
    }

    // Model change
    if (modelSel) {
      modelSel.addEventListener('change', function() {
        var isStickyRelay = modelSel.getAttribute('data-ps-sticky-relay') === 'true';
        stopSubmittedSearchRestore();
        clearSubmittedSearchScope();
        bumpEditRestoreToken();
        cancelActiveSearch();
        state.model = modelSel.value || '';
        clearTypeaheadSuggestions();
        clearHistorySearchSnapshot();
        clearActiveStep();
        // Sync make/year from dropdowns if state was not set (e.g. after back-navigation hydration)
        if (!state.make && makeSel.value) state.make = makeSel.value;
        if (!state.year && yearSel && yearSel.value) state.year = yearSel.value;
        showError('');
        try { writeSearchContextCookie({ ps_from_search: false }); } catch(_e) {}
        if (!state.make || !state.year || !state.model) {
          refreshStepGuidance(!isStickyRelay);
          return;
        }
        if (isStickyRelay) return;
        doSearch(false, true);
      });
    }

    if (searchQ) {
      if (directGridSearchMode) {
        searchQ.addEventListener('input', function() {
          var term = String(searchQ.value || '').replace(/^\s+|\s+$/g, '');
          var sensitiveState = getSensitiveSearchState(term);
          cancelActiveSearch();
          clearTypeaheadSuggestions();
          if (directSearchTimer) {
            clearTimeout(directSearchTimer);
            directSearchTimer = null;
          }
          if (sensitiveState) {
            showSensitiveSearchGuidance(sensitiveState);
            return;
          }
          if (searchQ.getAttribute('data-ps-sticky-relay') === 'true') return;
          if (term.length < keywordSearchMinChars) {
            restoreVehicleResultsForClearedKeywords();
            return;
          }
          directSearchTimer = setTimeout(function() {
            directSearchTimer = null;
            showBtnSpinner = false;
            doSearch(false, true, null, false, true);
          }, directSearchDebounceMs);
        });
      } else {
        ensureTypeaheadUi();
        searchQ.addEventListener('input', function() {
          var term = String(searchQ.value || '').replace(/^\s+|\s+$/g, '');
          var sensitiveState = getSensitiveSearchState(term);
          if (sensitiveState) {
            showSensitiveSearchGuidance(sensitiveState);
            return;
          }
          clearSensitiveSearchGuidance();
          if (!term) {
            clearTypeaheadSuggestions();
            restoreVehicleResultsForClearedKeywords();
            return;
          }
          if (term.length < 2) {
            clearTypeaheadSuggestions();
            return;
          }
          debouncedTypeaheadRequest();
        });
        searchQ.addEventListener('focus', function() {
          var term = String(searchQ.value || '').replace(/^\s+|\s+$/g, '');
          if (term.length >= 2) debouncedTypeaheadRequest();
        });
        searchQ.addEventListener('blur', function() {
          setTimeout(function() {
            var active = document.activeElement;
            if (active === searchQ) return;
            if (typeaheadState.shell && active && typeaheadState.shell.contains(active)) return;
            closeTypeahead();
          }, 120);
        });
        searchQ.addEventListener('keydown', function(evt) {
          var key = evt.which || evt.keyCode;
          if (!typeaheadState.shell || !typeaheadState.shell.getAttribute('data-open')) return;
          if (key === 40) {
            evt.preventDefault();
            setTypeaheadActiveIndex(typeaheadState.activeIndex + 1);
          } else if (key === 38) {
            evt.preventDefault();
            setTypeaheadActiveIndex(typeaheadState.activeIndex - 1);
          } else if (key === 13) {
            if (typeaheadState.activeIndex > -1) {
              evt.preventDefault();
              applyTypeaheadSuggestion(typeaheadState.activeIndex);
            }
          } else if (key === 27) {
            evt.preventDefault();
            closeTypeahead();
          }
        });
        document.addEventListener('click', function(evt) {
          var target = evt.target || evt.srcElement;
          if (!typeaheadState.shell || !typeaheadState.shell.getAttribute('data-open')) return;
          if (target === searchQ) return;
          if (typeaheadState.shell.contains(target)) return;
          closeTypeahead();
        });
      }
    }

    // Form submit (if search button exists)
    if (submitBtn && submitBtn.form) {
      submitBtn.form.addEventListener('submit', function(ev) {
        var term = String((searchQ && searchQ.value) || '');
        if (directSearchTimer) {
          clearTimeout(directSearchTimer);
          directSearchTimer = null;
        }
        if (navigateToSensitiveSearchHelp(term)) {
          ev.preventDefault();
          closeTypeahead();
          return;
        }
        stopSubmittedSearchRestore();
        bumpEditRestoreToken();
        ev.preventDefault();
        closeTypeahead();
        doSearch(true, false);
      });
    } else if (submitBtn) {
      submitBtn.addEventListener('click', function(ev) {
        var term = String((searchQ && searchQ.value) || '');
        if (directSearchTimer) {
          clearTimeout(directSearchTimer);
          directSearchTimer = null;
        }
        if (navigateToSensitiveSearchHelp(term)) {
          ev.preventDefault();
          closeTypeahead();
          return;
        }
        stopSubmittedSearchRestore();
        bumpEditRestoreToken();
        ev.preventDefault();
        closeTypeahead();
        doSearch(true, false);
      });
    }

    if (root) {
      root.addEventListener('click', function(e) {
        var node = e.target;
        var helpNode = null;
        while (node && node !== root) {
          if (node.getAttribute && node.getAttribute('data-ps-help-card') === '1') {
            helpNode = node;
            break;
          }
          node = node.parentNode;
        }
        if (!helpNode) return;
        if (grid && grid.contains(helpNode)) return;
        e.preventDefault();
        openSaveColorHelp(buildSaveColorHelpOptions({
          color: helpNode.getAttribute('data-candidate-color') || '',
          code: helpNode.getAttribute('data-candidate-code') || ''
        }));
      });
    }

    // Card click
    if (grid) {
      grid.addEventListener('click', function(e) {
        var a = e.target;
        while (a && a !== grid && a.nodeName !== 'A') a = a.parentNode;
        if (!a || a === grid || a.nodeName !== 'A') return;

        if (a.getAttribute && a.getAttribute('data-ps-help-card') === '1') {
          e.preventDefault();
          openSaveColorHelp(buildSaveColorHelpOptions({
            color: a.getAttribute('data-candidate-color') || '',
            code: a.getAttribute('data-candidate-code') || ''
          }));
          return;
        }

        // Custom handler
        if (onColorClick) {
          var handled = onColorClick(a, e, state);
          if (handled === false) return; // allow override to cancel
        }

        var priorScope = readCookie() || {};
        var payload = {
          make: a.getAttribute('data-make') || state.make || '',
          model: (modelSel && modelSel.value) || state.model || priorScope.model || '',
          color: a.getAttribute('data-color') || '',
          code: a.getAttribute('data-code') || '',
          hex: normHex(a.getAttribute('data-hex') || ''),
          year: yearSel && yearSel.value ? String(yearSel.value) : (state.year || priorScope.year || ''),
          vehicle_scope: true,
          ps_from_search: true
        };
        var rawMakeSlug = a.getAttribute('data-make-slug') || '';
        var rawColorSlug = a.getAttribute('data-color-slug') || '';
        payload.make_slug = rawMakeSlug || toSlug(payload.make);
        payload.color_slug = rawColorSlug || buildColorSlug(payload.color, payload.code);
        writePendingVehicleColorContext(payload);
        payload.shopify_page = buildColorHubUrl({
          shopify_page: a.getAttribute('data-shopify-page') || '',
          make_slug: payload.make_slug,
          color_slug: payload.color_slug,
          make: payload.make,
          color: payload.color,
          code: payload.code
        }, {});

        writeSubmittedSearchScope({
          make: payload.make || '',
          year: payload.year || '',
          model: payload.model || '',
          q: String((searchQ && searchQ.value) || state.q || '').replace(/^\s+|\s+$/g, '')
        });
        writeEditRestoreScope({
          make: payload.make || '',
          year: payload.year || '',
          model: payload.model || '',
          q: String((searchQ && searchQ.value) || state.q || '').replace(/^\s+|\s+$/g, '')
        });
        writeCookie(payload);

        if (navigateOnSelect) {
          // Allow the default anchor navigation to proceed
          return;
        }

        // Search/select mode: show B-state
        e.preventDefault();

        var isTricoat = a.getAttribute('data-is-tricoat') === '1';
        var urlMap = {
          pen: a.getAttribute('data-pen-url') || '',
          bottle: a.getAttribute('data-bottle-url') || '',
          spray: a.getAttribute('data-spray-url') || '',
          pro: a.getAttribute('data-pro-url') || ''
        };
        var imgMap = {
          pen: a.getAttribute('data-pen-img') || '',
          bottle: a.getAttribute('data-bottle-img') || '',
          spray: a.getAttribute('data-spray-img') || '',
          pro: a.getAttribute('data-pro-img') || ''
        };
        var nameMap = {
          pen: a.getAttribute('data-pen-name') || '',
          bottle: a.getAttribute('data-bottle-name') || '',
          spray: a.getAttribute('data-spray-name') || '',
          pro: a.getAttribute('data-pro-name') || ''
        };
        var textData = { make: payload.make, color: payload.color, code: payload.code };
        syncProductRows(isTricoat, urlMap, imgMap, nameMap, textData);

        var products = [];
        var types = ['pen', 'bottle', 'spray', 'pro'];
        for (var t = 0; t < types.length; t++) {
          var u = a.getAttribute('data-' + types[t] + '-url') || '';
          if (u) {
            products.push({
              type_slug: types[t], url: u,
              imageUrl: a.getAttribute('data-' + types[t] + '-img') || '',
              price: a.getAttribute('data-' + types[t] + '-price') || ''
            });
          }
        }

        var detail = {
          make: payload.make, color: payload.color, code: payload.code,
          hex: payload.hex, year: payload.year,
          years: readCardContextArray(a, 'data-years'),
          models: readCardContextArray(a, 'data-models'),
          factory_codes: readCardContextArray(a, 'data-factory-codes'),
          is_tricoat: isTricoat,
          make_slug: payload.make_slug, color_slug: payload.color_slug,
          products: products, shopify_page: payload.shopify_page || ''
        };

        try { document.dispatchEvent(new CustomEvent('ps:color:validated', { detail: detail })); } catch(_e) {}
        if (onColorValidated) onColorValidated(detail);

        renderPickedSummary(detail);
        if (grid) grid.style.display = 'none';
        if (pager) pager.style.display = 'none';
        setPagerWrapDisplay(false);

        // Scroll to picked summary
        if (window.requestAnimationFrame) {
          requestAnimationFrame(function() { requestAnimationFrame(scrollPickedIntoView); });
        } else {
          setTimeout(scrollPickedIntoView, 0);
        }

        wireChangeButton();
      });
    }

    // Pagination: prev / next / numbered
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        if (inFlight || state.page <= 1) return;
        state.page--;
        showBtnSpinner = false;
        fetchResults(true);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        if (inFlight || state.page >= state.pages) return;
        state.page++;
        showBtnSpinner = false;
        fetchResults(true);
      });
    }
    if (pager) {
      pager.addEventListener('click', function(ev) {
        if (inFlight) return;
        var t = ev.target || ev.srcElement;
        if (!t) return;
        if ((t.className || '').indexOf(pfx + '-pagebtn-') > -1 && t.hasAttribute('data-page')) {
          var p = parseInt(t.getAttribute('data-page'), 10);
          if (!isNaN(p) && p >= 1 && p <= state.pages && p !== state.page) {
            state.page = p;
            showBtnSpinner = false;
            fetchResults(true);
          }
        }
      });
    }

    // Retry button in error box
    if (errBox) {
      errBox.addEventListener('click', function(e) {
        var t = e.target;
        if (t && t.getAttribute('data-ps-retry')) {
          e.preventDefault();
          showError('');
          fetchResults(false);
        }
      });
    }

    // Global events (for homepage mode)
    if (showPickedSummary || doReplaceText) {
      try {
        document.addEventListener('ps:color:validated', function(ev) {
          var d = ev && ev.detail ? ev.detail : {};
          if (d && d.make) {
            updateVehicleCopy(d.make, d.color);
            applyStructuredSelectionCopy(d);
            applySelectionTokens({ make: d.make, color: d.color, code: d.code });
            applyColorHubLinks(d);
          }
        });
        document.addEventListener('ps:color:cleared', function(ev) {
          if (ev && ev.detail && ev.detail.clearVehicle === true) {
            syncVisibility();
            return;
          }
          var cur = readCookie() || {};
          writeCookie({ make: cur.make || (makeSel && makeSel.value) || '', year: cur.year || (yearSel && yearSel.value) || '' });
          syncVisibility();
        });
        document.addEventListener('ps:color:invalid', syncVisibility);
      } catch(_e) {}
    }

    // ==========================================================
    // INITIALIZATION
    // ==========================================================

    ensureSearchDropdown('make');
    ensureSearchDropdown('year');
    ensureSearchDropdown('model');
    applyUrlMakeContext();
    applyUrlVehicleHandoff();
    if (hasHistorySearchSnapshot()) {
      restoreSearchResultsFromHistory();
    }
    loadMakes();

    // bfcache resilience: re-hydrate when restored via back/forward navigation.
    // On pageshow with persisted=true, the page is restored from frozen state.
    // Reset inFlight and re-run hydration so dropdowns and grid are consistent.
    if (hydrateFromCookie) {
      window.addEventListener('pageshow', function(e) {
        var historySnapshot = null;
        var hasActiveSelection = false;
        if (!e || !e.persisted) return;
        cancelActiveSearch();
        cancelSelectedColorHydration();
        inFlight = false;
        _autoHydrated = false;
        historySnapshot = getHistorySearchSnapshot();
        hasActiveSelection = hasActiveColorSelection(readCookie() || {});
        if (hasActiveSelection) clearHistorySearchSnapshot();
        if (historySnapshot && !hasActiveSelection) restoreSearchResultsFromHistory(historySnapshot);
        // Re-run the full hydration chain from cookie
        loadMakes(function() {
          autoHydrateFromCookie(8);
        }, hasActiveSelection);
      });
    }

    if (hydrateFromCookie && showPickedSummary) {
      syncVisibility();
      var kick = function() {
        var historySnapshot = getHistorySearchSnapshot();
        try { syncVisibility(); } catch(e) {}
        if (historySnapshot) {
          restoreSearchResultsFromHistory(historySnapshot);
          return;
        }
        autoHydrateFromCookie(8);
        setTimeout(function() { autoHydrateFromCookie(4); }, 600);
      };
      if (window.PS && PS.u && typeof PS.u.onReady === 'function') { PS.u.onReady(kick); }
      else if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', kick); }
      else { kick(); }
      setTimeout(recoverSearchUiFromBlankState, 1800);
      setTimeout(recoverSearchUiFromBlankState, 3600);
    }

    // ==========================================================
    // PUBLIC API
    // ==========================================================

    return {
      state: state,
      loadMakes: loadMakes,
      loadYears: loadYears,
      loadModels: loadModels,
      doSearch: doSearch,
      syncVisibility: syncVisibility,
      beginEdit: beginEdit,
      isLoading: function() {
        return inFlight;
      },
      prepareSubmit: function() {
        if (directSearchTimer) {
          clearTimeout(directSearchTimer);
          directSearchTimer = null;
        }
      },
      submitSearch: function(callback) {
        if (directSearchTimer) {
          clearTimeout(directSearchTimer);
          directSearchTimer = null;
        }
        if (inFlight) {
          if (callback && typeof callback === 'function') callback(false);
          return;
        }
        doSearch(true, false, callback, false, false);
      }
    };
  }

  // ==========================================================
  // 6. EXPOSE
  // ==========================================================

  window.PSColorSearch = {
    create: createInstance,
    util: {
      esc: esc,
      qs: qs,
      toSlug: toSlug,
      buildColorSlug: buildColorSlug,
      normHex: normHex,
      apiBase: apiBase,
      readCookie: readCookie,
      writeCookie: writeCookie,
      extractMeta: extractMeta,
      buildMapsFromMakeColor: buildMapsFromMakeColor,
      cachedGet: cachedGet,
      attachScopedTypeahead: attachScopedTypeahead,
      buildScopedWorkerResultsUrl: buildScopedWorkerResultsUrl,
      buildScopedCommittedResultsUrl: buildScopedCommittedResultsUrl,
      buildScopedResultsPageUrl: buildScopedResultsPageUrl
    }
  };

  // Backward compatibility wrapper
  window.psInitVehicleColorSelector = function(uid, productBase) {
    return createInstance({ uid: uid, productBase: productBase, navigateOnSelect: true, perPage: 15 });
  };

})(window, document);
